import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { employeesApi, riskPreventionApi, tasksApi, salesClientsApi, timelineApi, fopSettingsApi, projectTimelineSnapshotApi, projectWeekStatusApi, plannedMilestonesApi } from "../lib/api";
import TaskGantt from "../components/TaskGantt";
import WorksectionProjectSection from "../components/WorksectionProjectSection";
import { useCurrency } from "../context/CurrencyContext";
import { useStatusesStandalone } from "../hooks/useStatuses";
import TaskStageBadge from "../components/TaskStageBadge";
import { isTaskDone } from "../lib/taskStage";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import ClientSelector from "../components/ClientSelector";
import TaskTimeline, { TimelinePill } from "../components/TaskTimeline";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import BulkPriceRequestModal from "../components/BulkPriceRequestModal";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { normalizeServiceType } from "../lib/serviceTypeNames";
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  FolderOpen,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  Clock,
  Split,
  Calendar,
  Send,
  MessageSquare,
  Users,
  Play,
  ShieldCheck,
  FileSignature,
  ListTodo,
  User,
  ExternalLink,
  Palette,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Eye,
  Download,
  Phone,
  Mail,
  Building2,
  Pencil,
  Link2,
  FileText,
  Target,
  Hash,
  Landmark,
  Unlink,
  XCircle,
  Check,
  CreditCard,
} from "lucide-react";
import CreateClientRequestDialog from "../components/CreateClientRequestDialog";
import ScopeChangeRequests from "../components/ScopeChangeRequests";
import TaskChangeRequests from "../components/TaskChangeRequests";
import ChangeHistoryBlock from "../components/ChangeHistoryBlock";
import ArtistSelector from "../components/ArtistSelector";
import RequestsGantt from "../components/RequestsGantt";
import InlineDatePicker from "../components/InlineDatePicker";
import SetupChatButton from "../components/SetupChatButton";
import ServiceMaterialsPanel, { useMaterialTemplates, attachTemplateMaterials, materialKey } from "../components/ServiceMaterialsPanel";

// Helper function to format API error messages
const formatApiError = (error, defaultMessage) => {
  const errorDetail = error?.response?.data?.detail;
  if (Array.isArray(errorDetail)) {
    return errorDetail.map(e => e.msg || e.message || String(e)).join(", ");
  }
  return errorDetail || defaultMessage;
};

// Unified project statuses - single flow from sales to completion
const DEAL_STATUSES = [
  // Sales phase
  { value: "proposal_sent", label: "Proposal", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { value: "contract_negotiation", label: "Negotiation", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "contract_signed", label: "Signed", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  // PM phase
  { value: "sent_to_pool", label: "In Pool", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  { value: "in_progress", label: "In Progress", color: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
  { value: "awaiting_client", label: "Awaiting Client", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
  { value: "awaiting_payment", label: "Awaiting Payment", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  { value: "completed", label: "Completed", color: "bg-green-500/20 text-green-400 border-green-500/30" },
];

const CURRENCIES = ["EUR", "USD", "UAH"];

// Max artist share for margin calculation
const MAX_ARTIST_SHARE_PERCENT = 35;

// Task type coefficients for suggested pricing
const TASK_TYPE_COEFFICIENTS = {
  close_up: 0.30,
  eye_level: 0.35,
  aerial_view: 0.40,
  exterior_render: 0.35,
  interior_render: 0.35,
  animation: 0.45,
  floor_plan: 0.25,
  vr: 0.50,
  other: 0.35,
};

// Generate month options for next 12 months
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = -2; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
    months.push({ value, label });
  }
  return months;
};

// Decide whether a given service should host the setup-chat icon.
// Rule: standalone payments always do; for split groups (sharing payment_group_id),
// only the "(1/N)" payment hosts it. Falls back to earliest created_at if the
// 1/N marker is missing.
const isSetupChatPrimary = (service, allServices) => {
  if (!service) return false;
  const groupId = service.payment_group_id;
  if (!groupId) return true;
  const siblings = (allServices || []).filter((s) => s.payment_group_id === groupId);
  if (siblings.length <= 1) return true;
  const firstByName = /\(1\/\d+\)\s*$/.test(service.name || "");
  const anyMatchesOneSlashN = siblings.some((s) => /\(1\/\d+\)\s*$/.test(s.name || ""));
  if (anyMatchesOneSlashN) return firstByName;
  const sorted = [...siblings].sort((a, b) =>
    String(a.created_at || "").localeCompare(String(b.created_at || ""))
  );
  return sorted[0]?.id === service.id;
};

// Whether the chat icon should appear for this service.
// True iff EITHER:
//   1) the service_type matches at least one keyword from the configured flows, OR
//   2) there's REAL progress on the chat — either saved responses or more than
//      the single auto-seeded intro message. Just opening the modal once (which
//      seeds the intro and sets setup_stage="sales") does NOT count as real
//      progress, so the icon doesn't get stuck on unmapped services.
// Otherwise the admin needs to map the service_type to a flow first.
const setupChatHasMappedFlow = (service, mappedKeywords) => {
  if (!service) return false;
  const responses = service.setup_responses || {};
  // The list endpoint trims message bodies but exposes a count.
  const msgCount =
    typeof service.setup_chat_messages_count === "number"
      ? service.setup_chat_messages_count
      : (service.setup_chat_messages || []).length;
  const hasRealProgress = Object.keys(responses).length > 0 || msgCount > 1;
  if (hasRealProgress) return true;
  const t = String(service.service_type || "").trim().toLowerCase();
  if (!t) return false;
  return (mappedKeywords || []).some((kw) => kw && t.includes(kw));
};

// Compute a deadline from a start date + a count of working days, skipping
// only weekends (Sat/Sun). The start date counts as working day #1 (if it's a
// weekday; otherwise the count begins on the next weekday). Returns a
// "YYYY-MM-DD" string in local time, or "" if inputs are incomplete.
const addBusinessDays = (startStr, workingDays) => {
  const days = parseInt(workingDays, 10);
  if (!startStr || !days || days < 1) return "";
  const d = new Date(`${startStr}T00:00:00`);
  if (isNaN(d.getTime())) return "";
  let counted = 0;
  while (true) {
    const dow = d.getDay(); // 0=Sun, 6=Sat
    if (dow !== 0 && dow !== 6) {
      counted += 1;
      if (counted >= days) break;
    }
    d.setDate(d.getDate() + 1);
  }
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const SalesProjectEditPage = () => {
  const { projectId, id } = useParams();
  const actualProjectId = projectId || id; // Support both /sales/project/:projectId and /unified-projects/:id
  const navigate = useNavigate();

  // --- Unsaved changes ----------------------------------------------------
  // Project Details saves on a button. Edit a field, click away in the
  // sidebar, and the edit was simply gone — no warning, no trace. Reported by
  // the PM lead after losing work. backlog 259f7233.
  const savedSnapshotRef = useRef("");
  const [pendingNav, setPendingNav] = useState(null);
  const { user } = useAuth();
  const { rates, formatAmount: formatAmountRaw, displayCurrency, cycleCurrency, getCurrencySymbol } = useCurrency();
  const monthOptions = useMemo(() => getMonthOptions(), []);
  
  // Format amounts respecting selected display currency
  const formatAmount = (amount, fromCurrency = "USD") => formatAmountRaw(amount, fromCurrency);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [services, setServices] = useState([]);
  // Service-type keywords currently mapped to a setup-chat flow. Loaded once
  // on mount; controls per-service chat-icon visibility (no flow ⇒ no icon).
  const [setupChatKeywords, setSetupChatKeywords] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/setup-chat/service-types-with-flow")
      .then(({ data }) => {
        if (!cancelled) setSetupChatKeywords(data?.keywords || []);
      })
      .catch(() => {
        // Endpoint may 401 if user lacks permission; default to empty list.
        if (!cancelled) setSetupChatKeywords([]);
      });
    return () => { cancelled = true; };
  }, []);
  // PM-pool readiness: which services still miss setup fields the SALES side
  // owns. Drives the blocking banner + per-service badges. Deliberately its own
  // effect rather than a member of the big Promise.all below — one 403 there
  // takes the whole page's data down with it.
  const [setupGate, setSetupGate] = useState(null);
  const [setupGateTick, setSetupGateTick] = useState(0);
  const refreshSetupGate = useCallback(() => setSetupGateTick((t) => t + 1), []);
  const [clients, setClients] = useState([]);
  const [legalEntities, setLegalEntities] = useState([]); // FOPs/Companies for payments
  const [managers, setManagers] = useState([]); // Sales managers
  const [pmManagers, setPmManagers] = useState([]); // Project managers
  const [artists, setArtists] = useState([]); // Available artists
  const [tasks, setTasks] = useState([]); // Tasks for this project
  // Phase 3-preview: WS-derived cost rollup per portal task (Map<task_id, {ws_task_count, total_hours, total_cost_usd}>)
  // Used in Tasks-table Costs column when manual staff_cost_total is empty.
  const [wsRollupByTask, setWsRollupByTask] = useState({});
  const [clientRequests, setClientRequests] = useState([]); // Client requests
  const [priceRequests, setPriceRequests] = useState([]); // TRP Price requests for approval
  const [outgoingRequests, setOutgoingRequests] = useState([]); // Our requests to partner
  const [requestsTab, setRequestsTab] = useState("incoming"); // incoming | outgoing
  const [requestsView, setRequestsView] = useState("list"); // list | gantt
  const [tasksView, setTasksView] = useState("table"); // table | gantt
  const [timelineSnapshot, setTimelineSnapshot] = useState([]);
  const [timelineWeekStatuses, setTimelineWeekStatuses] = useState([]);
  const [showBulkRequestModal, setShowBulkRequestModal] = useState(false);
  const [vexosFees, setVexosFees] = useState(null); // Global VEXOS fee config from /settings/vexos-fees
  const [portalPenaltyRate, setPortalPenaltyRate] = useState(null); // Portal-wide default %/day (for "inherit default" hint)
  const [serviceTypes, setServiceTypes] = useState([]); // Full service type objects with base_price
  const materialTemplates = useMaterialTemplates(); // {service: [material]} from Admin → Services
  const [serviceTypeNames, setServiceTypeNames] = useState([]); // Just names for task type dropdown
  const [expandedServiceId, setExpandedServiceId] = useState(null); // Track expanded service for change requests
  const [expandedTaskId, setExpandedTaskId] = useState(null); // Track expanded task for change requests
  const [taskTimelines, setTaskTimelines] = useState({}); // {task_id: timelineProgress}
  const [viewRequest, setViewRequest] = useState(null); // Client request being viewed
  const [viewRequestLoading, setViewRequestLoading] = useState(false);
  const [sentPaymentRequests, setSentPaymentRequests] = useState(new Set()); // Track sent payment requests
  
  // Create task from price request dialog
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [selectedPriceRequest, setSelectedPriceRequest] = useState(null);
  const [taskQuantities, setTaskQuantities] = useState([{ qty: 0 }]); // Array of quantities for each task
  const [creatingTasks, setCreatingTasks] = useState(false);
  
  // Use dynamic statuses
  const { projectStatuses, getProjectStatus } = useStatusesStandalone();
  
  // Determine if user can edit sales fields (Sales role or Admin, or PM if project not yet signed)
  const canEditSalesFields = user?.role === "admin" || user?.role === "sales" || user?.role === "sales_director";
  // Determine if project is in production phase (signed)
  const isInProduction = project?.status === "contract_signed" || 
    ["sent_to_pool", "in_progress", "awaiting_client", "awaiting_payment", "completed"].includes(project?.status) ||
    project?.phase === "production";
  // Can manage tasks (PM, PM Lead, Admin, Team, Sales, Sales Director)
  const canManageTasks = user?.role === "admin" || user?.role === "pm_lead" || user?.role === "pm" || user?.role === "team" || user?.role === "sales" || user?.role === "sales_director";
  // Is client viewing - hide internal info
  const isClient = user?.role === "client";
  // Art director gets project access without prices (financial sections hidden,
  // tasks/AD assignments still visible). ad_qc_marker_art_director_scrub_v1
  const isArtDirector = user?.role === "art_director";
  const hideFinancials = isClient || isArtDirector;
  // Can edit anything
  const canEdit = !isClient;

  // Setup chat + its gate moved to the production task (TaskDetailPage).

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    client_id: "",
    client_name: "",
    status: "proposal_sent",
    probability: 50,
    notes: "",
    materials_link: "",
    sales_manager_id: "",
    project_manager_id: "",
    art_director_id: "",
    contract_link: "",
    contract_number: "",
    contract_date: "",
    contract_name: "",
    communication_group: "",
    // Timeline
    start_date: "",
    estimated_days: 0,
    deadline: "",
    // SLA — null = inherit portal default (don't bake a number in here,
    // it silently overrides the portal-wide penalty rate on every save)
    penalty_rate_per_day: null,
    response_time_hours: null,
    // What we agreed to deliver, stated by a human. backlog 7659abe8.
    planned_services: [],
    service_materials: [],
  });
  
  // Start project state
  const [startingProject, setStartingProject] = useState(false);
  // Compact card: notes collapse into a one-line preview until clicked (saves vertical space).
  const [notesExpanded, setNotesExpanded] = useState(false);
  // The notes box is resizable, but the height was forgotten the moment you
  // opened another project — so the sales director re-dragged it on every
  // single one and asked for "розмір вікна попису для всіх проектів". The
  // height he picks is now the height every project opens at. Per-viewer, in
  // the browser: it is a reading preference, not project data. backlog 094e86c1.
  const notesRef = useRef(null);
  const [notesHeight, setNotesHeight] = useState(() => {
    try { return localStorage.getItem("project_notes_height") || ""; } catch (_) { return ""; }
  });
  // Tasks table quick filter: all | active | done | unpaid (Ops-console layer 2).
  const [taskFilter, setTaskFilter] = useState("all");
  // Combined Activity block (Client Requests + Change History) tab.
  const [activityTab, setActivityTab] = useState("requests");
  const taskIsDone = (task) => {
    const tl = taskTimelines[task.id];
    if (tl?.stages?.length) return (tl.current_stage_index || 0) >= tl.stages.length - 1;
    return isTaskDone(task);
  };
  const taskIsUnpaid = (task) => !!task?.payment_status && task.payment_status !== "paid";
  const taskMatchesFilter = (task) =>
    taskFilter === "all" ? true
    : taskFilter === "active" ? !taskIsDone(task)
    : taskFilter === "done" ? taskIsDone(task)
    : taskFilter === "unpaid" ? taskIsUnpaid(task)
    : true;
  // Client-side sort (Ops-console layer 4): by Economics (margin) or Progress. No backend, no mutation of `tasks`.
  const [taskSort, setTaskSort] = useState({ key: null, dir: "desc" });
  const toggleTaskSort = (key) =>
    setTaskSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  const taskProgressPct = (task) => {
    const tl = taskTimelines[task.id];
    if (tl?.stages?.length) return (tl.current_stage_index || 0) / Math.max(1, tl.stages.length - 1);
    return taskIsDone(task) ? 1 : 0;
  };
  // Mirrors the in-row margin computation EXACTLY (WS rollup + FX) so the sort order matches the shown %.
  const taskSortMargin = (task) => {
    const client = task?.client_price || 0;
    const artist = task?.agreed_price || 0;
    const adFee = task?.art_director_fee || 0;
    const currency = task?.currency || "USD";
    const manualStaffUAH = task?.staff_cost_total || 0;
    const wsCostUSD = wsRollupByTask[task?.id]?.total_cost_usd || 0;
    const wsCostUAH = (wsCostUSD > 0 && rates?.USD) ? wsCostUSD * rates.USD : 0;
    const staffUAH = manualStaffUAH > 0 ? manualStaffUAH : wsCostUAH;
    let staffInCur = staffUAH;
    if (currency === "USD" && rates?.USD) staffInCur = staffUAH / rates.USD;
    else if (currency === "EUR" && rates?.EUR) staffInCur = staffUAH / rates.EUR;
    const totalCost = artist + adFee + staffInCur;
    if (client <= 0 || totalCost <= 0) return -1;
    return (client - totalCost) / client;
  };
  const applyTaskSort = (arr) => {
    if (!taskSort.key) return arr;
    const val = taskSort.key === "economics" ? taskSortMargin : taskProgressPct;
    return [...arr].sort((a, b) => (val(b) - val(a)) * (taskSort.dir === "desc" ? 1 : -1));
  };

  // New service modal
  const [showNewService, setShowNewService] = useState(false);
  const [newService, setNewService] = useState({
    service_type: "", // Selected from dropdown
    service_type_id: null, // UUID from service_types collection
    name: "",
    totalAmount: "",
    currency: "EUR",
    probability: 50,
    splitPayments: false,
    client_billing_profile_id: "",
    payments: [{ month: "", percent: 100, amount: "" }],
  });
  
  // Edit payment dialog
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState({
    name: "",
    amount: "",
    currency: "EUR",
    legal_entity_id: "",
    client_billing_profile_id: "",
    contract_number: "",
    contract_date: "",
    contract_name: "",
    month: "",
  });

  // Split payment dialog
  const [splitPaymentDialogOpen, setSplitPaymentDialogOpen] = useState(false);
  const [paymentToSplit, setPaymentToSplit] = useState(null);
  const [splitParts, setSplitParts] = useState([
    { percent: 50, month: "" },
    { percent: 50, month: "" },
  ]);
  const [splittingPayment, setSplittingPayment] = useState(false);

  // Create payment from price request
  const [creatingPayment, setCreatingPayment] = useState(false);

  // Bank transaction linking
  const [linkTransactionDialogOpen, setLinkTransactionDialogOpen] = useState(false);
  const [linkingServiceId, setLinkingServiceId] = useState(null);
  const [unmatchedTransactions, setUnmatchedTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Studio Request to Partner (for TRP projects)
  const [studioRequestDialogOpen, setStudioRequestDialogOpen] = useState(false);
  const [studioRequestForm, setStudioRequestForm] = useState({
    request_type: "price_adjustment",
    title: "",
    description: "",
    amount: "",
    price_per_unit: "",
    quantity: "",
    discount: "",
    priority: "normal",
    extra_360_points: "",
  });
  const [submittingStudioRequest, setSubmittingStudioRequest] = useState(false);

  // Handle timeline stage change
  const handleTimelineStageChange = async (taskId, stageId) => {
    try {
      await timelineApi.updateTaskStage(taskId, { stage_id: stageId });
      toast.success("Task progress updated");
      // Refresh timeline for this task
      const timelineRes = await timelineApi.getTaskTimeline(taskId);
      if (timelineRes.data) {
        setTaskTimelines(prev => ({ ...prev, [taskId]: timelineRes.data }));
      }
    } catch (error) {
      console.error("Error updating timeline:", error);
      toast.error("Failed to update progress");
    }
  };

  useEffect(() => {
    fetchData();
  }, [actualProjectId]);

  // Portal-wide penalty default — loaded independently so the Penalty badge
  // always shows the real default ("1%·def") instead of a blank "default".
  // Previously this rode inside the big fetchData() Promise.all; any single
  // sibling call failing/timing-out silently blanked the whole field.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/settings/payment-penalty`);
        if (!cancelled && res.data && res.data.default_rate_per_day != null) {
          setPortalPenaltyRate(res.data.default_rate_per_day);
        }
      } catch (e) {
        // read may be 403 for some roles — leave the inline "default" fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load Gantt snapshot + Friday switchers when user opens the Gantt view.
  useEffect(() => {
    if (!actualProjectId || tasksView !== "gantt") return;
    let cancelled = false;
    (async () => {
      try {
        const [snapRes, wsRes] = await Promise.all([
          projectTimelineSnapshotApi.get(actualProjectId),
          projectWeekStatusApi.list(actualProjectId).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setTimelineSnapshot(Array.isArray(snapRes.data) ? snapRes.data : []);
        setTimelineWeekStatuses(Array.isArray(wsRes.data) ? wsRes.data : []);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load Gantt snapshot:", err);
          setTimelineSnapshot([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [actualProjectId, tasksView, tasks]);

  const handleGanttSavePlanned = async (taskId, milestones) => {
    try {
      await plannedMilestonesApi.set(taskId, milestones);
      // Refresh snapshot to reflect new planned dates
      const snap = await projectTimelineSnapshotApi.get(actualProjectId);
      setTimelineSnapshot(Array.isArray(snap.data) ? snap.data : []);
      toast.success("Plan saved");
    } catch (err) {
      console.error("Save plan failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to save plan");
    }
  };

  const handleGanttUpdateTaskDates = async (taskId, dates) => {
    try {
      // PUT /tasks/{id} accepts start_date + deadline as ISO YYYY-MM-DD
      await tasksApi.update(taskId, dates);
      // Refresh snapshot to reflect new dates on the Gantt
      const snap = await projectTimelineSnapshotApi.get(actualProjectId);
      setTimelineSnapshot(Array.isArray(snap.data) ? snap.data : []);
    } catch (err) {
      console.error("Update task dates failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to update task dates");
    }
  };

  const handleGanttSetWeekStatus = async (weekEnding, status) => {
    try {
      const res = await projectWeekStatusApi.upsert(actualProjectId, { week_ending: weekEnding, status });
      setTimelineWeekStatuses((prev) => {
        const idx = prev.findIndex((w) => w.week_ending === weekEnding);
        if (idx === -1) return [...prev, res.data].sort((a, b) => (a.week_ending > b.week_ending ? 1 : -1));
        const copy = [...prev];
        copy[idx] = res.data;
        return copy;
      });
    } catch (err) {
      console.error("Week status update failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to update week status");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      let foundProject = null;
      
      // Always try to load from unified projects first (main data source)
      try {
        const unifiedRes = await api.get(`/unified-projects/${actualProjectId}`);
        if (unifiedRes.data) {
          foundProject = {
            ...unifiedRes.data,
            project_name: unifiedRes.data.name,
          };
        }
      } catch (e) {
        console.log("Not found in unified, trying sales-projects-v2 as fallback");
      }
      
      // Fallback: If not found in unified, try sales-projects-v2
      if (!foundProject) {
        const projectRes = await api.get(`/sales-projects-v2`);
        const allProjects = projectRes.data || [];
        foundProject = allProjects.find(p => p.id === actualProjectId);
      }
      
      if (!foundProject) {
        toast.error("Project not found");
        navigate("/unified-projects");
        return;
      }
      
      setProject(foundProject);
      const seed = {
        name: foundProject.project_name || foundProject.name || "",
        client_id: foundProject.client_id || "",
        client_name: foundProject.client_name || "",
        status: foundProject.status || "proposal_sent",
        probability: foundProject.probability || 50,
        notes: foundProject.notes || "",
        materials_link: foundProject.materials_link || "",
        sales_manager_id: foundProject.sales_manager_id || "",
        project_manager_id: foundProject.project_manager_id || "",
        art_director_id: foundProject.art_director_id || "",
        contract_link: foundProject.contract_link || "",
        contract_number: foundProject.contract_number || "",
        contract_date: foundProject.contract_date || "",
        contract_name: foundProject.contract_name || "",
        communication_group: foundProject.communication_group || "",
        // Timeline
        start_date: foundProject.start_date || "",
        estimated_days: foundProject.estimated_days || 0,
        deadline: foundProject.deadline || "",
        // SLA
        penalty_rate_per_day: foundProject.penalty_rate_per_day ?? null,
        response_time_hours: foundProject.response_time_hours || null,
        planned_services: foundProject.planned_services || [],
        service_materials: foundProject.service_materials || [],
        materials_review: foundProject.materials_review || [],
      };
      setFormData(seed);
      // What "unsaved" is measured against — the exact object state is seeded
      // with, so a freshly loaded page is never dirty.
      savedSnapshotRef.current = JSON.stringify(seed);
      
      // Determine if current user is a client (check role from user context)
      const isClientUser = user?.role === "client";
      
      // Load data - different sets for clients vs internal users
      if (isClientUser) {
        // Clients: load tasks, requests, service-types, and their services/payments
        const [typesRes, requestsRes, tasksRes, servicesRes] = await Promise.all([
          api.get(`/service-types/list`),
          api.get(`/client-requests`),
          api.get(`/tasks?project_id=${actualProjectId}`),
          api.get(`/sales-services?project_id=${actualProjectId}`),  // Client's payments
        ]);

        // Extract names from service types objects
        setServiceTypes(typesRes.data || []);  // Full objects with base_price
        setServiceTypeNames((typesRes.data || []).map(t => t.name));  // Just names
        setTasks(tasksRes.data || []);

        // Phase 3-preview: bulk-fetch Worksection cost rollup for ALL tasks in this project
        // (one round-trip). Only tasks with ws_task_links get an entry; rest fall through to
        // existing "No costs" display. Failure silent — WS may not be configured for this tenant.
        try {
          const wsRes = await api.get(`/worksection/task-rollup/portal-project/${actualProjectId}`);
          const byId = {};
          (wsRes.data || []).forEach((r) => {
            byId[r.portal_task_id] = r;
          });
          setWsRollupByTask(byId);
        } catch (e) {
          // Don't toast — WS integration might just not be set up
          setWsRollupByTask({});
        }
        const clientServices = servicesRes.data || [];
        setServices(clientServices);  // Set services for client to see payments
        
        // Load timeline progress for each task
        const tasksList = tasksRes.data || [];
        if (tasksList.length > 0) {
          const timelinesMap = {};
          await Promise.all(
            tasksList.map(async (task) => {
              try {
                const timelineRes = await timelineApi.getTaskTimeline(task.id);
                if (timelineRes.data) {
                  timelinesMap[task.id] = timelineRes.data;
                }
              } catch (e) {
                // Task may not have timeline
              }
            })
          );
          setTaskTimelines(timelinesMap);
        }
        
        // Filter client requests for this project
        const projectRequests = (requestsRes.data || []).filter(r => r.project_id === actualProjectId);
        setClientRequests(projectRequests);
        
        // Mark services that already have payment requests sent
        // Check by service_id and by title matching
        const paymentRequests = projectRequests.filter(r => r.request_type === "payment_request");
        const sentServiceIds = new Set();
        paymentRequests.forEach(r => {
          if (r.service_id) {
            sentServiceIds.add(r.service_id);
          }
        });
        
        // Fallback for legacy requests without service_id: match by exact full
        // name. Keep the split suffix `(1/2)/(2/2)` intact — stripping it
        // wrongly fuses split-payment children together.
        clientServices.forEach(service => {
          const serviceName = (service.name || '').trim().toLowerCase();
          if (!serviceName) return;
          const hasMatchingRequest = paymentRequests.some(r => {
            if (r.service_id) return false;
            const reqName = (r.title || '').replace(/^Payment Request:\s*/i, '').trim().toLowerCase();
            return reqName === serviceName;
          });
          if (hasMatchingRequest) {
            sentServiceIds.add(service.id);
          }
        });

        setSentPaymentRequests(sentServiceIds);
      } else {
        // Internal users: load all data
        const [servicesRes, clientsRes, empRes, typesRes, requestsRes, tasksRes, artistsRes, pmUsersRes, adRes, marginRes, priceReqRes, outgoingReqRes, fopsRes, vexosFeesRes] = await Promise.all([
          api.get(`/sales-services`),
          api.get(`/sales-clients`),
          employeesApi.getAll(),
          api.get(`/service-types/list`),
          api.get(`/client-requests`),
          api.get(`/tasks?project_id=${actualProjectId}`),
          api.get(`/users?role=artist`),
          api.get(`/users?roles=pm,pm_lead,crm_manager`),  // Get PM users (PM, PM Lead and CRM Manager roles)
          api.get(`/art-directors`),
          api.get(`/settings/margin`),
          api.get(`/price-requests?project_id=${actualProjectId}`),  // TRP price requests (incoming)
          api.get(`/studio-requests?project_id=${actualProjectId}`).catch(() => ({ data: [] })),  // Outgoing to partner
          fopSettingsApi.getAll(),  // Load legal entities for payment creation
          api.get(`/settings/vexos-fees`).catch(() => ({ data: {} })),  // Global VEXOS pricing
        ]);
        if (vexosFeesRes.data && Object.keys(vexosFeesRes.data).length > 0) {
          setVexosFees(vexosFeesRes.data);
        }

        const projectServices = (servicesRes.data || []).filter(s => s.project_id === actualProjectId);
        setServices(projectServices);
        setClients(clientsRes.data || []);
        setLegalEntities(fopsRes.data || []);  // Set legal entities
        
        // Set price requests for this project (all are incoming from partner)
        setPriceRequests(priceReqRes.data || []);
        // Outgoing = studio-requests only (from studio to partner)
        setOutgoingRequests(outgoingReqRes.data || []);
        
        const allEmployees = empRes.data || [];
        const sales = allEmployees.filter(e => e.position === "sales" && e.is_active);
        setManagers(sales);
        
        // Use users for PM selection (matches project_manager_id)
        const pmUsers = pmUsersRes.data || [];
        setPmManagers(pmUsers);
        
        // Extract names from service types objects
        setServiceTypes(typesRes.data || []);  // Full objects with base_price
        setServiceTypeNames((typesRes.data || []).map(t => t.name));  // Just names
        setTasks(tasksRes.data || []);
        setArtists(artistsRes.data || []);
        setArtDirectors(adRes.data || []);
        if (marginRes.data) {
          setMarginSettings(marginRes.data);
        }

        // Phase 3-preview: Worksection cost rollup. Same logic as the client branch above
        // (line ~540) — needed in BOTH branches because fetchData has two parallel paths.
        try {
          const wsRes = await api.get(`/worksection/task-rollup/portal-project/${actualProjectId}`);
          const byId = {};
          (wsRes.data || []).forEach((r) => { byId[r.portal_task_id] = r; });
          setWsRollupByTask(byId);
        } catch (e) {
          setWsRollupByTask({});
        }
        
        // Load timeline progress for each task
        const tasksList = tasksRes.data || [];
        if (tasksList.length > 0) {
          const timelinesMap = {};
          await Promise.all(
            tasksList.map(async (task) => {
              try {
                const timelineRes = await timelineApi.getTaskTimeline(task.id);
                if (timelineRes.data) {
                  timelinesMap[task.id] = timelineRes.data;
                }
              } catch (e) {
                // Task may not have timeline, that's okay
              }
            })
          );
          setTaskTimelines(timelinesMap);
        }
        
        // Filter client requests for this project
        const projectRequests = (requestsRes.data || []).filter(r => r.project_id === actualProjectId);
        setClientRequests(projectRequests);
        
        // Mark services that already have payment requests sent
        // Check both by service_id and by service name in title
        const paymentRequests = projectRequests.filter(r => r.request_type === "payment_request");
        const sentServiceIds = new Set();
        paymentRequests.forEach(r => {
          if (r.service_id) {
            sentServiceIds.add(r.service_id);
          }
        });
        
        // Fallback for legacy requests without service_id: match by exact full
        // name. Keep the split suffix `(1/2)/(2/2)` intact — stripping it
        // wrongly fuses split-payment children together.
        projectServices.forEach(service => {
          const serviceName = (service.name || '').trim().toLowerCase();
          if (!serviceName) return;
          const hasMatchingRequest = paymentRequests.some(r => {
            if (r.service_id) return false;
            const reqName = (r.title || '').replace(/^Payment Request:\s*/i, '').trim().toLowerCase();
            return reqName === serviceName;
          });
          if (hasMatchingRequest) {
            sentServiceIds.add(service.id);
          }
        });

        setSentPaymentRequests(sentServiceIds);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  // Price Request handlers (for TRP partner requests)
  const handleApprovePriceRequest = async (requestId) => {
    try {
      await api.put(`/price-requests/${requestId}`, { action: "approve" });
      toast.success("Price request approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve request");
    }
  };

  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const handleRejectPriceRequest = (requestId) => {
    setRejectingRequestId(requestId);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const confirmRejectPriceRequest = async () => {
    if (!rejectingRequestId) return;
    try {
      await api.put(`/price-requests/${rejectingRequestId}`, { action: "reject", reason: rejectReason });
      toast.success("Price request rejected");
      setRejectDialogOpen(false);
      setRejectingRequestId(null);
      setRejectReason("");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject request");
    }
  };

  // Delete price request (without page reload)
  const handleDeletePriceRequest = async (requestId) => {
    try {
      await api.delete(`/price-requests/${requestId}`);
      // Remove from local state without full reload
      setPriceRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success("Request deleted");
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  // Delete studio request (outgoing)
  const handleDeleteStudioRequest = async (requestId) => {
    if (!window.confirm("Delete this outgoing request?")) return;
    try {
      await api.delete(`/studio-requests/${requestId}`);
      setOutgoingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success("Request deleted");
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  // Approve studio request (outgoing) directly by admin
  const handleApproveStudioRequest = async (requestId) => {
    try {
      await api.put(`/studio-requests/${requestId}/respond`, { action: "approve" });
      setOutgoingRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: "approved" } : r));
      toast.success("Request approved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  // Create payment from approved studio request
  const handleCreatePaymentFromStudioRequest = async (request) => {
    if (creatingPayment) return;
    setCreatingPayment(true);
    try {
      await api.post(`/studio-requests/${request.id}/create-payment`, {});
      toast.success("Payment created from studio request");
      setOutgoingRequests(prev => prev.map(r => r.id === request.id ? { ...r, payment_created: true } : r));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment");
    } finally {
      setCreatingPayment(false);
    }
  };

  // Update planned start/end dates on a partner price request (incoming)
  const handleUpdatePriceRequestDates = async (requestId, patch) => {
    try {
      const { data } = await api.patch(`/price-requests/${requestId}`, patch);
      setPriceRequests(prev => prev.map(r => r.id === requestId ? { ...r, ...data } : r));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dates");
      throw error;
    }
  };

  // Update planned start/end dates on a studio request (outgoing)
  const handleUpdateStudioRequestDates = async (requestId, patch) => {
    try {
      const { data } = await api.patch(`/studio-requests/${requestId}`, patch);
      setOutgoingRequests(prev => prev.map(r => r.id === requestId ? { ...r, ...data } : r));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dates");
      throw error;
    }
  };

  // Create tasks from approved studio request
  const handleCreateTasksFromStudioRequest = async (request) => {
    try {
      await api.post(`/studio-requests/${request.id}/create-tasks`, {});
      toast.success("Task created from studio request");
      setOutgoingRequests(prev => prev.map(r => r.id === request.id ? { ...r, task_created: true } : r));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tasks");
    }
  };

  // Open create task dialog from approved request
  const openCreateTaskDialog = (request) => {
    setSelectedPriceRequest(request);
    // Initialize with full quantity in one task
    setTaskQuantities([{ qty: request.quantity || 1 }]);
    setCreateTaskDialogOpen(true);
  };

  // Add a new task row
  const addTaskRow = () => {
    setTaskQuantities([...taskQuantities, { qty: 0 }]);
  };

  // Remove a task row
  const removeTaskRow = (index) => {
    if (taskQuantities.length > 1) {
      setTaskQuantities(taskQuantities.filter((_, i) => i !== index));
    }
  };

  // Update quantity for a specific task
  const updateTaskQuantity = (index, value) => {
    const newQuantities = [...taskQuantities];
    newQuantities[index] = { qty: parseInt(value) || 0 };
    setTaskQuantities(newQuantities);
  };

  // Calculate total allocated and remaining
  const getTotalAllocated = () => taskQuantities.reduce((sum, t) => sum + t.qty, 0);
  const getRemainingQuantity = () => (selectedPriceRequest?.quantity || 0) - getTotalAllocated();

  // Create tasks from approved request
  const handleCreateTasksFromRequest = async () => {
    if (!selectedPriceRequest) return;
    const totalAllocated = getTotalAllocated();
    const totalRequired = selectedPriceRequest.quantity || 1;
    
    if (totalAllocated !== totalRequired) {
      toast.error(`Allocated quantity (${totalAllocated}) must equal total (${totalRequired})`);
      return;
    }
    
    // Filter out zero quantities
    const validQuantities = taskQuantities.filter(t => t.qty > 0).map(t => t.qty);
    if (validQuantities.length === 0) {
      toast.error("At least one task must have quantity > 0");
      return;
    }
    
    setCreatingTasks(true);
    try {
      await api.post(`/price-requests/${selectedPriceRequest.id}/create-tasks`, {
        task_quantities: validQuantities
      });
      toast.success(`${validQuantities.length} task(s) created successfully`);
      setCreateTaskDialogOpen(false);
      setSelectedPriceRequest(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to create tasks");
    } finally {
      setCreatingTasks(false);
    }
  };

  // Validation for production statuses
  const PRODUCTION_STATUSES = ["sent_to_pool", "in_progress", "awaiting_client", "awaiting_payment", "completed"];
  
  const validateProductionFields = (data) => {
    const requiredFields = {
      contract_link: "Contract Link",
      communication_group: "Communication Group",
      start_date: "Start Date", 
      estimated_days: "Working Days",
      deadline: "Deadline",
      response_time_hours: "Response Time (hours)"
    };
    
    const missing = [];
    for (const [field, label] of Object.entries(requiredFields)) {
      const value = data[field];
      if (value === null || value === undefined || value === "" || 
          (["estimated_days", "response_time_hours"].includes(field) && value === 0)) {
        missing.push(label);
      }
    }
    return missing;
  };

  const isDirty = useMemo(() => {
    if (!savedSnapshotRef.current) return false;  // nothing loaded yet
    return JSON.stringify(formData) !== savedSnapshotRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Closing the tab or reloading: the browser's own dialog, the only one it
  // allows us here.
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  // Navigating inside the portal. This app mounts a plain BrowserRouter, so
  // react-router's useBlocker is not available; we catch the click on the way
  // down instead, before the router sees it.
  useEffect(() => {
    if (!isDirty) return undefined;
    const intercept = (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;  // open-in-new-tab keeps this page
      const link = e.target?.closest?.("a[href]");
      if (!link) return;
      const href = link.getAttribute("href");
      if (!href || !href.startsWith("/") || href.startsWith("//")) return;  // external or anchor
      if (link.target && link.target !== "_self") return;
      if (href === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNav(href);
    };
    document.addEventListener("click", intercept, true);
    return () => document.removeEventListener("click", intercept, true);
  }, [isDirty]);

  const handleSave = async () => {
    // Validate required fields for production statuses
    if (PRODUCTION_STATUSES.includes(formData.status)) {
      const missingFields = validateProductionFields(formData);
      if (missingFields.length > 0) {
        toast.error(`Cannot save with '${formData.status}' status. Missing required fields: ${missingFields.join(", ")}`);
        return false;
      }
    }
    
    try {
      setSaving(true);
      
      // Always use unified API for saving (primary data source)
      const updateData = {
        ...formData,
        name: formData.name,
        project_manager_id: formData.project_manager_id || null,
      };
      
      await api.put(`/unified-projects/${actualProjectId}`, updateData);
      
      toast.success("Project saved");
      fetchData();
      return true;
    } catch (error) {
      console.error("Save error:", error);
      const detail = error.response?.data?.detail || "Failed to save";
      toast.error(detail);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleStartProject = async () => {
    setStartingProject(true);
    try {
      const res = await riskPreventionApi.startProject(actualProjectId);
      toast.success("Project started!");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to start project";
      toast.error(detail);
    } finally {
      setStartingProject(false);
    }
  };

  // Create studio request (send to partner for approval)
  const handleCreateStudioRequest = async () => {
    if (!studioRequestForm.title) {
      toast.error("Please fill in the title");
      return;
    }
    
    setSubmittingStudioRequest(true);
    try {
      await api.post("/studio-requests", {
        project_id: actualProjectId,
        request_type: studioRequestForm.request_type,
        title: studioRequestForm.title,
        description: studioRequestForm.description,
        amount: studioRequestForm.amount ? parseFloat(studioRequestForm.amount) : null,
        price_per_unit: studioRequestForm.price_per_unit ? parseFloat(studioRequestForm.price_per_unit) : null,
        quantity: studioRequestForm.quantity ? parseInt(studioRequestForm.quantity) : null,
        discount: studioRequestForm.discount ? parseFloat(studioRequestForm.discount) : null,
        priority: studioRequestForm.priority,
        extra_360_points: studioRequestForm.extra_360_points ? parseInt(studioRequestForm.extra_360_points) : 0,
      });
      toast.success("Request sent to partner");
      setStudioRequestDialogOpen(false);
      setStudioRequestForm({
        request_type: "price_adjustment",
        title: "",
        description: "",
        amount: "",
        price_per_unit: "",
        quantity: "",
        discount: "",
        priority: "normal",
        extra_360_points: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create request");
    } finally {
      setSubmittingStudioRequest(false);
    }
  };

  // Task management
  const [showNewTask, setShowNewTask] = useState(false);
  const [artDirectors, setArtDirectors] = useState([]);
  const [marginSettings, setMarginSettings] = useState({ warning_threshold_percent: 40, critical_threshold_percent: 50, max_artist_share_percent: 30 });
  const [newTask, setNewTask] = useState({
    name: "",
    task_type: "",
    description: "",
    brief: "",
    brief_link: "",
    deadline: "",
    assigned_artist_id: "",
    agreed_price: "",
    client_price: "",
    currency: "USD",
    quantity: 1, // For batch task creation
    min_artist_rating: null, // Minimum rating for pool tasks
    art_director_review: false,
    art_director_id: "",
    visible_to_client: true,
  });
  
  // Drag ends on mouse-up; store whatever height the box now has.
  const rememberNotesHeight = () => {
    const el = notesRef.current;
    if (!el) return;
    const h = `${Math.round(el.getBoundingClientRect().height)}px`;
    if (h === notesHeight) return;
    setNotesHeight(h);
    try { localStorage.setItem("project_notes_height", h); } catch (_) { /* private mode */ }
  };

  // Edit task state
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingArtistId, setEditingArtistId] = useState("");
  
  // Assign Task Dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningTask, setAssigningTask] = useState(null);
  const [assignForm, setAssignForm] = useState({
    agreed_price: "",
    assigned_artist_id: "",
    send_to_pool: false,
  });

  // Calculate margin status for new task
  const newTaskMargin = useMemo(() => {
    const clientPrice = parseFloat(newTask.client_price) || 0;
    const agreedPrice = parseFloat(newTask.agreed_price) || 0;
    if (clientPrice <= 0) return { status: 'none', percent: 0 };
    
    let totalCost = agreedPrice;
    if (newTask.art_director_review && newTask.art_director_id) {
      // Get the selected art director's bonus percentage
      const selectedAD = artDirectors.find(ad => ad.id === newTask.art_director_id);
      const adBonusPercent = selectedAD?.art_director_bonus_percent ?? 0; // 0% if not set
      totalCost += agreedPrice * (adBonusPercent / 100);
    }
    
    const percent = (totalCost / clientPrice) * 100;
    
    if (percent >= marginSettings.critical_threshold_percent) {
      return { status: 'CRITICAL', percent: percent.toFixed(1), color: 'text-red-400' };
    } else if (percent >= marginSettings.warning_threshold_percent) {
      return { status: 'WARNING', percent: percent.toFixed(1), color: 'text-yellow-400' };
    }
    return { status: 'OK', percent: percent.toFixed(1), color: 'text-green-400' };
  }, [newTask.client_price, newTask.agreed_price, newTask.art_director_review, newTask.art_director_id, marginSettings, artDirectors]);

  // Calculate margin for assign dialog
  const assignMargin = useMemo(() => {
    if (!assigningTask) return { status: 'none', percent: 0 };
    const clientPrice = assigningTask.client_price || 0;
    const agreedPrice = parseFloat(assignForm.agreed_price) || 0;
    if (clientPrice <= 0 || agreedPrice <= 0) return { status: 'none', percent: 0 };
    
    const percent = (agreedPrice / clientPrice) * 100;
    const suggestedPrice = clientPrice * (TASK_TYPE_COEFFICIENTS[assigningTask.task_type] || 0.35);
    const maxPrice = clientPrice * (MAX_ARTIST_SHARE_PERCENT / 100);
    
    if (percent >= marginSettings.critical_threshold_percent) {
      return { status: 'CRITICAL', percent: percent.toFixed(1), color: 'text-red-400', suggestedPrice, maxPrice };
    } else if (percent >= marginSettings.warning_threshold_percent) {
      return { status: 'WARNING', percent: percent.toFixed(1), color: 'text-yellow-400', suggestedPrice, maxPrice };
    }
    return { status: 'OK', percent: percent.toFixed(1), color: 'text-green-400', suggestedPrice, maxPrice };
  }, [assigningTask, assignForm.agreed_price, marginSettings]);

  // Open assign dialog for a task
  const openAssignDialog = (task) => {
    setAssigningTask(task);
    setAssignForm({
      agreed_price: task.agreed_price || "",
      assigned_artist_id: task.assigned_artist_id || "",
      send_to_pool: false,
    });
    setAssignDialogOpen(true);
  };

  // Handle assign task submission
  const handleAssignTask = async () => {
    if (!assigningTask) return;
    
    const agreedPrice = parseFloat(assignForm.agreed_price) || 0;
    
    // Validate price is set
    if (agreedPrice <= 0) {
      toast.error("Please set artist price");
      return;
    }
    
    // Check margin threshold
    if (assignMargin.status === 'CRITICAL') {
      toast.error(`Cannot assign: Artist share (${assignMargin.percent}%) exceeds critical threshold (${marginSettings.critical_threshold_percent}%)`);
      return;
    }
    
    try {
      const updateData = {
        agreed_price: agreedPrice,
      };
      
      if (assignForm.send_to_pool) {
        updateData.status = "pool";
        updateData.assigned_artist_id = null;
      } else if (assignForm.assigned_artist_id) {
        updateData.assigned_artist_id = assignForm.assigned_artist_id;
        updateData.status = "assigned";
      }
      
      await tasksApi.update(assigningTask.id, updateData);
      toast.success(assignForm.send_to_pool ? "Task sent to pool" : "Task assigned");
      setAssignDialogOpen(false);
      setAssigningTask(null);
      fetchData();
    } catch (error) {
      console.error("Assign task error:", error);
      toast.error(formatApiError(error, "Failed to assign task"));
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    // Validate required field
    if (!newTask.task_type) {
      toast.error("Task Type is required");
      return;
    }

    // Validate: Art Director must be selected when Art Director Review is enabled
    if (newTask.art_director_review && !newTask.art_director_id) {
      toast.error("Please select an Art Director when Art Director Review is enabled");
      return;
    }

    // Check margin threshold - show warning but allow creation
    if (newTaskMargin.status === 'CRITICAL') {
      // Just log warning, don't block creation
      console.warn(`Task created with low margin: ${newTaskMargin.percent}%`);
    }
    
    try {
      // Calculate art director fee based on selected AD's bonus percentage
      let artDirectorFee = 0;
      if (newTask.art_director_review && newTask.art_director_id) {
        const selectedAD = artDirectors.find(ad => ad.id === newTask.art_director_id);
        const adBonusPercent = selectedAD?.art_director_bonus_percent ?? 0; // 0% if not set
        artDirectorFee = (parseFloat(newTask.agreed_price) || 0) * (adBonusPercent / 100);
      }

      const taskData = {
        ...newTask,
        project_id: actualProjectId,
        agreed_price: parseFloat(newTask.agreed_price) || 0,
        client_price: parseFloat(newTask.client_price) || 0,
        min_artist_rating: newTask.min_artist_rating || undefined,
        brief_link: newTask.brief_link || undefined,
        currency: newTask.currency || "USD",
        visible_to_client: newTask.visible_to_client !== false,
        art_director_review: newTask.art_director_review,
        art_director_id: newTask.art_director_review ? newTask.art_director_id : undefined,
        art_director_fee: artDirectorFee,
      };
      // Handle "unassigned" value
      if (taskData.assigned_artist_id === "unassigned") {
        taskData.assigned_artist_id = null;
      }
      // Remove quantity from API payload
      delete taskData.quantity;
      
      const quantity = parseInt(newTask.quantity) || 1;
      // Task type is the primary name; user name is optional suffix in brackets
      const baseName = newTask.name 
        ? `${newTask.task_type} [${newTask.name}]` 
        : newTask.task_type;
      
      // Create multiple tasks if quantity > 1
      if (quantity > 1) {
        const createPromises = [];
        for (let i = 1; i <= Math.min(quantity, 20); i++) {
          const batchTask = {
            ...taskData,
            name: `${baseName} (${i} of ${quantity})`,
          };
          createPromises.push(tasksApi.create(batchTask));
        }
        await Promise.all(createPromises);
        toast.success(`${quantity} tasks created`);
      } else {
        await tasksApi.create(taskData);
        toast.success("Task created");
      }
      
      setShowNewTask(false);
      setNewTask({
        name: "",
        task_type: "",
        description: "",
        brief: "",
        brief_link: "",
        deadline: "",
        assigned_artist_id: "",
        agreed_price: "",
        client_price: "",
        currency: "USD",
        quantity: 1,
        min_artist_rating: null,
        art_director_review: false,
        visible_to_client: true,
        art_director_id: "",
      });
      fetchData();
    } catch (error) {
      console.error("Task creation error:", error);
      toast.error(formatApiError(error, "Failed to create task"));
    }
  };

  // Update task artist
  const handleUpdateTaskArtist = async (taskId, newArtistId) => {
    try {
      await tasksApi.update(taskId, { 
        assigned_artist_id: newArtistId === "unassigned" ? null : newArtistId 
      });
      toast.success("Artist updated");
      setEditingTaskId(null);
      setEditingArtistId("");
      fetchData();
    } catch (error) {
      console.error("Update artist error:", error);
      toast.error(formatApiError(error, "Failed to update artist"));
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId, taskName) => {
    if (!window.confirm(`Delete task "${taskName}"? This cannot be undone.`)) return;
    try {
      await tasksApi.delete(taskId);
      toast.success("Task deleted");
      fetchData();
    } catch (error) {
      console.error("Delete task error:", error);
      toast.error(formatApiError(error, "Failed to delete task"));
    }
  };

  const openNewServiceModal = (overrideBudgetUAH) => {
    const defaultMonth = project?.month || monthOptions[2]?.value;
    // Default probability based on project status
    const signedStatuses = ["contract_signed", "sent_to_pool", "in_progress", "awaiting_client", "awaiting_payment", "completed"];
    const defaultProbability = signedStatuses.includes(project?.status) ? 100 : 50;
    
    // Use override budget (from requests diff) or tasks total
    const projectBudgetUAH = overrideBudgetUAH || totals.tasksRevenueUAH || 0;
    // Convert to display currency
    const projectBudget = displayCurrency === "UAH" 
      ? projectBudgetUAH 
      : projectBudgetUAH / (rates[displayCurrency] || 1);
    
    setNewService({
      service_type: "", // Empty - user must select from service types
      service_type_id: null,
      name: "",
      totalAmount: projectBudget > 0 ? projectBudget.toFixed(2) : "",
      currency: displayCurrency, // Use user's display currency
      baseBudgetUAH: projectBudgetUAH, // Store base budget for currency conversion
      probability: defaultProbability,
      splitPayments: true, // Default to split mode for payment planning
      legal_entity_id: "", // Legal entity for invoice
      client_billing_profile_id: "", // Bill to — client's own legal entity
      payments: [{ month: defaultMonth, percent: 100, amount: projectBudget > 0 ? projectBudget.toFixed(2) : "" }],
    });
    setShowNewService(true);
  };
  
  // Handle currency change in payment modal - convert amounts
  const handlePaymentCurrencyChange = (newCurrency) => {
    const baseBudgetUAH = newService.baseBudgetUAH || totals.tasksRevenueUAH || 0;
    const newBudget = newCurrency === "UAH" 
      ? baseBudgetUAH 
      : baseBudgetUAH / (rates[newCurrency] || 1);
    
    // Recalculate all payment amounts based on new currency
    const updatedPayments = newService.payments.map(p => ({
      ...p,
      amount: p.percent ? ((newBudget * parseFloat(p.percent)) / 100).toFixed(2) : ""
    }));
    
    setNewService({
      ...newService,
      currency: newCurrency,
      totalAmount: newBudget > 0 ? newBudget.toFixed(2) : "",
      payments: updatedPayments
    });
  };

  const updatePayment = (index, field, value) => {
    const updated = [...newService.payments];
    updated[index][field] = value;
    
    // Recalculate amount if percent changed
    if (field === "percent" && newService.totalAmount) {
      updated[index].amount = ((parseFloat(newService.totalAmount) * parseFloat(value)) / 100).toFixed(2);
    }
    // Recalculate percent if amount changed
    if (field === "amount" && newService.totalAmount) {
      updated[index].percent = ((parseFloat(value) / parseFloat(newService.totalAmount)) * 100).toFixed(1);
    }
    
    setNewService({ ...newService, payments: updated });
  };

  // Handle service type selection - set suggested base_price
  const handleServiceTypeSelect = (selectedTypeName) => {
    const selectedType = serviceTypes.find(t => t.name === selectedTypeName);
    const basePrice = selectedType?.base_price || 0;
    const selectedTypeId = selectedType?.id || null;
    
    // "Payment" is the custom free-text option
    if (selectedTypeName === "Payment") {
      setNewService({ 
        ...newService, 
        service_type: selectedTypeName,
        service_type_id: null,
        name: "",
        suggestedBasePrice: 0,
      });
      return;
    }
    
    // Use base_price if available, convert from EUR to selected currency
    if (basePrice > 0 && newService.currency) {
      const basePriceUAH = basePrice * (rates["EUR"] || 50);  // base_price is in EUR
      const convertedPrice = newService.currency === "UAH" 
        ? basePriceUAH 
        : basePriceUAH / (rates[newService.currency] || 1);
      
      setNewService({ 
        ...newService, 
        service_type: selectedTypeName,
        service_type_id: selectedTypeId,
        name: selectedTypeName,  // Auto-fill name with service type
        totalAmount: convertedPrice.toFixed(2),
        suggestedBasePrice: basePrice,  // Store original EUR price
        payments: [{
          ...newService.payments[0],
          amount: convertedPrice.toFixed(2)
        }]
      });
    } else {
      setNewService({ 
        ...newService, 
        service_type: selectedTypeName,
        service_type_id: selectedTypeId,
        name: selectedTypeName  // Auto-fill name
      });
    }
  };

  const addPaymentRow = () => {
    const nextMonth = monthOptions[newService.payments.length + 2]?.value || "";
    setNewService({
      ...newService,
      payments: [...newService.payments, { month: nextMonth, percent: "", amount: "" }],
    });
  };

  const removePaymentRow = (index) => {
    if (newService.payments.length > 1) {
      setNewService({
        ...newService,
        payments: newService.payments.filter((_, i) => i !== index),
      });
    }
  };

  const recalculateAmounts = () => {
    if (!newService.totalAmount) return;
    const total = parseFloat(newService.totalAmount);
    const updated = newService.payments.map(p => ({
      ...p,
      amount: ((total * parseFloat(p.percent || 0)) / 100).toFixed(2),
    }));
    setNewService({ ...newService, payments: updated });
  };

  const handleAddService = async () => {
    if (!newService.service_type) {
      toast.error("Select a service type");
      return;
    }
    if (!newService.totalAmount || parseFloat(newService.totalAmount) <= 0) {
      toast.error("Enter budget amount");
      return;
    }
    
    // Use service type name or custom name
    const serviceName = newService.name || newService.service_type || "Payment";
    
    // Get legal entity name for display
    const legalEntity = legalEntities.find(e => e.id === newService.legal_entity_id);
    const legalEntityName = legalEntity?.name || "";
    const billingProfile = (getClient()?.billing_profiles || [])
      .find(p => p.id === newService.client_billing_profile_id);
    const billingProfileLabel = billingProfile?.label || billingProfile?.legal_name || "";
    
    try {
      if (newService.splitPayments && newService.payments.length > 1) {
        // Create split payments
        await api.post(`/sales-services/split`, {
          project_id: actualProjectId,
          name: serviceName,
          service_type: newService.service_type || "Payment",
          service_type_id: newService.service_type_id || null,
          total_amount: parseFloat(newService.totalAmount),
          currency: newService.currency,
          probability: newService.probability,
          legal_entity_id: newService.legal_entity_id,
          legal_entity_name: legalEntityName,
          client_billing_profile_id: newService.client_billing_profile_id || null,
          client_billing_profile_label: billingProfileLabel,
          payments: newService.payments.map(p => ({
            month: p.month,
            percent: parseFloat(p.percent) || 0,
            amount: parseFloat(p.amount) || 0,
          })),
        });
        toast.success(`Created ${newService.payments.length} payments`);
      } else {
        // Single payment
        await api.post(`/sales-services`, {
          project_id: actualProjectId,
          name: serviceName,
          service_type: newService.service_type || "Payment",
          service_type_id: newService.service_type_id || null,
          amount: parseFloat(newService.totalAmount),
          currency: newService.currency,
          probability: newService.probability,
          legal_entity_id: newService.legal_entity_id,
          legal_entity_name: legalEntityName,
          client_billing_profile_id: newService.client_billing_profile_id || null,
          month: newService.payments[0]?.month,
        });
        toast.success("Payment added");
      }
      setShowNewService(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to add payment");
    }
  };

  const handleTogglePaid = async (serviceId, isPaid) => {
    try {
      await api.put(`/sales-services/${serviceId}`, { 
        is_paid: isPaid,
        paid_date: isPaid ? new Date().toISOString().split("T")[0] : null,
      });
      toast.success(isPaid ? "Marked as paid" : "Marked as unpaid");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Open edit payment dialog
  const handleEditPayment = (payment) => {
    setEditPaymentForm({
      name: payment.name || "",
      amount: payment.amount || "",
      currency: payment.currency || "EUR",
      legal_entity_id: payment.legal_entity_id || "",
      client_billing_profile_id: payment.client_billing_profile_id || "",
      contract_number: payment.contract_number || project.contract_number || "",
      contract_date: payment.contract_date || project.contract_date || "",
      month: payment.month || "",
    });
    setEditingPayment(payment);
  };

  // Save edited payment
  const handleSavePayment = async () => {
    if (!editingPayment) return;
    try {
      const legalEntity = legalEntities.find(e => e.id === editPaymentForm.legal_entity_id);
      const clientObj = getClient();
      const billingProfile = (clientObj?.billing_profiles || []).find(p => p.id === editPaymentForm.client_billing_profile_id);
      await api.put(`/sales-services/${editingPayment.id}`, {
        name: editPaymentForm.name,
        amount: parseFloat(editPaymentForm.amount),
        currency: editPaymentForm.currency,
        legal_entity_id: editPaymentForm.legal_entity_id,
        legal_entity_name: legalEntity?.name || "",
        client_billing_profile_id: editPaymentForm.client_billing_profile_id || null,
        client_billing_profile_label: billingProfile?.label || billingProfile?.legal_name || "",
        contract_number: editPaymentForm.contract_number,
        contract_date: editPaymentForm.contract_date,
        month: editPaymentForm.month,
      });
      toast.success("Payment updated");
      setEditingPayment(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.delete(`/sales-services/${serviceId}`);
      toast.success("Deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Create payment from approved price request
  const handleCreatePaymentFromRequest = async (request) => {
    if (creatingPayment) return;
    setCreatingPayment(true);
    try {
      await api.post(`/price-requests/${request.id}/create-payment`, {
        currency: "USD",
        month: new Date().toISOString().slice(0, 7),
      });
      toast.success("Payment created from request");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment");
    } finally {
      setCreatingPayment(false);
    }
  };

  // Create payment directly from a task row
  const handleCreatePaymentFromTask = async (task) => {
    if (creatingPayment) return;
    setCreatingPayment(true);
    try {
      await api.post(`/sales-services`, {
        project_id: actualProjectId,
        name: task.name || task.task_type || "Payment",
        // Old tasks carry snake_case slugs in task_type (exterior_render, ...) —
        // normalize so /sales-pnl groups them with catalog display names.
        service_type: normalizeServiceType(task.task_type) || task.name || "Payment",
        service_type_id: task.service_type_id || null,
        amount: task.client_price || 0,
        currency: task.currency || project?.currency || "USD",
        probability: 100,
        month: new Date().toISOString().slice(0, 7),
        from_task_id: task.id,
      });
      toast.success(`Payment "${task.name}" created`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment");
    } finally {
      setCreatingPayment(false);
    }
  };

  // Open split payment dialog
  const handleOpenSplitDialog = (payment) => {
    setPaymentToSplit(payment);
    setSplitParts([
      { percent: 50, month: payment.month || new Date().toISOString().slice(0, 7) },
      { percent: 50, month: "" },
    ]);
    setSplitPaymentDialogOpen(true);
  };

  // Add split part row
  const addSplitPart = () => {
    setSplitParts([...splitParts, { percent: 0, month: "" }]);
  };

  // Remove split part row
  const removeSplitPart = (idx) => {
    if (splitParts.length > 2) {
      setSplitParts(splitParts.filter((_, i) => i !== idx));
    }
  };

  // Update split part
  const updateSplitPart = (idx, field, value) => {
    const updated = [...splitParts];
    updated[idx] = { ...updated[idx], [field]: value };
    setSplitParts(updated);
  };

  // Execute split payment
  const handleSplitPayment = async () => {
    if (!paymentToSplit || splittingPayment) return;
    
    const totalPercent = splitParts.reduce((sum, p) => sum + (parseFloat(p.percent) || 0), 0);
    if (Math.abs(totalPercent - 100) > 0.5) {
      toast.error(`Total must equal 100% (currently ${totalPercent.toFixed(1)}%)`);
      return;
    }
    
    // Check all months are filled
    const emptyMonths = splitParts.filter(p => !p.month);
    if (emptyMonths.length > 0) {
      toast.error("Please select month for all parts");
      return;
    }
    
    setSplittingPayment(true);
    try {
      await api.post(`/sales-services/${paymentToSplit.id}/split-existing`, {
        splits: splitParts.map(p => ({
          percent: parseFloat(p.percent) || 0,
          month: p.month,
        })),
      });
      toast.success(`Split into ${splitParts.length} payments`);
      setSplitPaymentDialogOpen(false);
      setPaymentToSplit(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to split payment");
    } finally {
      setSplittingPayment(false);
    }
  };

  // Open link transaction dialog for a payment
  const handleOpenLinkTransaction = async (service) => {
    setLinkingServiceId(service.id);
    setLoadingTransactions(true);
    setLinkTransactionDialogOpen(true);
    
    try {
      // Fetch unmatched transactions, optionally filter by similar currency/amount
      const params = new URLSearchParams();
      if (service.currency) params.append("currency", service.currency);
      // Get transactions with amount range +-20% of service amount
      const minAmount = service.amount * 0.8;
      const maxAmount = service.amount * 1.2;
      params.append("min_amount", minAmount.toString());
      params.append("max_amount", maxAmount.toString());
      params.append("limit", "30");
      
      const response = await api.get(`/bank-transactions/unmatched?${params.toString()}`);
      setUnmatchedTransactions(response.data.items || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Не удалось загрузить транзакции");
      setUnmatchedTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Link a payment to a bank transaction
  const handleLinkTransaction = async (transactionId) => {
    if (!linkingServiceId) return;
    
    try {
      await api.post(`/sales-services/${linkingServiceId}/link-transaction?transaction_id=${transactionId}`);
      toast.success("Платёж связан с банковской транзакцией");
      setLinkTransactionDialogOpen(false);
      setLinkingServiceId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка при связывании");
    }
  };

  // Unlink a payment from bank transaction
  const handleUnlinkTransaction = async (serviceId) => {
    try {
      await api.delete(`/sales-services/${serviceId}/link-transaction`);
      toast.success("Связь с транзакцией удалена");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка при удалении связи");
    }
  };

  // Download Invoice (Счёт) for a payment
  const handleDownloadInvoice = async (service) => {
    try {
      toast.info("Generating invoice...");
      const response = await api.get(`/sales-services/${service.id}/invoice?format=docx`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${service.name?.replace(/[^a-zA-Z0-9]/g, '_')}_${service.month || 'payment'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded");
    } catch (error) {
      toast.error("Failed to generate invoice");
    }
  };

  // Mark invoice as sent to client (locks in due_date for hard cashflow)
  const handleMarkInvoiceSent = async (service) => {
    try {
      await api.post(`/sales-services/${service.id}/mark-invoice-sent`, {});
      toast.success("Invoice marked as sent");
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to mark sent");
    }
  };

  // Roll back the "sent" mark
  const handleUnmarkInvoiceSent = async (service) => {
    if (!window.confirm("Remove the 'invoice sent' mark? Cashflow forecast will move it back to 'not invoiced'.")) return;
    try {
      await api.post(`/sales-services/${service.id}/unmark-invoice-sent`);
      toast.success("Invoice mark removed");
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to unmark");
    }
  };

  // Download Act (Акт) for a paid payment
  const handleDownloadAct = async (service) => {
    try {
      toast.info("Generating act...");
      const response = await api.get(`/sales-services/${service.id}/act?format=docx`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Act_${service.name?.replace(/[^a-zA-Z0-9]/g, '_')}_${service.month || 'payment'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Act downloaded");
    } catch (error) {
      toast.error("Failed to generate act");
    }
  };

  // Create Stripe payment link for a service
  const handleCreateStripeLink = async (service) => {
    try {
      const res = await api.post("/stripe/create-payment-link", {
        service_id: service.id,
        origin_url: window.location.origin,
      });
      const url = res.data.checkout_url;
      await navigator.clipboard.writeText(url);
      setServices(prev => prev.map(s => s.id === service.id ? { ...s, stripe_checkout_url: url, stripe_payment_status: "pending" } : s));
      toast.success("Stripe link copied to clipboard!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create Stripe link");
    }
  };

  // Send Payment Request to client for a service
  const handleSendPaymentRequest = async (service) => {
    const amount = service.actual_amount || service.amount;
    const currency = service.currency || "USD";
    
    try {
      await api.post("/client-requests", {
        client_id: project?.client_id,
        project_id: actualProjectId,
        service_id: service.id,
        request_type: "payment_request",
        title: `Payment Request: ${service.name}`,
        description: `Please process payment of ${amount?.toLocaleString() || 0} ${currency} for ${service.name}`,
        ball_in_court: "client",
        expected_action: "payment",
        payment_amount: amount,
        payment_currency: currency,
      });
      setSentPaymentRequests(prev => new Set([...prev, service.id]));
      toast.success("Payment request sent to client");
    } catch (error) {
      toast.error(formatApiError(error, "Failed to send request"));
    }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm("Delete this project and all services?")) return;
    try {
      await api.delete(`/unified-projects/${actualProjectId}`);
      toast.success("Deleted");
      navigate("/sales");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete");
    }
  };

  // Open client request details
  const openViewRequest = async (requestId) => {
    setViewRequestLoading(true);
    try {
      const res = await api.get(`/client-requests/${requestId}`);
      setViewRequest(res.data);
    } catch (error) {
      toast.error("Failed to load request details");
    } finally {
      setViewRequestLoading(false);
    }
  };

  // Delete client request
  const deleteClientRequest = async (requestId) => {
    if (!window.confirm("Delete this client request?")) return;
    try {
      await api.delete(`/client-requests/${requestId}`);
      toast.success("Request deleted");
      // Refresh client requests
      const requestsRes = await api.get(`/client-requests`);
      const projectRequests = (requestsRes.data || []).filter(r => r.project_id === actualProjectId);
      setClientRequests(projectRequests);
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  // Get client - support both client_id (old) and client_name (new)
  const getClient = () => {
    if (project?.client_id) {
      return clients.find(c => c.id === project.client_id);
    }
    return null;
  };
  
  const getClientName = () => {
    const client = getClient();
    return client?.name || project?.client_name || "Unknown";
  };
  
  const getManager = () => managers.find(m => m.id === project?.sales_manager_id);
  
  // Get PM with contact info
  const getProjectManager = () => {
    if (project?.project_manager_id) {
      return pmManagers.find(pm => pm.id === project.project_manager_id);
    }
    return null;
  };
  
  // Calculate totals including task costs
  // What the payments imply the project contains. This is what `service_types`
  // has always been — a by-product of billing — and what the client's view was
  // built from. Shown as a suggestion until someone states the scope.
  // backlog 736d54ea.
  const derivedServiceTypes = useMemo(() => {
    const seen = [];
    for (const svc of services) {
      const name = (svc.service_type || svc.name || "").trim();
      if (name && !seen.includes(name)) seen.push(name);
    }
    return seen;
  }, [services]);

  // The services this project actually has: the explicit scope when someone set
  // one, otherwise the list implied by its payments. Same list the scope chips
  // show — materials hang off it so they exist on every project, not only the
  // 14 of 183 with an explicit scope. backlog 7165e045.
  const scopeServices = useMemo(
    () => ((formData.planned_services || []).length ? formData.planned_services : derivedServiceTypes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.planned_services, derivedServiceTypes],
  );

  const totals = useMemo(() => {
    let planUAH = 0, factUAH = 0;
    services.forEach(s => {
      let amountUAH = s.amount;
      if (s.currency === "USD") amountUAH *= (rates?.USD || 41.5);
      else if (s.currency === "EUR") amountUAH *= (rates?.EUR || 43.5);
      
      planUAH += amountUAH;
      if (s.is_paid) factUAH += amountUAH;
    });
    
    // Calculate task costs (artist + art director fees + staff hours)
    let artistCostUAH = 0;
    let artDirectorCostUAH = 0;
    let staffHoursCostUAH = 0;
    let tasksRevenueUAH = 0; // Sum of client_price for all tasks
    
    tasks.forEach(t => {
      let costUAH = t.agreed_price || 0;
      let adFeeUAH = t.art_director_fee || 0;
      // Staff cost: prefer manual staff_cost_total (UAH); otherwise fall back to the
      // Phase 3-preview Worksection rollup (USD → UAH). Mirrors the per-task Costs
      // column (see Tasks table) so the project total includes Worksection-sourced
      // costs that previously were shown per task but dropped from the total. (backlog caab2312)
      const manualStaffCostUAH = t.staff_cost_total || 0;
      const wsCostUSD = wsRollupByTask[t.id]?.total_cost_usd || 0;
      const wsCostUAH = (wsCostUSD > 0 && rates?.USD) ? wsCostUSD * rates.USD : 0;
      let staffCostUAH = manualStaffCostUAH > 0 ? manualStaffCostUAH : wsCostUAH;
      let clientPriceUAH = t.client_price || 0;
      
      // Convert artist and AD costs to UAH based on task currency
      if (t.currency === "USD") {
        costUAH *= (rates?.USD || 41.5);
        adFeeUAH *= (rates?.USD || 41.5);
        clientPriceUAH *= (rates?.USD || 41.5);
      } else if (t.currency === "EUR") {
        costUAH *= (rates?.EUR || 43.5);
        adFeeUAH *= (rates?.EUR || 43.5);
        clientPriceUAH *= (rates?.EUR || 43.5);
      }
      
      artistCostUAH += costUAH;
      artDirectorCostUAH += adFeeUAH;
      staffHoursCostUAH += staffCostUAH;
      tasksRevenueUAH += clientPriceUAH;
    });
    
    const totalCostUAH = artistCostUAH + artDirectorCostUAH + staffHoursCostUAH;
    const netProfitUAH = planUAH - totalCostUAH;
    const marginPercent = planUAH > 0 ? ((netProfitUAH / planUAH) * 100) : 0;
    
    // Calculate difference between payments and tasks
    const paymentTasksDiffUAH = tasksRevenueUAH - planUAH;
    
    // Calculate requests total (incoming approved/pending price requests)
    const requestsTotalUAH = (() => {
      let total = 0;
      priceRequests.filter(r => r.status !== "rejected").forEach(r => {
        let amt = (r.proposed_price || 0) * (r.quantity || 1);
        // price requests are typically in USD
        total += amt * (rates?.USD || 41.5);
      });
      outgoingRequests.filter(r => r.status !== "rejected").forEach(r => {
        let amt = r.amount || 0;
        total += amt * (rates?.USD || 41.5);
      });
      return total;
    })();
    
    // Requests vs Payments difference
    const requestsPaymentsDiffUAH = requestsTotalUAH > 0 ? requestsTotalUAH - planUAH : 0;
    
    return { 
      planUAH, 
      factUAH, 
      paidCount: services.filter(s => s.is_paid).length,
      artistCostUAH,
      artDirectorCostUAH,
      staffHoursCostUAH,
      totalCostUAH,
      netProfitUAH,
      marginPercent,
      taskCount: tasks.length,
      tasksRevenueUAH,
      paymentTasksDiffUAH,
      requestsTotalUAH,
      requestsPaymentsDiffUAH,
    };
  }, [services, tasks, rates, priceRequests, outgoingRequests, wsRollupByTask]);


  // Group services by payment_group_id
  const groupedServices = useMemo(() => {
    const groups = {};
    const standalone = [];
    services.forEach(s => {
      if (s.payment_group_id) {
        if (!groups[s.payment_group_id]) groups[s.payment_group_id] = [];
        groups[s.payment_group_id].push(s);
      } else {
        standalone.push(s);
      }
    });
    return { groups, standalone };
  }, [services]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-zinc-400">Project not found</p>
        <Button onClick={() => navigate("/sales")} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />Back
        </Button>
      </div>
    );
  }

  const totalPercent = newService.payments.reduce((sum, p) => sum + (parseFloat(p.percent) || 0), 0);

  return (
    <div data-testid="sales-project-edit-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(isClient ? "/client/projects" : "/sales")} className="text-zinc-400 hover:text-zinc-50 h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-zinc-50">{formData.name || "Edit Project"}</h1>
            <p className="text-xs text-zinc-500">{getClientName()} • {getManager()?.name || "No manager"}</p>
          </div>
        </div>
        {!isClient && (
          <div className="flex items-center gap-2">
{/* Standalone "Gantt / Timeline" button removed — Gantt now lives inside the Tasks block as a Table/Gantt toggle. Fullscreen escape at /project-timeline/:id still works if you navigate directly. */}
            {project?.client_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  // Open the tab synchronously (popup-blocker safe), then drop the
                  // short-lived read-only client token in once it's minted.
                  const win = window.open("", "_blank");
                  if (win) win.document.write("Завантаження кабінету клієнта…");
                  try {
                    const { data } = await api.post("/admin/impersonate-client", {
                      client_id: project.client_id,
                    });
                    const url = `/client#imptoken=${encodeURIComponent(data.token)}`;
                    if (win) win.location.href = url;
                    else window.open(url, "_blank");
                  } catch (e) {
                    if (win) win.close();
                    toast.error(e?.response?.data?.detail || "Не вдалося відкрити кабінет клієнта");
                  }
                }}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8"
                title="Подивитись кабінет клієнта 1:1 (тільки для перегляду)"
              >
                <Eye className="h-3.5 w-3.5 mr-1" />Кабінет клієнта
              </Button>
            )}
            <Button variant="destructive" size="sm" onClick={handleDeleteProject} className="bg-red-900/50 hover:bg-red-900 text-red-400 h-8">
              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 h-8">
              <Save className="h-3.5 w-3.5 mr-1" />{saving ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Project Details - Compact Version */}
        <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
          <CardHeader className="pb-1 pt-3">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-sm">
              <FolderOpen className="h-4 w-4 text-sky-400" />Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-1 pb-3">
            {/* CLIENT VIEW: Read-only simplified project info */}
            {isClient ? (
              <>
                {/* Row 1: Project name and status */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px]">Project Name</Label>
                    <div className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-50 text-sm h-8 flex items-center">
                      {formData.name || "—"}
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px]">Status</Label>
                    <div className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-sm h-8 flex items-center">
                      <Badge className={cn("text-[10px]", getProjectStatus(formData.status)?.color)}>
                        {getProjectStatus(formData.status)?.name || formData.status}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* What we are doing for you — the scope, stated rather than
                    guessed from the invoices. backlog 736d54ea. */}
                {((formData.planned_services || []).length > 0 || derivedServiceTypes.length > 0) && (
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px]">Що ми для вас робимо</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {((formData.planned_services || []).length ? formData.planned_services : derivedServiceTypes).map((svc) => (
                        <Badge key={svc} className="h-6 bg-sky-500/10 text-sky-300 border-sky-500/30 text-[11px] font-normal">
                          {svc}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Row 2: Manager contact info */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px] flex items-center gap-0.5">
                      <User className="h-2.5 w-2.5" />Your Project Manager
                    </Label>
                    <div className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-300 text-sm h-8 flex items-center">
                      {project?.project_manager_name || "Not assigned yet"}
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px] flex items-center gap-0.5">
                      💬 Communication Channel
                    </Label>
                    {formData.communication_group ? (
                      <a 
                        href={formData.communication_group} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-2 py-1 bg-sky-900/30 border border-sky-700/50 rounded-md text-sky-400 text-sm h-8 flex items-center hover:bg-sky-900/50 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />Open Chat
                      </a>
                    ) : (
                      <div className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-500 text-sm h-8 flex items-center">
                        Not set
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Row 3: Timeline info */}
                <div className="grid grid-cols-3 gap-2 pt-1 border-t border-zinc-800/50">
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px] flex items-center gap-0.5">
                      <Calendar className="h-2.5 w-2.5" />Start Date
                    </Label>
                    <div className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-300 text-sm h-8 flex items-center">
                      {formData.start_date ? new Date(formData.start_date).toLocaleDateString() : "Not set"}
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px]">Deadline</Label>
                    <div className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-300 text-sm h-8 flex items-center">
                      {formData.deadline ? new Date(formData.deadline).toLocaleDateString() : "Not set"}
                      {project?.deadline_adjustments > 0 && (
                        <span className="ml-1 text-[9px] text-orange-400">+{project.deadline_adjustments}d</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px] flex items-center gap-0.5">
                      📄 Contract
                    </Label>
                    {formData.contract_link ? (
                      <a 
                        href={formData.contract_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-2 py-1 bg-green-900/30 border border-green-700/50 rounded-md text-green-400 text-sm h-8 flex items-center hover:bg-green-900/50 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />View Contract
                      </a>
                    ) : (
                      <div className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-500 text-sm h-8 flex items-center">
                        Not available
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              /* MANAGER VIEW: Full editable form */
              <>
                {/* Row 1: Name, Client */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-zinc-500 text-[10px]">Project Name</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm" />
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <Label className="text-zinc-500 text-[10px]">Client</Label>
                    <ClientSelector
                      value={formData.client_id}
                      clients={clients}
                      onChange={(clientId, clientName) => {
                        setFormData({ ...formData, client_id: clientId, client_name: clientName });
                        setProject(prev => prev ? { ...prev, client_id: clientId, client_name: clientName } : prev);
                      }}
                      onClientCreated={(newClient) => {
                        setClients(prev => [...prev, newClient]);
                      }}
                      compact
                    />
                  </div>
                </div>

                {/* Scope — which services this project is supposed to deliver.
                    Asked for because nothing on the project said it: the list of
                    services was whatever payments happened to exist, so before
                    the payments were split nobody could say what was promised,
                    and the client's "що ми для вас робимо" was a billing
                    by-product. backlog 7659abe8 / 736d54ea. */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label className="text-zinc-500 text-[10px]">Services in scope</Label>
                    {(formData.planned_services || []).length === 0 && derivedServiceTypes.length > 0 && (
                      <span className="text-[10px] text-zinc-600">
                        — none picked, showing what the payments imply
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(formData.planned_services || []).map((svc) => (
                      <Badge
                        key={svc}
                        className="h-6 bg-sky-500/10 text-sky-300 border-sky-500/30 text-[11px] font-normal gap-1"
                      >
                        {svc}
                        <button
                          type="button"
                          onClick={() => setFormData({
                            ...formData,
                            planned_services: (formData.planned_services || []).filter(x => x !== svc),
                          })}
                          className="text-sky-400/70 hover:text-rose-400"
                          title="Remove from scope"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                    {(formData.planned_services || []).length === 0 && derivedServiceTypes.map((svc) => (
                      <Badge
                        key={`derived-${svc}`}
                        className="h-6 bg-zinc-800 text-zinc-400 border-zinc-700 text-[11px] font-normal cursor-pointer hover:text-zinc-100"
                        title="From a payment on this project — click to put the whole implied list into scope"
                        onClick={() => setFormData({
                          ...formData,
                          planned_services: derivedServiceTypes,
                          service_materials: attachTemplateMaterials(formData.service_materials, derivedServiceTypes, materialTemplates),
                        })}
                      >
                        {svc}
                      </Badge>
                    ))}
                    <Select
                      value=""
                      onValueChange={(v) => {
                        if (!v) return;
                        const current = formData.planned_services || [];
                        if (current.includes(v)) return;
                        // The service brings its materials list from Admin → Services.
                        setFormData({
                          ...formData,
                          planned_services: [...current, v],
                          service_materials: attachTemplateMaterials(formData.service_materials, [v], materialTemplates),
                        });
                      }}
                    >
                      <SelectTrigger
                        className="h-6 w-[150px] bg-zinc-800 border-zinc-700 border-dashed text-zinc-400 text-[11px]"
                        data-testid="project-scope-add"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        <SelectValue placeholder="Add service" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700 max-h-72">
                        {serviceTypes
                          .filter(st => !(formData.planned_services || []).includes(st.name))
                          .map(st => (
                            <SelectItem key={st.id || st.name} value={st.name} className="text-zinc-50 text-xs">
                              {st.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* What the client still owes us, per service. It lived in
                      chat, so "did they send the chess board?" had no answer
                      anyone could look up — including the client, who sees the
                      same list in their cabinet. backlog 4ab0c5c1, WS 23241245.

                      Fed by scopeServices, not planned_services: an explicit
                      scope exists on 14 projects out of 183, and everywhere
                      else the chips are inferred from payments — so the panel
                      simply was not there, which is what the PM lead was
                      asking about. backlog 7165e045. */}
                  <ServiceMaterialsPanel
                    services={scopeServices}
                    materials={formData.service_materials || []}
                    templates={materialTemplates}
                    onChange={(next) => setFormData((f) => ({ ...f, service_materials: next }))}
                    review={formData.materials_review || []}
                    onShowToClient={(svc) => setFormData((f) => ({
                      ...f,
                      materials_review: (f.materials_review || []).filter((k) => k !== materialKey(svc)),
                    }))}
                  />
                </div>

                {/* Row 2: Status & Managers - compact */}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 h-7 text-xs w-[120px]" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {projectStatuses.map(s => <SelectItem key={s.id} value={s.id} className="text-zinc-50 text-xs">{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Select 
                    value={String(formData.probability || 50)} 
                    onValueChange={(v) => setFormData({ ...formData, probability: parseInt(v) })}
                  >
                    <SelectTrigger className={cn(
                      "h-7 w-[70px] text-xs bg-zinc-800 border-zinc-700",
                      formData.probability >= 80 ? "text-green-400" :
                      formData.probability >= 50 ? "text-zinc-200" :
                      formData.probability >= 30 ? "text-yellow-400" :
                      "text-zinc-400"
                    )}>
                      <Target className="h-3 w-3 mr-1 text-zinc-500" />
                      <SelectValue>{formData.probability}%</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(p => (
                        <SelectItem key={p} value={String(p)} className="text-zinc-50 text-xs">
                          {p}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={formData.project_manager_id || "not_assigned"} onValueChange={(v) => setFormData({ ...formData, project_manager_id: v === "not_assigned" ? "" : v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 h-7 text-xs w-[140px]">
                      <User className="h-3 w-3 mr-1 text-blue-400" />
                      <SelectValue placeholder="PM" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="not_assigned" className="text-zinc-400 text-xs">No PM</SelectItem>
                      {pmManagers.map(pm => <SelectItem key={pm.id} value={pm.id} className="text-zinc-50 text-xs">{pm.name}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Select value={formData.sales_manager_id || "not_assigned"} onValueChange={(v) => setFormData({ ...formData, sales_manager_id: v === "not_assigned" ? "" : v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 h-7 text-xs w-[140px]">
                      <DollarSign className="h-3 w-3 mr-1 text-green-400" />
                      <SelectValue placeholder="Sales" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="not_assigned" className="text-zinc-400 text-xs">No Sales</SelectItem>
                      {managers.map(m => <SelectItem key={m.id} value={m.id} className="text-zinc-50 text-xs">{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  {/* Project-level Art Director — admin/pm_lead only (mirrors backend gate at PUT /unified-projects). */}
                  {(user?.role === "admin" || user?.role === "pm_lead") ? (
                    <Select
                      value={formData.art_director_id || "not_assigned"}
                      onValueChange={(v) => setFormData({ ...formData, art_director_id: v === "not_assigned" ? "" : v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 h-7 text-xs w-[160px]" data-testid="select-project-art-director">
                        <User className="h-3 w-3 mr-1 text-purple-400" />
                        <SelectValue placeholder="Art Director" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="not_assigned" className="text-zinc-400 text-xs">No Art Director</SelectItem>
                        {artDirectors.map(ad => (
                          <SelectItem key={ad.id} value={ad.id} className="text-zinc-50 text-xs">{ad.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : project?.art_director_name ? (
                    <div
                      className="bg-zinc-800 border border-zinc-700 rounded-md h-7 text-xs px-2 flex items-center text-purple-300"
                      title="Project Art Director (read-only — only admin or pm_lead can change)"
                    >
                      <User className="h-3 w-3 mr-1 text-purple-400" />
                      {project.art_director_name}
                    </div>
                  ) : null}
                </div>
                
                {/* Row 3: Notes — still collapsed until clicked, but the box
                    shows ~2000 characters and scrolls the rest (up to 20000):
                    these notes carry the client's language, decision makers and
                    contract numbers, and three clamped lines hid most of it.
                    backlog 0a0ef5c5; Kostya 29.10. */}
                <div className="space-y-0.5">
                  {(notesExpanded || !formData.notes) ? (
                    <Textarea
                      ref={notesRef}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      onBlur={() => { if (formData.notes) setNotesExpanded(false); }}
                      onMouseUp={rememberNotesHeight}
                      autoFocus={notesExpanded}
                      style={notesHeight ? { height: notesHeight } : undefined}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[320px] text-xs resize-y"
                      rows={20}
                      maxLength={20000}
                      placeholder="Project notes..."
                      data-testid="project-notes-input"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setNotesExpanded(true)}
                      className="w-full flex items-start gap-2 text-left bg-zinc-800/60 border border-zinc-700 rounded-md min-h-[56px] max-h-[320px] overflow-y-auto py-2 px-2.5 text-xs text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
                      title="Click to edit notes"
                      data-testid="project-notes-preview"
                    >
                      <FileText className="h-3 w-3 text-zinc-500 shrink-0 mt-0.5" />
                      <span className="whitespace-pre-wrap break-words">{formData.notes}</span>
                    </button>
                  )}
                </div>
                
                {/* Row 4: Contract & Timeline - compact inline badges */}
                {PRODUCTION_STATUSES.includes(formData.status) && (
                  <div className="text-[10px] text-orange-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Fields with <span className="text-red-400">*</span> required for production
                  </div>
                )}
                <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-zinc-800/50">
                  {/* Contract Link */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.contract_link 
                            ? "bg-green-900/30 border-green-700/50 text-green-400" 
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-400",
                          PRODUCTION_STATUSES.includes(formData.status) && !formData.contract_link && "border-red-500/50"
                        )}
                      >
                        <FileText className="h-3 w-3" />
                        Contract
                        {formData.contract_link && <CheckCircle2 className="h-3 w-3" />}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Contract Link {PRODUCTION_STATUSES.includes(formData.status) && <span className="text-red-400">*</span>}</Label>
                        <Input 
                          value={formData.contract_link} 
                          onChange={(e) => setFormData({ ...formData, contract_link: e.target.value })} 
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm" 
                          placeholder="https://..." 
                        />
                        {formData.contract_link && (
                          <a href={formData.contract_link} target="_blank" rel="noopener noreferrer" 
                             className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" /> Open contract
                          </a>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Contract № */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.contract_number 
                            ? "bg-zinc-800/80 border-zinc-600 text-zinc-200" 
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500"
                        )}
                      >
                        <Hash className="h-3 w-3" />
                        {formData.contract_number || "№"}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Contract Number</Label>
                        <Input 
                          value={formData.contract_number} 
                          onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })} 
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm" 
                          placeholder="2026/01-15" 
                        />
                        <Label className="text-xs text-zinc-400">Contract Name</Label>
                        <Input 
                          value={formData.contract_name} 
                          onChange={(e) => setFormData({ ...formData, contract_name: e.target.value })} 
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm" 
                          placeholder="Договір про надання послуг" 
                          data-testid="contract-name-input"
                        />
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Contract Date */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.contract_date 
                            ? "bg-zinc-800/80 border-zinc-600 text-zinc-200" 
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500"
                        )}
                      >
                        <Calendar className="h-3 w-3" />
                        {formData.contract_date ? new Date(formData.contract_date).toLocaleDateString('uk-UA', {day: '2-digit', month: '2-digit'}) : "Date"}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Contract Date</Label>
                        <Input 
                          type="date"
                          value={formData.contract_date} 
                          onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })} 
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm" 
                        />
                      </div>
                    </PopoverContent>
                  </Popover>

                  <span className="text-zinc-600 mx-1">│</span>

                  {/* Start Date */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.start_date
                            ? "bg-zinc-800/80 border-zinc-600 text-zinc-200"
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500",
                          PRODUCTION_STATUSES.includes(formData.status) && !formData.start_date && "border-red-500/50"
                        )}
                      >
                        <Play className="h-3 w-3 text-zinc-500" />
                        {formData.start_date ? new Date(formData.start_date).toLocaleDateString('uk-UA', {day: '2-digit', month: '2-digit'}) : "Start"}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Start Date {PRODUCTION_STATUSES.includes(formData.status) && <span className="text-red-400">*</span>}</Label>
                        <Input
                          type="date"
                          value={formData.start_date}
                          onChange={(e) => {
                            const start_date = e.target.value;
                            const auto = addBusinessDays(start_date, formData.estimated_days);
                            setFormData({ ...formData, start_date, ...(auto ? { deadline: auto } : {}) });
                          }}
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                        />
                        <p className="text-[10px] text-zinc-500">Deadline auto-fills from start date + working days (skips Sat/Sun).</p>
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Working Days */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.estimated_days 
                            ? "bg-zinc-800/80 border-zinc-600 text-zinc-200" 
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500",
                          PRODUCTION_STATUSES.includes(formData.status) && !formData.estimated_days && "border-red-500/50"
                        )}
                      >
                        <Clock className="h-3 w-3" />
                        {formData.estimated_days || 0}d
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-40 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Working Days {PRODUCTION_STATUSES.includes(formData.status) && <span className="text-red-400">*</span>}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.estimated_days}
                          onChange={(e) => {
                            const estimated_days = parseInt(e.target.value) || 0;
                            const auto = addBusinessDays(formData.start_date, estimated_days);
                            setFormData({ ...formData, estimated_days, ...(auto ? { deadline: auto } : {}) });
                          }}
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                        />
                        <p className="text-[10px] text-zinc-500">Deadline auto-fills from start date + working days (skips Sat/Sun).</p>
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Deadline */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.deadline
                            ? "bg-zinc-800/80 border-zinc-600 text-zinc-200"
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500",
                          PRODUCTION_STATUSES.includes(formData.status) && !formData.deadline && "border-red-500/50"
                        )}
                      >
                        <Target className="h-3 w-3 text-zinc-500" />
                        {formData.deadline ? new Date(formData.deadline).toLocaleDateString('uk-UA', {day: '2-digit', month: '2-digit'}) : "Deadline"}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Deadline {PRODUCTION_STATUSES.includes(formData.status) && <span className="text-red-400">*</span>}</Label>
                        <Input 
                          type="date"
                          value={formData.deadline} 
                          onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} 
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm" 
                        />
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Penalty */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.penalty_rate_per_day > 0 
                            ? "bg-red-900/30 border-red-700/50 text-red-400" 
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500"
                        )}
                      >
                        <AlertTriangle className="h-3 w-3" />
                        {formData.penalty_rate_per_day == null
                          ? (portalPenaltyRate != null ? `${portalPenaltyRate}%·def` : "default")
                          : `${formData.penalty_rate_per_day}%`}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-44 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">
                          Penalty %/day{portalPenaltyRate != null && <span className="text-zinc-500"> (empty = portal default {portalPenaltyRate}%)</span>}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          step="0.1"
                          placeholder={portalPenaltyRate != null ? `default ${portalPenaltyRate}%` : "portal default"}
                          value={formData.penalty_rate_per_day ?? ""}
                          onChange={(e) => setFormData({ ...formData, penalty_rate_per_day: e.target.value === "" ? null : (parseFloat(e.target.value) || 0) })}
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                        />
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Response Time */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.response_time_hours
                            ? "bg-zinc-800/80 border-zinc-600 text-zinc-200"
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500",
                          PRODUCTION_STATUSES.includes(formData.status) && !formData.response_time_hours && "border-red-500/50"
                        )}
                      >
                        <MessageSquare className="h-3 w-3 text-zinc-500" />
                        {formData.response_time_hours || "?"}h
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-40 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Response Time (hours) {PRODUCTION_STATUSES.includes(formData.status) && <span className="text-red-400">*</span>}</Label>
                        <Input
                          type="number"
                          min="1"
                          max="720"
                          value={formData.response_time_hours || ""}
                          onChange={(e) => setFormData({ ...formData, response_time_hours: parseInt(e.target.value) || null })}
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                        />
                      </div>
                    </PopoverContent>
                  </Popover>

                  <span className="text-zinc-600 mx-1">│</span>

                  {/* Materials link (moved off Row 3 into the badge row for a compact card) */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge
                        variant="outline"
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.materials_link
                            ? "bg-zinc-800/80 border-zinc-600 text-zinc-200"
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500"
                        )}
                      >
                        <FolderOpen className="h-3 w-3 text-sky-400" />
                        Materials
                        {formData.materials_link && <CheckCircle2 className="h-3 w-3 text-sky-400" />}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Materials link</Label>
                        <Input
                          value={formData.materials_link || ""}
                          onChange={(e) => setFormData({ ...formData, materials_link: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                          placeholder="https://..."
                        />
                        {formData.materials_link && (
                          <a href={formData.materials_link} target="_blank" rel="noopener noreferrer"
                             className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" /> Open materials
                          </a>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Chat / communication group */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge
                        variant="outline"
                        className={cn(
                          "h-7 px-2 cursor-pointer hover:bg-zinc-700 gap-1",
                          formData.communication_group
                            ? "bg-zinc-800/80 border-zinc-600 text-zinc-200"
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-500",
                          PRODUCTION_STATUSES.includes(formData.status) && !formData.communication_group && "border-red-500/50"
                        )}
                      >
                        <MessageSquare className="h-3 w-3 text-emerald-400" />
                        Chat
                        {formData.communication_group && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 bg-zinc-900 border-zinc-700 p-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-400">Chat link {PRODUCTION_STATUSES.includes(formData.status) && <span className="text-red-400">*</span>}</Label>
                        <Input
                          value={formData.communication_group}
                          onChange={(e) => setFormData({ ...formData, communication_group: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                          placeholder="https://t.me/..."
                        />
                        {formData.communication_group && (
                          <a href={formData.communication_group} target="_blank" rel="noopener noreferrer"
                             className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" /> Open chat
                          </a>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Summary Card - replacing Contacts (moved to popover) */}
        {!hideFinancials && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle
              className="text-zinc-50 flex items-center gap-2 text-sm cursor-pointer hover:text-emerald-400 transition-colors"
              onClick={cycleCurrency}
              title="Click to change currency"
            >
              <DollarSign className="h-4 w-4 text-green-400" />Summary
              <span className="text-[10px] text-zinc-500 ml-1">({displayCurrency})</span>
              
              {/* Contacts Popover */}
              <Popover>
                <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="ml-auto h-6 px-2 text-xs text-sky-400 hover:text-sky-300 hover:bg-sky-900/20"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Contacts
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 bg-zinc-900 border-zinc-700 p-3" align="end">
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-zinc-400 uppercase">Contact Information</p>
                    
                    {/* PM Contact */}
                    {getProjectManager() && (
                      <div className="p-2 bg-zinc-800/50 rounded-lg">
                        <p className="text-[10px] text-zinc-500 uppercase mb-1">Project Manager</p>
                        <p className="text-sm text-zinc-50 font-medium">{getProjectManager()?.name}</p>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {getProjectManager()?.phone && (
                            <a href={`tel:${getProjectManager()?.phone}`} className="flex items-center gap-1 text-[10px] text-sky-400 hover:text-sky-300 bg-sky-900/20 px-1.5 py-0.5 rounded">
                              <Phone className="h-2.5 w-2.5" />{getProjectManager()?.phone}
                            </a>
                          )}
                          {getProjectManager()?.telegram && (
                            <a href={`https://t.me/${getProjectManager()?.telegram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-sky-400 hover:text-sky-300 bg-sky-900/20 px-1.5 py-0.5 rounded">
                              <MessageSquare className="h-2.5 w-2.5" />{getProjectManager()?.telegram}
                            </a>
                          )}
                          {getProjectManager()?.email && (
                            <a href={`mailto:${getProjectManager()?.email}`} className="flex items-center gap-1 text-[10px] text-sky-400 hover:text-sky-300 bg-sky-900/20 px-1.5 py-0.5 rounded">
                              <Mail className="h-2.5 w-2.5" />{getProjectManager()?.email}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Client Contact */}
                    {getClient() && (
                      <div className="p-2 bg-zinc-800/50 rounded-lg">
                        <p className="text-[10px] text-zinc-500 uppercase mb-1">Client</p>
                        <p className="text-sm text-zinc-50 font-medium">{getClient()?.contact_person || getClient()?.name}</p>
                        {getClient()?.company_name && (
                          <p className="text-[10px] text-zinc-400 flex items-center gap-1">
                            <Building2 className="h-2.5 w-2.5" />{getClient()?.company_name}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {getClient()?.phone && (
                            <a href={`tel:${getClient()?.phone}`} className="flex items-center gap-1 text-[10px] text-sky-400 hover:text-sky-300 bg-sky-900/20 px-1.5 py-0.5 rounded">
                              <Phone className="h-2.5 w-2.5" />{getClient()?.phone}
                            </a>
                          )}
                          {getClient()?.email && (
                            <a href={`mailto:${getClient()?.email}`} className="flex items-center gap-1 text-[10px] text-sky-400 hover:text-sky-300 bg-sky-900/20 px-1.5 py-0.5 rounded">
                              <Mail className="h-2.5 w-2.5" />{getClient()?.email}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {!getProjectManager() && !getClient() && (
                      <p className="text-xs text-zinc-500 text-center py-2">No contacts available</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {/* Revenue - compact */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 bg-zinc-800/50 rounded cursor-pointer hover:bg-zinc-800" onClick={cycleCurrency}>
                <p className="text-[9px] text-zinc-500 uppercase">Plan</p>
                <p className="text-base font-bold text-zinc-50">{formatAmount(totals.planUAH, "UAH")}</p>
              </div>
              <div className="p-2 bg-green-900/20 border border-green-800/30 rounded cursor-pointer hover:bg-green-900/30" onClick={cycleCurrency}>
                <p className="text-[9px] text-green-500 uppercase">Paid</p>
                <p className="text-base font-bold text-green-400">{formatAmount(totals.factUAH, "UAH")}</p>
                <p className="text-[9px] text-zinc-500">{totals.paidCount}/{services.length}</p>
              </div>
            </div>
            
            {/* Costs - compact inline */}
            {tasks.length > 0 && (
              <div className="p-2 bg-red-900/10 border border-red-800/30 rounded space-y-0.5 cursor-pointer hover:bg-red-900/20" onClick={cycleCurrency}>
                <div className="flex justify-between items-center">
                  <span className="text-[9px] text-red-400 uppercase">Costs ({totals.taskCount})</span>
                  <span className="text-sm font-mono text-red-400 font-bold">-{formatAmount(totals.totalCostUAH, "UAH")}</span>
                </div>
                <div className="flex gap-2 text-[9px] text-zinc-500">
                  {totals.artistCostUAH > 0 && <span>Artists: -{formatAmount(totals.artistCostUAH, "UAH")}</span>}
                  {totals.artDirectorCostUAH > 0 && <span>AD: -{formatAmount(totals.artDirectorCostUAH, "UAH")}</span>}
                </div>
              </div>
            )}
            
            {/* Net Profit & Margin - compact */}
            <div 
              className={cn(
                "p-2 rounded border cursor-pointer flex justify-between items-center",
                totals.netProfitUAH >= 0 ? "bg-emerald-900/20 border-emerald-800/30 hover:bg-emerald-900/30" : "bg-red-900/20 border-red-800/30 hover:bg-red-900/30"
              )}
              onClick={cycleCurrency}
            >
              <div>
                <p className="text-[9px] text-zinc-400 uppercase">Profit</p>
                <p className={cn("text-base font-bold", totals.netProfitUAH >= 0 ? "text-emerald-400" : "text-red-400")}>
                  {totals.netProfitUAH >= 0 ? "+" : ""}{formatAmount(Math.abs(totals.netProfitUAH), "UAH")}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[9px] text-zinc-400 uppercase">Margin</p>
                <p className={cn(
                  "text-base font-bold",
                  totals.marginPercent >= 50 ? "text-emerald-400" : totals.marginPercent >= 30 ? "text-yellow-400" : "text-red-400"
                )}>
                  {totals.marginPercent.toFixed(1)}%
                </p>
              </div>
            </div>
            
            {/* Status + Start Button - compact */}
            <div className="flex items-center gap-2">
              <Badge className={cn("text-[10px] border flex-shrink-0", getProjectStatus(formData.status)?.color)}>
                {getProjectStatus(formData.status)?.name}
              </Badge>
              {project?.status !== "active" && (
                <Button
                  onClick={handleStartProject}
                  disabled={startingProject}
                  size="sm"
                  className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700"
                >
                  {startingProject ? "..." : <><Play className="h-3 w-3 mr-1" />Start</>}
                </Button>
              )}
              {project?.status === "active" && (
                <div className="flex items-center gap-1 text-[10px] text-green-400">
                  <ShieldCheck className="h-3 w-3" />Active
                </div>
              )}
            </div>
            
            {/* Client Requests badge */}
            {clientRequests.filter(r => r.status === "pending").length > 0 && (
              <div className="flex items-center justify-between p-1.5 bg-orange-900/20 border border-orange-800/30 rounded">
                <span className="text-[9px] text-orange-500 uppercase">Requests</span>
                <Badge variant="outline" className="text-[10px] bg-orange-500/20 text-orange-400 border-orange-500/30">
                  {clientRequests.filter(r => r.status === "pending").length} pending
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>
        
      {/* PARTNER REQUESTS SECTION - With Tabs: Incoming/Outgoing (only for TRP projects) */}
      {canManageTasks && project?.type === "trp" && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
                <Send className="h-4 w-4 text-amber-400" />
                Partner Requests
              </CardTitle>
              <div className="flex items-center gap-2">
                {/* List / Gantt view toggle */}
                <div className="inline-flex rounded border border-zinc-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setRequestsView("list")}
                    className={cn(
                      "px-2 py-1 text-[11px] flex items-center gap-1 transition-colors",
                      requestsView === "list" ? "bg-zinc-700 text-zinc-50" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                    )}
                  >
                    <ListTodo className="h-3 w-3" /> List
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequestsView("gantt")}
                    className={cn(
                      "px-2 py-1 text-[11px] flex items-center gap-1 border-l border-zinc-700 transition-colors",
                      requestsView === "gantt" ? "bg-zinc-700 text-zinc-50" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                    )}
                  >
                    <Calendar className="h-3 w-3" /> Gantt
                  </button>
                </div>
                <Button
                  size="sm"
                  onClick={() => setShowBulkRequestModal(true)}
                  className="bg-amber-600 hover:bg-amber-700 h-7 text-xs"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />New Request
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <Tabs value={requestsTab} onValueChange={setRequestsTab} className="w-full">
              <TabsList className="bg-zinc-800/50 mb-3">
                <TabsTrigger value="incoming" className="data-[state=active]:bg-zinc-700 text-xs">
                  Incoming
                  {priceRequests.filter(r => r.status === "pending").length > 0 && (
                    <Badge className="ml-1.5 bg-amber-500/20 text-amber-400 text-[10px] px-1.5">
                      {priceRequests.filter(r => r.status === "pending").length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="outgoing" className="data-[state=active]:bg-zinc-700 text-xs">
                  Outgoing
                  {outgoingRequests.filter(r => r.status === "pending").length > 0 && (
                    <Badge className="ml-1.5 bg-blue-500/20 text-blue-400 text-[10px] px-1.5">
                      {outgoingRequests.filter(r => r.status === "pending").length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="incoming" className="space-y-2 mt-0">
                {requestsView === "gantt" ? (
                  <RequestsGantt
                    items={priceRequests.map((r) => ({
                      id: r.id,
                      label: r.task_name,
                      sublabel: `${(r.task_type || "").replace("_", " ")} × ${r.quantity || 1}`,
                      start_date: r.start_date,
                      end_date: r.end_date,
                      status: r.status,
                    }))}
                    emptyHint="No incoming requests"
                  />
                ) : priceRequests.length === 0 ? (
                  <p className="text-xs text-zinc-500 text-center py-4">No incoming requests</p>
                ) : [...priceRequests].sort((a, b) => {
                  const order = { pending: 0, approved: 1, rejected: 2 };
                  return (order[a.status] ?? 3) - (order[b.status] ?? 3);
                }).map((req) => {
                  const qty = req.quantity || 1;
                  const basePrice = req.base_price || 0;
                  const proposedPrice = req.proposed_price || 0;
                  const discount = basePrice > 0 && basePrice > proposedPrice ? Math.round((1 - proposedPrice / basePrice) * 100) : 0;
                  const total = proposedPrice * qty;
                  const vexosDetails = req.vexos_details || req.vexos_data || null;
                  const isVexosReq = !!req.is_vexos_task || (typeof req.task_type === "string" && req.task_type.toLowerCase() === "vexos");
                  const vexosChips = (() => {
                    if (!isVexosReq || !vexosDetails) return [];
                    const v = vexosDetails;
                    const chips = [];
                    if (Number(v.base_fee_qty) > 0) chips.push({ label: "Base fee", value: "×1" });
                    if (Number(v.orbit_fee_qty) > 0) chips.push({ label: "Orbit", value: `×${v.orbit_fee_qty}` });
                    if (Number(v.unit_apartments_qty) > 0) chips.push({ label: "Apartments", value: `×${v.unit_apartments_qty}` });
                    if (Number(v.unit_villas_qty) > 0) chips.push({ label: "Villas", value: `×${v.unit_villas_qty}` });
                    if (Number(v.fee_360_qty) > 0) chips.push({ label: "360° Views", value: `×${v.fee_360_qty}` });
                    if (v.orbit_animation) chips.push({ label: "Orbit Animation", value: Number(v.orbit_fee_qty) > 1 ? `×${v.orbit_fee_qty}` : "" });
                    if (v.custom_design) chips.push({ label: "Custom Design", value: "" });
                    if (v.rush_deadline) chips.push({ label: "Rush Deadline", value: "" });
                    if (Number(v.client_invoice) > 0) chips.push({ label: "Client invoice", value: `$${Number(v.client_invoice).toLocaleString()} (5% commission)` });
                    if (Number(v.components_total) > 0) chips.push({ label: "Components total", value: `$${Number(v.components_total).toLocaleString()}` });
                    if (Number(v.commission) > 0) chips.push({ label: "Commission", value: `$${Number(v.commission).toLocaleString()}` });
                    return chips;
                  })();
                  return (
                    <div key={req.id} className={cn("p-2.5 rounded border",
                      req.status === "pending" && "bg-amber-500/10 border-amber-500/30",
                      req.status === "approved" && "bg-emerald-500/10 border-emerald-500/20",
                      req.status === "rejected" && "bg-red-500/10 border-red-500/20 opacity-50"
                    )}>
                      <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0 flex items-center gap-2 overflow-hidden">
                        <span className="text-sm text-zinc-50 truncate">{req.task_name}</span>
                        <span className="text-xs text-zinc-400 shrink-0 whitespace-nowrap">
                          {req.task_type?.replace("_", " ")} × {qty}
                          {req.extra_360_points > 0 && (
                            <span className="ml-2 text-purple-300 font-mono" title="Additional 360 tour points">
                              +{req.extra_360_points}pt × ${req.extra_360_unit_price || 20} = ${req.extra_360_total ?? (req.extra_360_points * 20)}
                            </span>
                          )}
                        </span>
                      </div>
                      {req.status !== "rejected" && (
                        <div className="flex items-center gap-1 mr-2">
                          <InlineDatePicker
                            value={req.start_date}
                            placeholder="Start"
                            tone="blue"
                            label="Planned start"
                            onSave={(v) => handleUpdatePriceRequestDates(req.id, { start_date: v })}
                          />
                          <InlineDatePicker
                            value={req.end_date}
                            placeholder="End"
                            tone="purple"
                            label="Planned end"
                            onSave={(v) => handleUpdatePriceRequestDates(req.id, { end_date: v })}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-2 mr-2">
                        {basePrice > 0 && basePrice > proposedPrice && (
                          <span className="text-xs text-zinc-500 line-through">${basePrice.toFixed(0)}</span>
                        )}
                        <span className="text-sm font-mono font-medium text-zinc-50">${proposedPrice.toFixed(0)}</span>
                        {discount > 0 && (
                          <Badge variant="outline" className="text-[10px] bg-amber-500/20 text-amber-400 border-amber-500/30 px-1">-{discount}%</Badge>
                        )}
                        {qty > 1 && <span className="text-xs text-emerald-400 font-mono">= ${total.toFixed(0)}</span>}
                      </div>
                      {req.status === "pending" && (
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-400" onClick={() => handleApprovePriceRequest(req.id)}><CheckCircle2 className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => handleRejectPriceRequest(req.id)}><XCircle className="h-4 w-4" /></Button>
                        </div>
                      )}
                      {req.status === "approved" && (
                        <div className="flex items-center gap-1.5">
                          <Badge className="bg-emerald-500/20 text-emerald-400">approved</Badge>
                          {!req.task_created ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-[10px] border-blue-500/30 text-blue-400 hover:bg-blue-500/10 px-2"
                              onClick={() => openCreateTaskDialog(req)}
                              data-testid={`create-task-from-req-${req.id}`}
                            >
                              <Plus className="h-3 w-3 mr-0.5" />Task
                            </Button>
                          ) : (
                            <Badge variant="outline" className="text-[9px] bg-blue-500/20 text-blue-400 border-blue-500/30">Task</Badge>
                          )}
                          {!req.payment_created ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-[10px] border-purple-500/30 text-purple-400 hover:bg-purple-500/10 px-2"
                              onClick={() => handleCreatePaymentFromRequest(req)}
                              disabled={creatingPayment}
                              data-testid={`create-payment-from-req-${req.id}`}
                            >
                              <Plus className="h-3 w-3 mr-0.5" />Payment
                            </Button>
                          ) : (
                            <Badge variant="outline" className="text-[9px] bg-purple-500/20 text-purple-400 border-purple-500/30">Payment</Badge>
                          )}
                        </div>
                      )}
                      {req.status === "rejected" && <Badge className="bg-red-500/20 text-red-400">rejected</Badge>}
                      </div>
                      {isVexosReq && (vexosChips.length > 0 || req.notes) && (
                        <div className="mt-2 pt-2 border-t border-white/10">
                          {vexosChips.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {vexosChips.map((c, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-orange-500/15 border border-orange-500/30 text-orange-200"
                                >
                                  <span className="text-orange-400">{c.label}</span>
                                  {c.value && <span className="font-mono text-zinc-50">{c.value}</span>}
                                </span>
                              ))}
                            </div>
                          )}
                          {vexosChips.length === 0 && (() => {
                            const notes = (req.notes || "").trim();
                            // Old BulkPriceRequestModal auto-generated this exact summary in notes
                            // when no user notes were provided. It carries no breakdown data,
                            // so suppress it and show an explicit "legacy" hint instead.
                            const isAutoSummary = /^VEXOS Components:\s*\$[\d.,-]+,\s*Commission:\s*\$[\d.,-]+\s*$/i.test(notes);
                            if (notes && !isAutoSummary) {
                              return <p className="text-[11px] text-zinc-400 italic whitespace-pre-wrap">{notes}</p>;
                            }
                            return (
                              <p className="text-[11px] text-zinc-500 italic">
                                Breakdown not stored — request created before 2026-05-08 fix. Total saved: ${(req.proposed_price || 0).toFixed(0)}.
                              </p>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Total of incoming requests */}
                {requestsView === "list" && priceRequests.filter(r => r.status !== "rejected").length > 0 && (
                  <div className="flex justify-between items-center px-2 pt-1 border-t border-zinc-800">
                    <span className="text-xs text-zinc-500">Total (excl. rejected)</span>
                    <span className="text-sm font-mono text-zinc-50 font-medium">
                      ${priceRequests.filter(r => r.status !== "rejected").reduce((sum, r) => sum + ((r.proposed_price || 0) * (r.quantity || 1)), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </span>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="outgoing" className="space-y-2 mt-0">
                {requestsView === "gantt" ? (
                  <RequestsGantt
                    items={outgoingRequests.map((r) => ({
                      id: r.id,
                      label: r.title,
                      sublabel: `${(r.request_type || "").replace(/_/g, " ")}${r.quantity ? ` × ${r.quantity}` : ""}`,
                      start_date: r.start_date,
                      end_date: r.end_date,
                      status: r.status,
                    }))}
                    emptyHint="No outgoing requests"
                  />
                ) : outgoingRequests.length === 0 ? (
                  <p className="text-xs text-zinc-500 text-center py-4">No outgoing requests</p>
                ) : (
                  <>
                    {[...outgoingRequests].sort((a, b) => {
                      const order = { pending: 0, approved: 1, rejected: 2 };
                      return (order[a.status] ?? 3) - (order[b.status] ?? 3);
                    }).map((req) => (
                      <div key={req.id} className={cn("p-2.5 rounded border",
                        req.status === "pending" && "bg-blue-500/10 border-blue-500/30",
                        req.status === "approved" && "bg-emerald-500/10 border-emerald-500/20",
                        req.status === "rejected" && "bg-red-500/10 border-red-500/20 opacity-50"
                      )}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm text-zinc-50 truncate">{req.title}</span>
                              {req.request_type && <span className="text-xs text-zinc-400 shrink-0">{req.request_type.replace(/_/g, " ")}</span>}
                              {req.price_per_unit > 0 ? (
                                <span className="text-xs text-zinc-400 font-mono">
                                  ${req.price_per_unit} x {req.quantity || 1}
                                  {req.discount > 0 && <span className="text-orange-400"> -{req.discount}%</span>}
                                </span>
                              ) : req.quantity > 0 ? (
                                <span className="text-xs text-zinc-400">qty: {req.quantity}</span>
                              ) : null}
                              {(req.amount > 0 || (req.price_per_unit > 0 && req.quantity > 0)) ? (
                                <span className="text-xs text-emerald-400 font-mono font-medium">
                                  = ${(req.amount || (req.price_per_unit * (req.quantity || 1) * (1 - (req.discount || 0) / 100))).toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-xs text-zinc-600 italic">no price</span>
                              )}
                              {req.extra_360_points > 0 && (
                                <span className="text-xs text-purple-300 font-mono" title="Additional 360 tour points">
                                  +{req.extra_360_points}pt × ${req.extra_360_unit_price || 20} = ${req.extra_360_total ?? (req.extra_360_points * 20)}
                                </span>
                              )}
                              {req.deadline && <span className="text-xs text-zinc-500">Due: {new Date(req.deadline).toLocaleDateString()}</span>}
                              {req.priority && req.priority !== "normal" && (
                                <Badge variant="outline" className="text-[9px] h-4 px-1 bg-orange-500/10 text-orange-400 border-orange-500/30">{req.priority}</Badge>
                              )}
                            </div>
                            {req.status !== "rejected" && (
                              <div className="flex items-center gap-1 mt-1.5">
                                <InlineDatePicker
                                  value={req.start_date}
                                  placeholder="Start"
                                  tone="blue"
                                  label="Planned start"
                                  onSave={(v) => handleUpdateStudioRequestDates(req.id, { start_date: v })}
                                />
                                <InlineDatePicker
                                  value={req.end_date}
                                  placeholder="End"
                                  tone="purple"
                                  label="Planned end"
                                  onSave={(v) => handleUpdateStudioRequestDates(req.id, { end_date: v })}
                                />
                              </div>
                            )}
                            {req.description && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{req.description}</p>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Badge className={cn("text-[10px]",
                              req.status === "pending" && "bg-blue-500/20 text-blue-400",
                              req.status === "approved" && "bg-emerald-500/20 text-emerald-400",
                              req.status === "rejected" && "bg-red-500/20 text-red-400"
                            )}>{req.status}</Badge>
                            {req.status === "pending" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-[10px] border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                onClick={() => handleApproveStudioRequest(req.id)}
                                data-testid={`approve-studio-req-${req.id}`}
                              >
                                <Check className="h-3 w-3 mr-0.5" />
                                Approve
                              </Button>
                            )}
                            {req.status === "approved" && !req.task_created && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-[10px] border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                                onClick={() => handleCreateTasksFromStudioRequest(req)}
                                data-testid={`create-task-from-studio-${req.id}`}
                              >
                                <Plus className="h-3 w-3 mr-0.5" />
                                Task
                              </Button>
                            )}
                            {req.status === "approved" && !req.payment_created && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-[10px] border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                                onClick={() => handleCreatePaymentFromStudioRequest(req)}
                                disabled={creatingPayment}
                                data-testid={`create-payment-from-studio-${req.id}`}
                              >
                                <DollarSign className="h-3 w-3 mr-0.5" />
                                Payment
                              </Button>
                            )}
                            {req.task_created && (
                              <Badge variant="outline" className="text-[9px] bg-blue-500/20 text-blue-400 border-blue-500/30">Task</Badge>
                            )}
                            {req.payment_created && (
                              <Badge variant="outline" className="text-[9px] bg-purple-500/20 text-purple-400 border-purple-500/30">Payment</Badge>
                            )}
                            {req.status === "pending" && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6 text-zinc-500 hover:text-red-400"
                                onClick={() => handleDeleteStudioRequest(req.id)}
                                data-testid={`delete-outgoing-req-${req.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Total of outgoing requests */}
                    {outgoingRequests.filter(r => r.status !== "rejected" && r.amount > 0).length > 0 && (
                      <div className="flex justify-between items-center px-2 pt-1 border-t border-zinc-800">
                        <span className="text-xs text-zinc-500">Total (excl. rejected)</span>
                        <span className="text-sm font-mono text-zinc-50 font-medium">
                          ${outgoingRequests.filter(r => r.status !== "rejected").reduce((sum, r) => sum + (r.amount || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* The setup chat moved to the production TASK (see TaskDetailPage) and the
          gate now blocks the task start, not the sales→pool handoff. */}

      {/* OLD PRICE REQUESTS SECTION - For non-TRP projects (only for PM/Admin) */}
      {canManageTasks && project?.type !== "trp" && priceRequests.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <AlertCircle className="h-4 w-4 text-amber-400" />
              Price Requests from Partner ({priceRequests.filter(r => r.status === "pending").length} pending)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            {/* Pending Requests */}
            {priceRequests.filter(r => r.status === "pending").length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-amber-400 font-medium">Awaiting Approval</p>
                {priceRequests.filter(r => r.status === "pending").map((req) => {
                  const qty = req.quantity || 1;
                  const basePrice = req.base_price || 0;
                  const proposedPrice = req.proposed_price || 0;
                  const discount = basePrice > 0 ? Math.round((1 - proposedPrice / basePrice) * 100) : 0;
                  const total = proposedPrice * qty;
                  // Format prices with max 2 decimal places
                  const formatPrice = (v) => parseFloat(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
                  return (
                    <div key={req.id} className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-zinc-50">{req.task_name}</p>
                          <p className="text-xs text-zinc-400 mt-0.5">
                            {req.task_type?.replace("_", " ")} • Qty: {qty}
                          </p>
                          {req.notes && (
                            <p className="text-xs text-zinc-500 mt-1">{req.notes}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            {basePrice > 0 && basePrice > proposedPrice && (
                              <span className="text-xs text-zinc-500 line-through">${formatPrice(basePrice)}</span>
                            )}
                            <span className="text-sm font-medium text-zinc-50">${formatPrice(proposedPrice)}</span>
                            {discount > 0 && (
                              <Badge variant="outline" className="text-xs bg-red-500/20 text-red-400 border-red-500/30">
                                -{discount}%
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-amber-400 mt-0.5">Total: ${formatPrice(total)}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                            onClick={() => handleApprovePriceRequest(req.id)}
                            data-testid={`approve-price-req-${req.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            onClick={() => handleRejectPriceRequest(req.id)}
                            data-testid={`reject-price-req-${req.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Approved Requests */}
            {priceRequests.filter(r => r.status === "approved").length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-emerald-400 font-medium">Approved ({priceRequests.filter(r => r.status === "approved").length})</p>
                <div className="space-y-1">
                  {priceRequests.filter(r => r.status === "approved").map((req) => {
                    const qty = req.quantity || 1;
                    const basePrice = req.base_price || 0;
                    const proposedPrice = req.proposed_price || 0;
                    const discount = basePrice > 0 ? Math.round((1 - proposedPrice / basePrice) * 100) : 0;
                    const total = proposedPrice * qty;
                    // Format prices with max 2 decimal places
                    const formatPrice = (v) => parseFloat(v).toFixed(2);
                    const hasTask = req.task_created;
                    const hasPayment = req.payment_created;
                    return (
                      <div key={req.id} className="flex items-center justify-between p-3 rounded bg-emerald-500/10 border border-emerald-500/20">
                        <div className="flex-1">
                          <p className="text-sm text-zinc-50 font-medium">{req.task_name}</p>
                          <p className="text-xs text-zinc-400 mt-0.5">
                            {req.task_type?.replace("_", " ")} • Qty: {qty}
                            {req.discount_reason && (
                              <span className="text-blue-400 ml-2" title={req.discount_reason}>💬 {req.discount_reason.substring(0, 30)}...</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right mr-3">
                          <div className="flex items-center gap-2 justify-end">
                            {basePrice > 0 && basePrice > proposedPrice && (
                              <span className="text-xs text-zinc-500 line-through">${formatPrice(basePrice)}</span>
                            )}
                            <span className="text-sm font-medium text-zinc-50">${formatPrice(proposedPrice)}</span>
                            {discount > 0 && (
                              <Badge variant="outline" className="text-[10px] bg-amber-500/20 text-amber-400 border-amber-500/30">
                                -{discount}%
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-emerald-400 mt-0.5">Total: ${formatPrice(total)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Show badges for created task/payment */}
                          {hasTask && (
                            <Badge variant="outline" className="text-[10px] bg-blue-500/20 text-blue-400 border-blue-500/30">
                              Task
                            </Badge>
                          )}
                          {hasPayment && (
                            <Badge variant="outline" className="text-[10px] bg-purple-500/20 text-purple-400 border-purple-500/30">
                              Payment
                            </Badge>
                          )}
                          
                          {/* Create buttons if not yet created */}
                          {!hasTask && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                              onClick={() => openCreateTaskDialog(req)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Task
                            </Button>
                          )}
                          {!hasPayment && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                              onClick={() => handleCreatePaymentFromRequest(req)}
                              disabled={creatingPayment}
                            >
                              <DollarSign className="h-3 w-3 mr-1" />
                              Payment
                            </Button>
                          )}
                          
                          {/* Delete button */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            onClick={() => handleDeletePriceRequest(req.id)}
                            title="Delete request"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                          
                          <Badge variant="outline" className="text-xs bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                            Approved
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Rejected Requests */}
            {priceRequests.filter(r => r.status === "rejected").length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-red-400 font-medium">Rejected ({priceRequests.filter(r => r.status === "rejected").length})</p>
                <div className="space-y-1">
                  {priceRequests.filter(r => r.status === "rejected").map((req) => {
                    const qty = req.quantity || 1;
                    const basePrice = req.base_price || 0;
                    const proposedPrice = req.proposed_price || 0;
                    const discount = basePrice > 0 ? Math.round((1 - proposedPrice / basePrice) * 100) : 0;
                    const total = proposedPrice * qty;
                    // Format prices with max 2 decimal places
                    const formatPrice = (v) => parseFloat(v).toFixed(2);
                    return (
                      <div key={req.id} className="flex items-center justify-between p-3 rounded bg-red-500/10 border border-red-500/20">
                        <div className="flex-1">
                          <p className="text-sm text-zinc-50 line-through opacity-60">{req.task_name}</p>
                          <p className="text-xs text-zinc-400 mt-0.5">
                            {req.task_type?.replace("_", " ")} • Qty: {qty}
                          </p>
                          <p className="text-xs text-red-400 mt-1">
                            Reason: {req.rejection_reason || "Not specified"}
                          </p>
                        </div>
                        <div className="text-right mr-3 opacity-60">
                          <div className="flex items-center gap-2 justify-end">
                            {basePrice > 0 && basePrice > proposedPrice && (
                              <span className="text-xs text-zinc-500 line-through">${formatPrice(basePrice)}</span>
                            )}
                            <span className="text-sm font-medium text-zinc-400">${formatPrice(proposedPrice)}</span>
                            {discount > 0 && (
                              <Badge variant="outline" className="text-[10px] bg-amber-500/20 text-amber-400 border-amber-500/30">
                                -{discount}%
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-zinc-500 mt-0.5">Total: ${formatPrice(total)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            onClick={() => handleDeletePriceRequest(req.id)}
                            title="Delete rejected request"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Badge variant="outline" className="text-xs bg-red-500/20 text-red-400 border-red-500/30">
                            Rejected
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* WORKSECTION LINKING SECTION — internal roles only (no clients/partners).
          Lets PM connect this portal project to a Worksection project and then
          link individual portal tasks to one-or-many WS tasks (M:N supported).
          Phase 2 — UI only. Phase 3 will write resolved Worksection hours/cost
          to task.staff_cost_total under a global toggle (default off).
          See backend/worksection.py + frontend/src/components/WorksectionProjectSection.js. */}
      {!isClient && canManageTasks && actualProjectId && (
        <WorksectionProjectSection
          projectId={actualProjectId}
          portalTasks={tasks}
          canEdit={canManageTasks}
        />
      )}

      {/* TASKS SECTION - Visible for clients, art directors, when project is signed, or for PM/Admin */}
      {(isClient || isInProduction || canManageTasks || isArtDirector) && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <ListTodo className="h-4 w-4 text-cyan-400" />
              Tasks ({tasks.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* View toggle: Table | Gantt — only when there's something to show */}
              {tasks.length > 0 && (
                <div className="inline-flex rounded-md border border-zinc-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setTasksView("table")}
                    className={cn(
                      "px-2 py-1 text-[10px] uppercase tracking-wide transition-colors",
                      tasksView === "table" ? "bg-zinc-700 text-zinc-50" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                    )}
                  >
                    Table
                  </button>
                  <button
                    type="button"
                    onClick={() => setTasksView("gantt")}
                    className={cn(
                      "px-2 py-1 text-[10px] uppercase tracking-wide transition-colors flex items-center gap-1",
                      tasksView === "gantt" ? "bg-zinc-700 text-zinc-50" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                    )}
                  >
                    <Calendar className="h-3 w-3" /> Gantt
                  </button>
                </div>
              )}
              {canManageTasks && (
                <Button
                  size="sm"
                  onClick={() => setShowNewTask(true)}
                  className="bg-cyan-600 hover:bg-cyan-700 h-7 text-xs"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />New Task
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {tasks.length > 0 && tasksView === "gantt" ? (
              <TaskGantt
                snapshot={timelineSnapshot}
                weekStatuses={timelineWeekStatuses}
                onSetWeekStatus={handleGanttSetWeekStatus}
                onSavePlanned={handleGanttSavePlanned}
                onUpdateTaskDates={!isClient && canManageTasks ? handleGanttUpdateTaskDates : undefined}
                canEditWeekStatus={!isClient && canManageTasks}
                canEditPlanned={!isClient && canManageTasks}
              />
            ) : tasks.length === 0 ? (
              <div className="text-center py-4 text-zinc-500">
                <ListTodo className="h-6 w-6 mx-auto mb-1 opacity-50" />
                <p className="text-sm">No tasks yet</p>
                {!isInProduction && !isClient && (
                  <p className="text-xs mt-1 text-orange-400">Tasks can be created after contract is signed</p>
                )}
              </div>
            ) : (
              <>
              {/* Layer 2: quick filters — hide/show rows without touching the money columns */}
              <div className="flex items-center gap-1.5 flex-wrap mb-3">
                {[
                  { k: "all", label: "All", n: tasks.length },
                  { k: "active", label: "Active", n: tasks.filter((t) => !taskIsDone(t)).length },
                  { k: "done", label: "Done", n: tasks.filter(taskIsDone).length },
                  { k: "unpaid", label: "Unpaid", n: tasks.filter(taskIsUnpaid).length },
                ].map((f) => (
                  <button
                    key={f.k}
                    type="button"
                    onClick={() => setTaskFilter(f.k)}
                    className={cn(
                      "h-7 px-2.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5",
                      taskFilter === f.k
                        ? "bg-zinc-100 text-zinc-900 border-zinc-100"
                        : "bg-zinc-900 text-zinc-400 border-zinc-700 hover:text-zinc-200"
                    )}
                  >
                    {f.label}
                    <span className={cn("text-[10px]", taskFilter === f.k ? "text-zinc-500" : "text-zinc-600")}>{f.n}</span>
                  </button>
                ))}
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400 text-xs py-2">Task</TableHead>
                    <TableHead className="text-zinc-400 text-xs py-2">Type</TableHead>
                    {!isClient && <TableHead className="text-zinc-400 text-xs py-2">Artist</TableHead>}
                    <TableHead className="text-zinc-400 text-xs py-2">Status</TableHead>
                    <TableHead className="text-zinc-400 text-xs py-2 text-center">Versions</TableHead>
                    <TableHead
                      className="text-zinc-400 text-xs py-2 cursor-pointer select-none hover:text-zinc-200"
                      onClick={() => toggleTaskSort("progress")}
                    >
                      Progress{taskSort.key === "progress" && <span className="ml-1 text-[9px]">{taskSort.dir === "desc" ? "▼" : "▲"}</span>}
                    </TableHead>
                    {!hideFinancials && <TableHead className="text-zinc-400 text-xs py-2">Payment</TableHead>}
                    {/* Layer 2b: Client/Costs/Profit/Margin collapsed into one Economics column (full breakdown in the cell tooltip + task detail). Layer 4: click to sort by margin. */}
                    {!hideFinancials && (
                      <TableHead
                        className="text-zinc-400 text-xs py-2 text-right cursor-pointer select-none hover:text-zinc-200"
                        onClick={() => toggleTaskSort("economics")}
                      >
                        Economics{taskSort.key === "economics" && <span className="ml-1 text-[9px]">{taskSort.dir === "desc" ? "▼" : "▲"}</span>}
                      </TableHead>
                    )}
                    {isClient && <TableHead className="text-zinc-400 text-xs py-2 text-right">Your Price</TableHead>}
                    <TableHead className="text-zinc-400 py-2"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applyTaskSort(tasks.filter(taskMatchesFilter)).map((task) => {
                    // Calculate margin for each task - include ALL costs
                    const taskClientPrice = task.client_price || 0;
                    const taskArtistPrice = task.agreed_price || 0;
                    const taskAdFee = task.art_director_fee || 0;
                    const taskCurrency = task.currency || "USD";
                    const hasAssignedArtist = !!task.assigned_artist_id;
                    
                    // Staff cost: prefer manual task.staff_cost_total (in UAH) when set.
                    // Otherwise fall back to Phase 3-preview Worksection rollup (in USD → convert to UAH).
                    // This makes the Costs column honest even before Phase 3-full auto-write toggle flips.
                    const manualStaffCostUAH = task.staff_cost_total || 0;
                    const wsRollup = wsRollupByTask[task.id];
                    const wsCostUSD = wsRollup?.total_cost_usd || 0;
                    const wsCostUAH = (wsCostUSD > 0 && rates?.USD) ? wsCostUSD * rates.USD : 0;
                    const staffCostUAH = manualStaffCostUAH > 0 ? manualStaffCostUAH : wsCostUAH;
                    const staffCostFromWS = manualStaffCostUAH === 0 && wsCostUSD > 0;

                    let staffCostInTaskCurrency = staffCostUAH;
                    if (taskCurrency === "USD" && rates?.USD) {
                      staffCostInTaskCurrency = staffCostUAH / rates.USD;
                    } else if (taskCurrency === "EUR" && rates?.EUR) {
                      staffCostInTaskCurrency = staffCostUAH / rates.EUR;
                    }
                    
                    const taskTotalCost = taskArtistPrice + taskAdFee + staffCostInTaskCurrency;
                    const hasCosts = taskTotalCost > 0;
                    const taskProfit = taskClientPrice - taskTotalCost;
                    const taskMarginPercent = taskClientPrice > 0 && hasCosts ? (taskProfit / taskClientPrice) * 100 : 0;
                    const taskMarginColor = taskMarginPercent >= 50 ? "text-green-400" : 
                      taskMarginPercent >= 30 ? "text-yellow-400" : 
                      taskMarginPercent > 0 ? "text-red-400" : "text-zinc-500";
                    const isZeroMargin = hasCosts && taskMarginPercent <= 0;
                    
                    return (
                    <React.Fragment key={task.id}>
                    <TableRow className="border-zinc-800 hover:bg-zinc-800/50">
                      <TableCell className="py-2 max-w-[320px]">
                        <div className="flex items-center gap-2 whitespace-nowrap min-w-0">
                          {/* Expand/collapse button for change requests */}
                          {!isClient && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-zinc-500 hover:text-orange-400 shrink-0"
                              onClick={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
                              title="Change Requests"
                            >
                              {expandedTaskId === task.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                          <span
                            className="font-medium text-zinc-50 text-sm truncate min-w-0"
                            title={task.name}
                          >
                            {task.name}
                          </span>
                          {task.deadline && (
                            <span className="text-[10px] text-zinc-500 flex items-center gap-0.5 shrink-0">
                              <Calendar className="h-2.5 w-2.5" />
                              {new Date(task.deadline).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <Badge
                          variant="outline"
                          className="text-[10px] h-5 bg-zinc-800 text-zinc-300 whitespace-nowrap max-w-[160px] inline-block truncate align-middle"
                          title={task.task_type || "General"}
                        >
                          {task.task_type || "General"}
                        </Badge>
                      </TableCell>
                      {!isClient && (
                        <TableCell className="py-2">
                          {isArtDirector ? (
                            task.assigned_artist_id && task.assigned_artist_name ? (
                              <div className="flex items-center gap-1">
                                <Avatar className="h-5 w-5">
                                  <AvatarFallback className="bg-purple-500/20 text-purple-400 text-[10px]">
                                    {task.assigned_artist_name?.[0] || <Palette className="h-2.5 w-2.5" />}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-xs text-zinc-300">{task.assigned_artist_name}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-zinc-600">Unassigned</span>
                            )
                          ) : editingTaskId === task.id ? (
                            <div className="flex items-center gap-1">
                              <Select
                                value={editingArtistId || "unassigned"}
                                onValueChange={(v) => setEditingArtistId(v)}
                              >
                                <SelectTrigger className="h-7 w-32 bg-zinc-800 border-zinc-700 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                  <SelectItem value="unassigned">Unassigned</SelectItem>
                                  {artists.map(a => (
                                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-green-400 hover:text-green-300"
                                onClick={() => handleUpdateTaskArtist(task.id, editingArtistId)}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-zinc-400 hover:text-zinc-50"
                                onClick={() => { setEditingTaskId(null); setEditingArtistId(""); }}
                              >
                                ×
                              </Button>
                            </div>
                          ) : (
                            <div 
                              className="flex items-center gap-1 cursor-pointer group"
                              onClick={() => {
                                // If no artist assigned OR no agreed_price, open full assign dialog
                                // Otherwise quick-edit artist for assigned tasks
                                if (!task.assigned_artist_id || !task.agreed_price || task.agreed_price <= 0) {
                                  openAssignDialog(task);
                                } else {
                                  setEditingTaskId(task.id);
                                  setEditingArtistId(task.assigned_artist_id || "unassigned");
                                }
                              }}
                              title={(!task.agreed_price || task.agreed_price <= 0) ? "Click to set price and assign" : task.assigned_artist_id ? "Click to change artist" : "Click to assign artist"}
                            >
                              {/* First check if price needs to be set */}
                              {(!task.agreed_price || task.agreed_price <= 0) ? (
                                <span className="text-orange-400 text-xs group-hover:text-orange-300 flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  Set Price
                                </span>
                              ) : task.assigned_artist_id ? (
                                <>
                                  <Avatar className="h-5 w-5">
                                    <AvatarFallback className="bg-purple-500/20 text-purple-400 text-[10px]">
                                      {task.assigned_artist_name?.[0] || <Palette className="h-2.5 w-2.5" />}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-xs text-zinc-300 group-hover:text-zinc-50">{task.assigned_artist_name || "Assigned"}</span>
                                </>
                              ) : (
                                <span className="text-blue-400 text-sm group-hover:text-blue-300 flex items-center gap-1">
                                  <Plus className="h-3 w-3" />
                                  Assign
                                </span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <TaskStageBadge task={task} size="sm" />
                      </TableCell>
                      {/* Versions column */}
                      <TableCell className="py-2 text-center">
                        {(() => {
                          const versions = task.versions || [];
                          const totalVersions = versions.length;
                          const revisions = versions.filter(v =>
                            v.internal_status === 'rejected' ||
                            (v.client_approved === false && v.internal_status === 'approved')
                          ).length;
                          const currentVersion = totalVersions > 0 ? versions[totalVersions - 1] : null;
                          const currentStatus = currentVersion?.internal_status;

                          if (totalVersions === 0) {
                            return <span className="text-xs text-zinc-600">—</span>;
                          }

                          return (
                            <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                              <span className="text-xs text-zinc-50 font-medium">v{totalVersions}</span>
                              {revisions > 0 && (
                                <span className="text-[10px] text-orange-400">{revisions} rev</span>
                              )}
                              {currentStatus && (
                                <Badge className={cn(
                                  "text-[9px] px-1 py-0",
                                  currentStatus === 'approved' ? "bg-green-900/50 text-green-400" :
                                  currentStatus === 'rejected' ? "bg-red-900/50 text-red-400" :
                                  "bg-yellow-900/50 text-yellow-400"
                                )}>
                                  {currentStatus === 'pending' ? 'review' : currentStatus}
                                </Badge>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      {/* Progress column — compact bar + step count (replaces the busy 6-dot TaskTimeline for a calmer, scannable table; full labelled timeline lives in the task detail). */}
                      <TableCell className="py-2 min-w-[120px]">
                        {taskTimelines[task.id]?.stages?.length ? (() => {
                          const tl = taskTimelines[task.id];
                          const total = tl.stages.length;
                          const idx = tl.current_stage_index || 0;
                          const done = idx >= total - 1;
                          const pct = total > 1 ? Math.round((Math.min(idx, total - 1) / (total - 1)) * 100) : (idx > 0 ? 100 : 0);
                          const curName = tl.stages[Math.min(idx, total - 1)]?.name || "";
                          const step = `${Math.min(idx + 1, total)}/${total}`;
                          return (
                            <div title={curName ? `${curName} · ${step}` : step}>
                              <div className="h-1.5 w-full max-w-[110px] rounded-full bg-zinc-800 overflow-hidden">
                                <div
                                  className={cn("h-full rounded-full transition-all", done ? "bg-emerald-500/70" : "bg-sky-500/70")}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <div className="text-[10px] text-zinc-500 mt-1 tabular-nums">{step}</div>
                            </div>
                          );
                        })() : (
                          <span className="text-xs text-zinc-600">—</span>
                        )}
                      </TableCell>
                      {!hideFinancials && (
                        <TableCell>
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            {task.payment_status === "paid" ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] h-5 w-fit">
                                Paid
                              </Badge>
                            ) : task.payment_status === "approved" || task.payment_status === "payment_pending" ? (
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] h-5 w-fit">
                                To pay
                              </Badge>
                            ) : task.payment_status === "invoiced" ? (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-[10px] h-5 w-fit">
                                Invoiced
                              </Badge>
                            ) : task.payment_status === "pending" ? (
                              <Badge className="bg-zinc-600/30 text-zinc-400 border-zinc-500/30 text-[10px] h-5 w-fit">
                                Pending
                              </Badge>
                            ) : (
                              <Badge className="bg-zinc-700/50 text-zinc-500 border-zinc-600/30 text-[10px] h-5 w-fit">
                                —
                              </Badge>
                            )}
                            {task.agreed_price > 0 && (
                              <span className="text-[10px] text-zinc-500">
                                {task.currency === "UAH" ? "₴" : "$"}{task.agreed_price?.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {!hideFinancials && (
                        <TableCell className="text-right">
                          {!hasCosts ? (
                            <span className="text-orange-400 text-xs">{hasAssignedArtist ? "—" : "No costs"}</span>
                          ) : (
                            <div
                              className="flex flex-col items-end leading-tight whitespace-nowrap"
                              title={[
                                taskClientPrice > 0 ? `Client: ${formatAmount(taskClientPrice, taskCurrency)}` : null,
                                `Costs: ${formatAmount(taskTotalCost, taskCurrency)}`,
                                taskArtistPrice > 0 ? `· art: ${formatAmount(taskArtistPrice, taskCurrency)}` : null,
                                staffCostInTaskCurrency > 0 ? `· staff${staffCostFromWS ? " (Worksection " + (wsRollup?.total_hours || 0) + "h)" : ""}: ${formatAmount(staffCostInTaskCurrency, taskCurrency)}` : null,
                                taskAdFee > 0 ? `· AD: ${formatAmount(taskAdFee, taskCurrency)}` : null,
                              ].filter(Boolean).join(" ")}
                            >
                              <span className={cn("font-mono text-sm font-semibold", taskProfit > 0 ? "text-green-400" : taskProfit < 0 ? "text-red-400" : "text-zinc-500")}>
                                {formatAmount(taskProfit, taskCurrency)}
                              </span>
                              <span className={cn("font-mono text-[10px] font-medium", isZeroMargin ? "text-red-400" : taskMarginColor)}>
                                {isZeroMargin ? "0%!" : (taskClientPrice > 0 ? `${taskMarginPercent.toFixed(0)}%` : "—")}
                              </span>
                            </div>
                          )}
                        </TableCell>
                      )}
                      {isClient && (
                        <TableCell className="text-right">
                          <span className="text-zinc-50 font-mono">
                            {formatAmount(task.client_price || 0, taskCurrency)}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-0.5">
                          {!hideFinancials && taskClientPrice > 0 && (
                            (() => {
                              const hasLinkedPayment = services.some(s => s.from_task_id === task.id || (s.name === task.name && s.amount === taskClientPrice));
                              return !hasLinkedPayment ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-zinc-500 hover:text-purple-400 h-7 w-7 p-0"
                                  onClick={() => handleCreatePaymentFromTask(task)}
                                  disabled={creatingPayment}
                                  title="Create payment from task"
                                  data-testid={`create-payment-from-task-${task.id}`}
                                >
                                  <DollarSign className="h-3.5 w-3.5" />
                                </Button>
                              ) : (
                                <span className="h-7 w-7 flex items-center justify-center" title="Payment exists">
                                  <DollarSign className="h-3 w-3 text-purple-400/40" />
                                </span>
                              );
                            })()
                          )}
                          {/* Setup chat — only for tasks whose type maps to a setup flow.
                              Sales fills their owner=sales fields here (these gate task start). */}
                          {!isClient && (() => {
                            const ty = (task.task_type || task.service_type || "").toLowerCase();
                            const isSetupTask = ty && (setupChatKeywords || []).some((kw) => kw && ty.includes(kw));
                            return isSetupTask ? (
                              <SetupChatButton
                                taskId={task.id}
                                serviceName={task.name}
                                serviceType={task.task_type || task.service_type}
                                canEdit={true}
                                variant="icon"
                              />
                            ) : null;
                          })()}
                          <Link to={isClient ? `/client/tasks/${task.id}` : `/tasks/${task.id}`}>
                            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-zinc-50 h-7 w-7 p-0">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                          {!isClient && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-zinc-500 hover:text-red-400 h-7 w-7 p-0"
                              onClick={() => handleDeleteTask(task.id, task.name)}
                              data-testid={`delete-task-${task.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Expanded row — full task detail (Layer 3): labelled stage timeline + economics breakdown + change requests */}
                    {!isClient && expandedTaskId === task.id && (
                      <TableRow className="border-zinc-800 bg-zinc-900/50">
                        <TableCell colSpan={9} className="py-3 px-4">
                          <div className="space-y-3">
                            {taskTimelines[task.id]?.stages?.length > 0 && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Progress</p>
                                <TaskTimeline
                                  stages={taskTimelines[task.id].stages}
                                  currentStageIndex={taskTimelines[task.id].current_stage_index || 0}
                                  showLabels={true}
                                  size="default"
                                />
                              </div>
                            )}
                            {!hideFinancials && hasCosts && (
                              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs pt-2 border-t border-zinc-800">
                                <span className="text-zinc-500">Client <span className="text-zinc-200 font-mono">{formatAmount(taskClientPrice, taskCurrency)}</span></span>
                                <span className="text-zinc-500">Costs <span className="text-red-400 font-mono">{formatAmount(taskTotalCost, taskCurrency)}</span></span>
                                <span className="text-zinc-500">Profit <span className={cn("font-mono", taskProfit >= 0 ? "text-green-400" : "text-red-400")}>{formatAmount(taskProfit, taskCurrency)}</span></span>
                                <span className="text-zinc-500">Margin <span className={cn("font-mono", taskMarginColor)}>{taskClientPrice > 0 ? `${taskMarginPercent.toFixed(0)}%` : "—"}</span></span>
                              </div>
                            )}
                            <TaskChangeRequests
                              taskId={task.id}
                              taskName={task.name}
                              currentDeadline={task.deadline}
                              currentPrice={task.agreed_price}
                              currentClientPrice={task.client_price}
                              taskCurrency={taskCurrency}
                              canApprove={user?.role === "admin" || user?.role === "pm_lead" || user?.role === "finance"}
                              onChangeApplied={fetchData}
                              compact={false}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                  })}
                </TableBody>
              </Table>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Services/Payments - hide entirely for art directors (no prices); show read-only label for clients */}
      {!isArtDirector && (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4 text-purple-400" />{isClient ? "Your Payments" : "Payments"} ({services.length})
          </CardTitle>
          {!isClient && (
            <Button size="sm" onClick={openNewServiceModal} className="bg-purple-600 hover:bg-purple-700 h-7 text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" />Add Payment
            </Button>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {/* Payment vs Tasks mismatch alert */}
          {!isClient && totals.paymentTasksDiffUAH !== 0 && tasks.length > 0 && (
            <div className={cn(
              "mb-3 p-3 rounded-lg border flex items-center justify-between",
              totals.paymentTasksDiffUAH > 0 
                ? "bg-amber-900/20 border-amber-700/30" 
                : "bg-blue-900/20 border-blue-700/30"
            )}>
              <div className="flex items-center gap-2">
                <AlertTriangle className={cn("h-4 w-4", totals.paymentTasksDiffUAH > 0 ? "text-amber-400" : "text-blue-400")} />
                <div>
                  <p className={cn("text-sm font-medium", totals.paymentTasksDiffUAH > 0 ? "text-amber-400" : "text-blue-400")}>
                    {totals.paymentTasksDiffUAH > 0 
                      ? "Tasks exceed payments — additional invoice needed" 
                      : "Payments exceed tasks — room for more tasks"}
                  </p>
                  <p className="text-xs text-zinc-400">
                    Tasks total: {formatAmount(totals.tasksRevenueUAH, "UAH")} | 
                    Payments: {formatAmount(totals.planUAH, "UAH")} | 
                    Difference: <span className={totals.paymentTasksDiffUAH > 0 ? "text-amber-400" : "text-blue-400"}>
                      {totals.paymentTasksDiffUAH > 0 ? "+" : ""}{formatAmount(totals.paymentTasksDiffUAH, "UAH")}
                    </span>
                  </p>
                </div>
              </div>
              {totals.paymentTasksDiffUAH > 0 && (
                <Button 
                  size="sm" 
                  onClick={() => openNewServiceModal(totals.paymentTasksDiffUAH)}
                  className="bg-amber-600 hover:bg-amber-700 h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add {formatAmount(totals.paymentTasksDiffUAH, "UAH")}
                </Button>
              )}
            </div>
          )}
          {/* Requests vs Payments mismatch alert */}
          {!isClient && totals.requestsTotalUAH > 0 && (
            <div className={cn(
              "mb-3 p-3 rounded-lg border flex items-center justify-between",
              totals.requestsPaymentsDiffUAH > 0 
                ? "bg-purple-900/20 border-purple-700/30" 
                : "bg-emerald-900/20 border-emerald-700/30"
            )}>
              <div className="flex items-center gap-2">
                <MessageSquare className={cn("h-4 w-4", totals.requestsPaymentsDiffUAH > 0 ? "text-purple-400" : "text-emerald-400")} />
                <div>
                  <p className={cn("text-sm font-medium", totals.requestsPaymentsDiffUAH > 0 ? "text-purple-400" : "text-emerald-400")}>
                    {totals.requestsPaymentsDiffUAH > 0 
                      ? "Requests exceed payments — create additional payments" 
                      : "Payments cover all requests"}
                  </p>
                  <p className="text-xs text-zinc-400">
                    Requests total: {formatAmount(totals.requestsTotalUAH, "UAH")} | 
                    Payments: {formatAmount(totals.planUAH, "UAH")} | 
                    Difference: <span className={totals.requestsPaymentsDiffUAH > 0 ? "text-purple-400" : "text-emerald-400"}>
                      {totals.requestsPaymentsDiffUAH > 0 ? "+" : ""}{formatAmount(totals.requestsPaymentsDiffUAH, "UAH")}
                    </span>
                  </p>
                </div>
              </div>
              {totals.requestsPaymentsDiffUAH > 0 && (
                <Button 
                  size="sm" 
                  onClick={() => openNewServiceModal(totals.requestsPaymentsDiffUAH)}
                  className="bg-purple-600 hover:bg-purple-700 h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add {formatAmount(totals.requestsPaymentsDiffUAH, "UAH")}
                </Button>
              )}
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                {/* Payment name is capped: service names run to a full sentence
                    ("complete exterior modeling + rooftop amenities + …") and an
                    uncapped column pushed Docs/Split/Send off the right edge, so
                    PMs reported they simply had no split button. backlog 9e14663e */}
                <TableHead className="text-zinc-400 text-xs py-2 w-[260px]">Payment</TableHead>
                <TableHead className="text-zinc-400 text-xs py-2">Month</TableHead>
                <TableHead className="text-zinc-400 text-xs py-2">Currency</TableHead>
                {!isClient && <TableHead className="text-zinc-400 text-xs py-2 text-center">Prob.</TableHead>}
                <TableHead className="text-zinc-400 text-xs py-2">Legal Entity</TableHead>
                <TableHead className="text-zinc-400 text-xs py-2 text-right">Amount</TableHead>
                <TableHead className="text-zinc-400 text-xs py-2 text-center">Paid</TableHead>
                <TableHead className="text-zinc-400 text-xs py-2">Paid Date</TableHead>
                {!isClient && <TableHead className="text-zinc-400 text-xs py-2">Bank Tx</TableHead>}
                <TableHead className="text-zinc-400 text-xs py-2 text-center">Docs</TableHead>
                {!isClient && <TableHead className="text-zinc-400 w-[60px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.length === 0 ? (
                <TableRow><TableCell colSpan={isClient ? 8 : 11} className="text-center py-4 text-zinc-500 text-sm">No payments yet</TableCell></TableRow>
              ) : services.map(service => (
                <React.Fragment key={service.id}>
                  <TableRow className={cn("border-zinc-800", service.is_paid && "bg-green-950/20")}>
                    <TableCell className="py-1.5 max-w-[260px]">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {!isClient && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedServiceId(expandedServiceId === service.id ? null : service.id)}
                            className="h-5 w-5 p-0 shrink-0 text-zinc-400 hover:text-zinc-50"
                          >
                            {expandedServiceId === service.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                        {isClient ? (
                          <span className="text-zinc-50 text-sm truncate min-w-0" title={service.name}>{service.name}</span>
                        ) : (
                          <Button 
                            variant="link"
                            onClick={() => navigate(`/sales/payment/${service.id}`)}
                            title={service.name}
                            className="text-zinc-50 text-sm hover:text-orange-400 p-0 h-auto font-normal min-w-0 justify-start"
                          >
                            <span className="truncate">{service.name}</span>
                          </Button>
                        )}
                        {!isClient && service.payment_group_id && <Badge className="text-[10px] h-4 shrink-0 bg-purple-900/30 text-purple-400 border-purple-700">Split</Badge>}
                        {!isClient && service.source_request_id && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className="text-[10px] h-4 shrink-0 bg-emerald-900/30 text-emerald-400 border-emerald-700 cursor-help">
                                  <Link2 className="h-2.5 w-2.5 mr-0.5" />
                                  Linked
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs">
                                Locked by approved {service.source_request_type === "studio_request" ? "studio request" : "price request"}.<br/>
                                To change the amount, revise the source request.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {!isClient && project?.type === "trp" && !service.source_request_id && !service.from_price_request && !service.from_studio_request && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className="text-[10px] h-4 shrink-0 bg-amber-900/30 text-amber-400 border-amber-700 cursor-help">
                                  <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                                  Unlinked
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs">
                                Legacy payment with no source request. New TRP payments<br/>
                                auto-link to approved requests; backfill couldn't match this one.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-400 font-mono text-xs py-1.5">{service.month}</TableCell>
                    <TableCell className="py-1.5">
                      <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/30">
                        {service.currency || "UAH"}
                      </Badge>
                    </TableCell>
                    {/* Probability column for non-clients */}
                    {!isClient && (
                      <TableCell className="text-center py-1.5">
                        <Select
                          value={String(service.probability || formData.probability || 50)}
                          onValueChange={async (v) => {
                            try {
                              // The backend floors probability on factual signals
                              // (paid, demand sent, production phase). It used to do
                              // that silently: the pick stuck until you reloaded and
                              // found 50% again. Now we say what happened and why.
                              // backlog cc703e9d.
                              const res = await api.put(`/sales-services/${service.id}`, {
                                probability: Number(v),
                                probability_override: true
                              });
                              const saved = res?.data?.probability;
                              const reason = res?.data?.probability_floor_reason;
                              // Refresh services
                              const servicesRes = await api.get(`/sales-services?project_id=${projectId}`);
                              setServices(servicesRes.data || []);
                              if (saved != null && Number(saved) !== Number(v)) {
                                toast.warning(
                                  `Kept at ${saved}% — ${reason || "this payment has a higher floor"}`,
                                  { description: "Ask an admin or the sales director if it really should be lower." }
                                );
                              } else {
                                toast.success("Probability updated");
                              }
                            } catch (error) {
                              toast.error(formatApiError(error, "Failed to update probability"));
                            }
                          }}
                        >
                          <SelectTrigger className={cn(
                            "h-6 w-[60px] text-xs border-0",
                            (service.probability || 50) >= 80 ? "bg-green-900/30 text-green-400" :
                            (service.probability || 50) >= 50 ? "bg-blue-900/30 text-blue-400" :
                            (service.probability || 50) >= 30 ? "bg-yellow-900/30 text-yellow-400" :
                            "bg-zinc-700/50 text-zinc-400"
                          )}>
                            <SelectValue>{service.probability || formData.probability || 50}%</SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {(service.probability_floor || 0) > 0 && (
                              <div className="px-2 py-1.5 text-[10px] text-amber-400/90 border-b border-zinc-700">
                                Below {service.probability_floor}% is refused
                                {service.probability_floor_reason ? ` — ${service.probability_floor_reason}` : ""}
                              </div>
                            )}
                            {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(p => (
                              <SelectItem
                                key={p}
                                value={String(p)}
                                disabled={p < (service.probability_floor || 0)}
                                className={cn("text-xs", p < (service.probability_floor || 0) ? "text-zinc-600" : "text-zinc-50")}
                              >
                                {p}%
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    )}
                    <TableCell className="text-zinc-400 text-xs py-1.5">
                      {service.legal_entity_name || <span className="text-zinc-600">—</span>}
                    </TableCell>
                    <TableCell className="text-right py-1.5">
                      <span className="font-mono text-green-400 text-sm">{formatAmountRaw(service.amount, service.currency)}</span>
                    </TableCell>
                    <TableCell className="text-center py-1.5">
                      <div className="flex items-center justify-center gap-1">
                        {isClient ? (
                          <Badge className={service.is_paid ? "bg-green-900/30 text-green-400" : "bg-yellow-900/30 text-yellow-400"}>
                            {service.is_paid ? "Paid" : "Pending"}
                          </Badge>
                        ) : (
                          <Switch
                            checked={service.is_paid}
                            onCheckedChange={(checked) => handleTogglePaid(service.id, checked)}
                            className="data-[state=checked]:bg-green-600 scale-90"
                          />
                        )}
                        {!isClient && (service.is_paid ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        ) : (
                          <Clock className="h-3.5 w-3.5 text-zinc-500" />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-xs py-1.5">{service.paid_date || "—"}</TableCell>
                    {/* Bank Transaction link column */}
                    {!isClient && (
                      <TableCell className="py-1.5">
                        {service.bank_transaction_id && service.bank_transaction_info ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1">
                                  <Badge className="bg-green-900/30 text-green-400 border-green-700/30 text-[10px] cursor-pointer">
                                    <Landmark className="h-2.5 w-2.5 mr-1" />
                                    {service.bank_transaction_info.date}
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleUnlinkTransaction(service.id)}
                                    className="h-5 w-5 p-0 text-zinc-500 hover:text-red-400"
                                    title="Отвязать транзакцию"
                                  >
                                    <Unlink className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-zinc-800 border-zinc-700 max-w-sm">
                                <div className="text-xs space-y-1">
                                  <p><strong>Дата:</strong> {service.bank_transaction_info.date}</p>
                                  <p><strong>Сумма:</strong> {service.bank_transaction_info.amount} {service.bank_transaction_info.currency}</p>
                                  <p><strong>Отправитель:</strong> {service.bank_transaction_info.counterparty_name || "—"}</p>
                                  <p><strong>Назначение:</strong> {service.bank_transaction_info.purpose?.substring(0, 100) || "—"}</p>
                                  <p><strong>ФОП:</strong> {service.bank_transaction_info.fop_name || "—"}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenLinkTransaction(service)}
                            className="h-6 px-2 text-[10px] text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                            title="Связать с банковской транзакцией"
                          >
                            <Landmark className="h-3 w-3 mr-1" />
                            Связать
                          </Button>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="py-1.5">
                      <div className="flex items-center gap-1">
                        {/* Edit button */}
                        {!isClient && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditPayment(service)}
                            className="h-6 w-6 p-0 text-zinc-400 hover:text-zinc-50 hover:bg-zinc-700"
                            title="Edit Payment"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                        {/* Split button - only for unpaid payments without split group */}
                        {!isClient && !service.is_paid && !service.payment_group_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenSplitDialog(service)}
                            className="h-6 w-6 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                            title="Split into multiple payments"
                          >
                            <Split className="h-3 w-3" />
                          </Button>
                        )}
                        {/* Invoice button - always available */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadInvoice(service)}
                          className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          title="Download Invoice"
                        >
                          <FileSignature className="h-3 w-3 mr-1" />
                          Inv
                        </Button>
                        {/* Demand-payment unified: invoice-sent state is driven by
                            the Send-Demand action (paper airplane button in the next
                            column). Cashflow forecast picks it up automatically. */}
                        {/* Act button - now available for all payments */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadAct(service)}
                          className="h-6 px-2 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          title="Download Act (Акт)"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Act
                        </Button>
                        {/* Stripe payment link */}
                        {!isClient && (
                          service.stripe_payment_status === "paid" ? (
                            <span className="h-6 px-2 flex items-center text-xs text-green-400" title="Paid via Stripe">
                              <CreditCard className="h-3 w-3 mr-1" />
                              Paid
                            </span>
                          ) : service.stripe_checkout_url ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCreateStripeLink(service)}
                              className="h-6 px-2 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                              title="Copy Stripe payment link"
                              data-testid={`stripe-copy-${service.id}`}
                            >
                              <CreditCard className="h-3 w-3 mr-1" />
                              Link
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCreateStripeLink(service)}
                              className="h-6 px-2 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                              title="Create Stripe payment link"
                              data-testid={`stripe-create-${service.id}`}
                            >
                              <CreditCard className="h-3 w-3 mr-1" />
                              Pay
                            </Button>
                          )
                        )}
                      </div>
                    </TableCell>
                    {!isClient && (
                      <TableCell className="py-1.5">
                        <div className="flex items-center gap-1">
                          {(() => {
                            // Demand-payment is the single source of truth for
                            // "invoice sent" state. Click sends; click-on-sent
                            // offers to revoke (deletes client_request and
                            // unmarks invoice_sent_at on the service).
                            const linkedRequest = clientRequests.find(r =>
                              r.request_type === "payment_request" &&
                              r.service_id === service.id
                            );
                            const isSent = sentPaymentRequests.has(service.id) || !!linkedRequest;
                            const revoke = async () => {
                              if (!linkedRequest) return;
                              if (!window.confirm("Cancel the payment demand sent to this client?")) return;
                              try {
                                await api.delete(`/client-requests/${linkedRequest.id}`);
                                setSentPaymentRequests((prev) => {
                                  const next = new Set(prev);
                                  next.delete(service.id);
                                  return next;
                                });
                                toast.success("Demand revoked");
                                fetchData();
                              } catch (e) {
                                toast.error(formatApiError(e, "Failed to revoke demand"));
                              }
                            };
                            const sentDate = linkedRequest?.created_at?.slice(0, 10);
                            return (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => (isSent ? revoke() : handleSendPaymentRequest(service))}
                                className={cn(
                                  "h-6 w-6 p-0",
                                  isSent
                                    ? "bg-emerald-500/15 text-emerald-300 hover:bg-rose-500/15 hover:text-rose-300 ring-1 ring-emerald-500/40"
                                    : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                )}
                                title={isSent
                                  ? `Demand sent${sentDate ? " on " + sentDate : ""} — click to revoke`
                                  : "Send payment demand to client"}
                              >
                                {isSent ? (
                                  <CheckCircle2 className="h-3 w-3" />
                                ) : (
                                  <Send className="h-3 w-3" />
                                )}
                              </Button>
                            );
                          })()}
                          {/* Invoice download button */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/sales/payment/${service.id}`)}
                            className="h-6 w-6 p-0 text-zinc-400 hover:text-orange-400"
                            title="Open payment details"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                          {/* Setup chat moved to the production task — see TaskDetailPage. */}
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteService(service.id)} className="h-6 w-6 p-0 text-red-400 hover:text-red-300">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {/* Expanded row for Change Requests - only for internal users */}
                  {!isClient && expandedServiceId === service.id && (
                    <TableRow className="border-zinc-800 bg-zinc-900/50">
                      <TableCell colSpan={9} className="py-2 px-3">
                        <ScopeChangeRequests
                          projectId={actualProjectId}
                          serviceId={service.id}
                          serviceName={service.name}
                          isProjectActive={isInProduction}
                          compact={true}
                          onApplied={fetchData}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {/* Activity — Client Requests + Change History combined into one tabbed card (both often small; placed under Payments to reclaim vertical space). */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="flex items-center gap-1">
            {[
              { k: "requests", label: "Client Requests", n: clientRequests.length },
              { k: "history", label: "Change History", n: project?.change_history?.length || 0 },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setActivityTab(t.k)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                  activityTab === t.k ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:text-zinc-200"
                )}
              >
                {t.label}
                <span className={cn("text-[10px]", activityTab === t.k ? "text-zinc-400" : "text-zinc-600")}>{t.n}</span>
              </button>
            ))}
          </div>
          {activityTab === "requests" && !isClient && (project?.client_id || project?.client_name) && (
            <CreateClientRequestDialog
              projectId={actualProjectId}
              onCreated={fetchData}
              trigger={
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-7 text-xs">
                  <Send className="h-3.5 w-3.5 mr-1" />Request
                </Button>
              }
            />
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {activityTab === "requests" ? (
            clientRequests.length === 0 ? (
              <div className="text-center py-4 text-zinc-500">
                <MessageSquare className="h-6 w-6 mx-auto mb-1 opacity-50" />
                <p className="text-sm">No client requests yet</p>
                {!project?.client_id && !project?.client_name && (
                  <p className="text-xs mt-1 text-orange-400">Add a client to send requests</p>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400 text-xs py-2">Request</TableHead>
                    <TableHead className="text-zinc-400 text-xs py-2">Type</TableHead>
                    <TableHead className="text-zinc-400 text-xs py-2">Status</TableHead>
                    <TableHead className="text-zinc-400 text-xs py-2">Time</TableHead>
                    <TableHead className="text-zinc-400 text-xs py-2 w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientRequests.map((req) => (
                    <TableRow key={req.id} className={cn("border-zinc-800 cursor-pointer hover:bg-zinc-800/50", req.is_overdue && "bg-red-950/20")} onClick={() => openViewRequest(req.id)}>
                      <TableCell className="py-2"><span className="text-zinc-50 text-sm">{req.title}</span></TableCell>
                      <TableCell className="py-2"><Badge variant="outline" className="text-[10px] h-5">{req.request_type?.replace(/_/g, ' ')}</Badge></TableCell>
                      <TableCell className="py-2">
                        <Badge variant="outline" className={cn("text-[10px] h-5",
                          req.status === "responded" ? "bg-green-500/10 text-green-400 border-green-500/30"
                          : req.is_overdue ? "bg-red-500/10 text-red-400 border-red-500/30"
                          : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                        )}>
                          {req.status === "responded" ? req.response_type : req.is_overdue ? "OVERDUE" : "Pending"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[10px] text-zinc-500 py-2">
                        {req.time_elapsed_hours !== undefined && !isNaN(req.time_elapsed_hours)
                          ? req.time_elapsed_hours < 24 ? `${Math.round(req.time_elapsed_hours)}h` : `${Math.floor(req.time_elapsed_hours / 24)}d`
                          : "—"}
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openViewRequest(req.id); }} className="h-6 w-6 p-0 text-zinc-500 hover:text-sky-400" title="View details">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {!isClient && (
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteClientRequest(req.id); }} className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400" title="Delete request">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            project?.change_history?.length > 0 ? (
              <ChangeHistoryBlock history={project.change_history} bare maxItems={15} />
            ) : (
              <p className="text-sm text-zinc-500 italic py-3">No changes recorded yet</p>
            )
          )}
        </CardContent>
      </Card>

      {/* New Service Modal */}
      <Dialog open={showNewService} onOpenChange={setShowNewService}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Plus className="h-5 w-5 text-purple-400" />Add Payment / Service
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Service Type Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Service Type</Label>
              <Select value={newService.service_type} onValueChange={handleServiceTypeSelect}>
                <SelectTrigger className={cn("bg-zinc-800 border-zinc-700 text-zinc-50", !newService.service_type && "text-zinc-500")}>
                  <SelectValue placeholder="Select service type..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-72">
                  {serviceTypes.map(st => (
                    <SelectItem key={st.id || st.name} value={st.name} className="text-zinc-50">
                      <div className="flex justify-between items-center w-full gap-3">
                        <span>{st.name}</span>
                        {st.base_price > 0 && (
                          <span className="text-xs text-green-400 ml-2 font-mono">{st.base_price}€</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="Payment" className="text-zinc-500 border-t border-zinc-700 mt-1">
                    Custom Payment (free text)
                  </SelectItem>
                </SelectContent>
              </Select>
              {newService.suggestedBasePrice > 0 && (
                <p className="text-xs text-green-500">Base price: €{newService.suggestedBasePrice}/unit</p>
              )}
            </div>

            {/* Project Budget Info - shows in selected currency */}
            {totals.taskCount > 0 && (
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Project Budget (from tasks)</span>
                  <span className="text-lg font-semibold text-zinc-50">
                    {getCurrencySymbol(newService.currency)}{parseFloat(newService.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 mt-1">{totals.taskCount} tasks total</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Payment Name {newService.service_type && newService.service_type !== "Payment" && <span className="text-zinc-600 text-xs">(auto-filled)</span>}</Label>
              <Input value={newService.name} onChange={(e) => setNewService({ ...newService, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-50" placeholder={newService.service_type === "Payment" ? "Enter payment name..." : "Auto-filled from service type"} />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Amount</Label>
                <Input type="number" value={newService.totalAmount} onChange={(e) => {
                  setNewService({ ...newService, totalAmount: e.target.value });
                }} onBlur={recalculateAmounts} className="bg-zinc-800 border-zinc-700 text-zinc-50" placeholder="10000" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Currency</Label>
                <Select value={newService.currency} onValueChange={handlePaymentCurrencyChange}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {CURRENCIES.map(c => <SelectItem key={c} value={c} className="text-zinc-50">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Legal Entity Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Legal Entity (for invoice)</Label>
              <Select value={newService.legal_entity_id} onValueChange={(v) => setNewService({ ...newService, legal_entity_id: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue placeholder="Select legal entity..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {legalEntities.filter(e => e.is_active).map(entity => (
                    <SelectItem key={entity.id} value={entity.id} className="text-zinc-50">
                      {entity.name} {entity.company_type === 'llc' ? '(LLC)' : entity.company_type === 'corp' ? '(Corp)' : '(ФОП)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">Entity whose bank details will be used for invoice</p>
            </div>

            {/* Bill to — which of the client's legal entities receives the рахунок.
                It used to exist only in the edit dialog, so the only way to pick
                the counterparty was to create the payment and open it again.
                backlog 7dbaf65c. */}
            {(() => {
              const clientObj = getClient();
              const profiles = clientObj?.billing_profiles || [];
              if (profiles.length < 2) return null;
              const defaultProfile = profiles.find(p => p.is_default) || profiles[0];
              return (
                <div className="space-y-2">
                  <Label className="text-zinc-400">Bill to (Client Legal Entity)</Label>
                  <Select
                    value={newService.client_billing_profile_id || "default"}
                    onValueChange={(v) => setNewService({ ...newService, client_billing_profile_id: v === "default" ? "" : v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50" data-testid="new-payment-bill-to">
                      <SelectValue placeholder="Use client default..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="default" className="text-zinc-300">
                        Default ({defaultProfile?.label || defaultProfile?.legal_name || "client default"})
                      </SelectItem>
                      {profiles.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-zinc-50">
                          {p.label || p.legal_name || "(unnamed profile)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">Recipient (Отримувач) on the invoice and act</p>
                </div>
              );
            })()}

            <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
              <Switch checked={newService.splitPayments} onCheckedChange={(v) => setNewService({ ...newService, splitPayments: v })} />
              <div>
                <p className="text-sm text-zinc-50 flex items-center gap-2"><Split className="h-4 w-4 text-purple-400" />Split into multiple payments</p>
                <p className="text-xs text-zinc-500">Divide budget across different months</p>
              </div>
            </div>

            {/* Payment Schedule */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Payment Schedule</Label>
              <div className="space-y-2">
                {newService.payments.map((payment, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-800/30 rounded">
                    <Select value={payment.month} onValueChange={(v) => updatePayment(idx, "month", v)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 w-[120px] h-8 text-sm"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {monthOptions.map(m => <SelectItem key={m.value} value={m.value} className="text-zinc-50 text-sm">{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    
                    {newService.splitPayments && (
                      <>
                        <div className="flex items-center gap-1">
                          <Input type="number" value={payment.percent} onChange={(e) => updatePayment(idx, "percent", e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-50 w-[70px] h-8 text-sm text-center" placeholder="%" />
                          <span className="text-zinc-500 text-sm">%</span>
                        </div>
                        <span className="text-zinc-600">=</span>
                      </>
                    )}
                    
                    <div className="flex items-center gap-1 flex-1">
                      <Input type="number" value={newService.splitPayments ? payment.amount : newService.totalAmount} onChange={(e) => newService.splitPayments ? updatePayment(idx, "amount", e.target.value) : null} readOnly={!newService.splitPayments} className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm" />
                      <span className="text-zinc-500 text-sm">{newService.currency}</span>
                    </div>
                    
                    {newService.splitPayments && newService.payments.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => removePaymentRow(idx)} className="h-7 w-7 p-0 text-red-400">×</Button>
                    )}
                  </div>
                ))}
              </div>
              
              {newService.splitPayments && (
                <div className="flex items-center justify-between">
                  <Button size="sm" variant="outline" onClick={addPaymentRow} className="text-purple-400 border-purple-700 hover:bg-purple-900/30">
                    <Plus className="h-3 w-3 mr-1" />Add Row
                  </Button>
                  <span className={cn("text-sm font-mono", Math.abs(totalPercent - 100) < 0.1 ? "text-green-400" : "text-yellow-400")}>
                    Total: {totalPercent.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewService(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleAddService} className="bg-purple-600 hover:bg-purple-700">
              {newService.splitPayments && newService.payments.length > 1 ? `Create ${newService.payments.length} Payments` : "Create Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Task Dialog */}
      <Dialog open={showNewTask} onOpenChange={setShowNewTask}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-cyan-400" />
              Create New Task
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateTask} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Service Type *</Label>
                <Select
                  value={newTask.task_type}
                  onValueChange={(v) => setNewTask({ ...newTask, task_type: v })}
                  required
                >
                  <SelectTrigger className={cn("bg-zinc-950 border-zinc-800 text-zinc-50", !newTask.task_type && "text-zinc-500")}>
                    <SelectValue placeholder="Select service type..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                    {serviceTypeNames.map(type => (
                      <SelectItem key={type} value={type}>
                        {type.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-400">Currency</Label>
                <Select
                  value={newTask.currency}
                  onValueChange={(v) => setNewTask({ ...newTask, currency: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                    <SelectItem value="UAH">UAH (₴)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Name <span className="text-zinc-600 text-xs">(optional — abbreviation or note)</span></Label>
              <Input
                value={newTask.name}
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                placeholder="e.g. ANC-2, Phase 1, Kitchen"
                className="bg-zinc-950 border-zinc-800 text-zinc-50"
                data-testid="create-task-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Deadline</Label>
              <Input
                type="date"
                value={newTask.deadline}
                onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-50"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Assign Artist</Label>
              <ArtistSelector
                selectedArtistId={newTask.assigned_artist_id === "unassigned" ? "" : newTask.assigned_artist_id}
                onSelect={(artistId) => setNewTask({ ...newTask, assigned_artist_id: artistId || "unassigned" })}
                taskType={newTask.task_type}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Artist Price ({newTask.currency})</Label>
                <Input
                  type="number"
                  value={newTask.agreed_price}
                  onChange={(e) => setNewTask({ ...newTask, agreed_price: e.target.value })}
                  placeholder="0"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-400">Client Price ({newTask.currency})</Label>
                <Input
                  type="number"
                  value={newTask.client_price}
                  onChange={(e) => setNewTask({ ...newTask, client_price: e.target.value })}
                  placeholder="0"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                />
              </div>
            </div>

            {/* Margin Indicator */}
            {newTaskMargin.status !== 'none' && (
              <div className={`p-3 rounded-lg border ${
                newTaskMargin.status === 'CRITICAL' ? 'bg-red-500/10 border-red-500/30' :
                newTaskMargin.status === 'WARNING' ? 'bg-yellow-500/10 border-yellow-500/30' :
                'bg-green-500/10 border-green-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${newTaskMargin.color}`}>
                    Artist Share: {newTaskMargin.percent}%
                  </span>
                  <span className={`text-xs ${newTaskMargin.color}`}>
                    {newTaskMargin.status === 'CRITICAL' ? `Exceeds ${marginSettings.critical_threshold_percent}% - Cannot create` :
                     newTaskMargin.status === 'WARNING' ? `Warning: above ${marginSettings.warning_threshold_percent}%` :
                     'Margin OK'}
                  </span>
                </div>
              </div>
            )}

            {/* Who sees this task. Default visible — that is how tasks behaved
                before the flag, and the cabinet is the client's only window
                into progress. Untick for internal work. backlog 46d2b81b. */}
            <div className="p-2 border border-zinc-700 rounded-lg">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="task_visible_to_client"
                  checked={newTask.visible_to_client !== false}
                  onChange={(e) => setNewTask({ ...newTask, visible_to_client: e.target.checked })}
                  className="w-4 h-4 accent-orange-500"
                  data-testid="task-visible-to-client"
                />
                <Label htmlFor="task_visible_to_client" className="text-zinc-300 cursor-pointer text-sm">
                  Показувати клієнту
                  <span className="text-zinc-500 ml-1 text-xs">
                    {newTask.visible_to_client === false ? "— внутрішня задача" : "— видно в кабінеті"}
                  </span>
                </Label>
              </div>
            </div>

            {/* Art Director Review */}
            <div className="p-2 border border-zinc-700 rounded-lg space-y-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="art_director_review_sales"
                  checked={newTask.art_director_review}
                  onChange={(e) => setNewTask({ 
                    ...newTask, 
                    art_director_review: e.target.checked,
                    art_director_id: e.target.checked ? newTask.art_director_id : ""
                  })}
                  className="w-4 h-4 accent-orange-500"
                />
                <Label htmlFor="art_director_review_sales" className="text-zinc-300 cursor-pointer text-sm">
                  Art Director Review
                  {newTask.art_director_id && (
                    <span className="text-orange-400 ml-1">
                      (+{artDirectors.find(ad => ad.id === newTask.art_director_id)?.art_director_bonus_percent ?? 0}%)
                    </span>
                  )}
                </Label>
              </div>
              {newTask.art_director_review && (
                <div className="space-y-1.5 pt-1">
                  <Label className="text-zinc-400 text-xs">
                    Select Art Director <span className="text-red-400">*</span>
                  </Label>
                  <Select
                    value={newTask.art_director_id}
                    onValueChange={(v) => setNewTask({ ...newTask, art_director_id: v })}
                  >
                    <SelectTrigger className={`bg-zinc-950 border-zinc-800 text-zinc-50 w-full ${!newTask.art_director_id ? 'border-orange-500/50' : ''}`}>
                      <SelectValue placeholder="Choose art director... (required)" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {artDirectors.map(ad => (
                        <SelectItem key={ad.id} value={ad.id}>
                          {ad.name} <span className="text-zinc-500">({ad.art_director_bonus_percent ?? 0}%)</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!newTask.art_director_id && (
                    <p className="text-xs text-orange-400">Selection required</p>
                  )}
                </div>
              )}
            </div>

            {/* Brief / Description */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-400">Brief / Description</Label>
                <Input
                  type="url"
                  value={newTask.brief_link}
                  onChange={(e) => setNewTask({ ...newTask, brief_link: e.target.value })}
                  placeholder="TZ link (optional)"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 text-xs w-48 h-7"
                />
              </div>
              <Textarea
                value={newTask.brief}
                onChange={(e) => setNewTask({ ...newTask, brief: e.target.value })}
                placeholder="Task requirements and details..."
                className="bg-zinc-950 border-zinc-800 text-zinc-50 min-h-[100px]"
              />
            </div>
            
            {/* Quantity & Rating - Compact Row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={newTask.quantity}
                  onChange={(e) => setNewTask({ ...newTask, quantity: Math.min(20, Math.max(1, parseInt(e.target.value) || 1)) })}
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8"
                />
              </div>
              
              {(!newTask.assigned_artist_id || newTask.assigned_artist_id === "unassigned") && (
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">Min Artist Rating</Label>
                  <Select
                    value={newTask.min_artist_rating?.toString() || "any"}
                    onValueChange={(v) => setNewTask({ ...newTask, min_artist_rating: v === "any" ? null : parseInt(v) })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8">
                      <SelectValue placeholder="Any rating" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="any">Any</SelectItem>
                      {[5, 6, 7, 8, 9, 10].map(r => (
                        <SelectItem key={r} value={r.toString()}>{r}+</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewTask(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button 
                type="submit" 
                className={newTaskMargin.status === 'CRITICAL' ? "bg-orange-600 hover:bg-orange-700" : "bg-cyan-600 hover:bg-cyan-700"}
              >
                {newTaskMargin.status === 'CRITICAL' ? 'Create (Low Margin)' : 'Create Task'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Client Request Dialog */}
      <Dialog open={!!viewRequest} onOpenChange={() => setViewRequest(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Eye className="h-5 w-5 text-sky-400" />
              Request Details
            </DialogTitle>
          </DialogHeader>
          
          {viewRequestLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : viewRequest && (
            <div className="space-y-4 py-2">
              {/* Title and Status */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-zinc-50">{viewRequest.title}</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={cn(
                    "text-xs",
                    viewRequest.status === "pending" 
                      ? viewRequest.is_overdue 
                        ? "bg-red-500/10 text-red-400 border-red-500/30"
                        : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                      : "bg-green-500/10 text-green-400 border-green-500/30"
                  )}>
                    {viewRequest.status === "responded" ? "Responded" : viewRequest.is_overdue ? "OVERDUE" : "Pending"}
                  </Badge>
                  {viewRequest.template_name && (
                    <Badge variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                      {viewRequest.template_name}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-800/50 rounded-lg text-sm">
                <div>
                  <p className="text-xs text-zinc-500">Client</p>
                  <p className="text-zinc-50">{viewRequest.client_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Created By</p>
                  <p className="text-zinc-50">{viewRequest.created_by_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Type</p>
                  <p className="text-zinc-50">{viewRequest.request_type?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Time</p>
                  <p className="text-zinc-50">
                    {viewRequest.time_elapsed_hours !== undefined && !isNaN(viewRequest.time_elapsed_hours)
                      ? viewRequest.time_elapsed_hours < 24 
                        ? `${Math.round(viewRequest.time_elapsed_hours)}h`
                        : `${Math.floor(viewRequest.time_elapsed_hours / 24)}d`
                      : "—"
                    }
                  </p>
                </div>
              </div>

              {/* Description */}
              {viewRequest.description && (
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Description</p>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{viewRequest.description}</p>
                </div>
              )}

              {/* Template Fields with Submitted Data */}
              {viewRequest.template_fields && viewRequest.template_fields.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">
                    {viewRequest.status === "responded" ? "Client Submitted Data" : "Fields Client Needs to Fill"}
                  </p>
                  <div className="space-y-2 p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                    {viewRequest.template_fields.map((field) => {
                      const value = viewRequest.template_data?.[field.id];
                      const hasValue = value && (typeof value === "string" ? value.trim() !== "" : true);
                      
                      return (
                        <div key={field.id} className="py-2 px-2 bg-zinc-800/50 rounded">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-zinc-400 text-sm font-medium">{field.label}</span>
                            <div className="flex items-center gap-2">
                              {field.is_required && !hasValue && viewRequest.status === "pending" && (
                                <span className="text-red-400 text-xs">required</span>
                              )}
                              <Badge variant="outline" className="text-[10px] h-5 bg-zinc-900 text-zinc-500 border-zinc-700">
                                {field.field_type}
                              </Badge>
                            </div>
                          </div>
                          {/* Show submitted value */}
                          {hasValue ? (
                            <div className="mt-1">
                              {field.field_type === "file" ? (
                                <a 
                                  href={value} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1"
                                >
                                  <Download className="h-3 w-3" />
                                  Download File
                                </a>
                              ) : field.field_type === "url" ? (
                                <a 
                                  href={value.startsWith("http") ? value : `https://${value}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1 break-all"
                                >
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                  {value}
                                </a>
                              ) : field.field_type === "checkbox" ? (
                                <span className={cn("text-sm", value ? "text-green-400" : "text-zinc-500")}>
                                  {value ? "✓ Yes" : "✗ No"}
                                </span>
                              ) : (
                                <p className="text-zinc-50 text-sm whitespace-pre-wrap">{value}</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-zinc-600 text-sm italic mt-1">
                              {viewRequest.status === "pending" ? "Not yet filled" : "Not provided"}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Client Response */}
              {viewRequest.status === "responded" && (
                <div className="space-y-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-xs text-green-400 uppercase tracking-wider flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Client Response: {viewRequest.response_type}
                  </p>
                  {viewRequest.response_comment && (
                    <p className="text-sm text-zinc-300">{viewRequest.response_comment}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setViewRequest(null)} className="border-zinc-700">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Task Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(open) => { if (!open) { setAssignDialogOpen(false); setAssigningTask(null); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <User className="h-5 w-5 text-orange-500" />
              Assign Task: {assigningTask?.name}
            </DialogTitle>
          </DialogHeader>
          
          {assigningTask && (
            <div className="space-y-4">
              {/* Task Info */}
              <div className="bg-zinc-800/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Task Type:</span>
                  <span className="text-zinc-50">{assigningTask.task_type}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Client Price:</span>
                  <span className="text-zinc-50 font-mono">
                    {assigningTask.currency === "USD" ? "$" : assigningTask.currency === "EUR" ? "€" : "₴"}
                    {assigningTask.client_price?.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Artist Price Input */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Artist Price ({assigningTask.currency || "USD"}) *</Label>
                <Input
                  type="number"
                  placeholder="Enter artist price"
                  value={assignForm.agreed_price}
                  onChange={(e) => setAssignForm({ ...assignForm, agreed_price: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
                {assignMargin.status !== 'none' && assignMargin.suggestedPrice && (
                  <div className="text-xs text-zinc-500">
                    Suggested: {assigningTask.currency === "USD" ? "$" : assigningTask.currency === "EUR" ? "€" : "₴"}
                    {Math.round(assignMargin.suggestedPrice).toLocaleString()} 
                    {" "}(max: {assigningTask.currency === "USD" ? "$" : assigningTask.currency === "EUR" ? "€" : "₴"}
                    {Math.round(assignMargin.maxPrice).toLocaleString()})
                  </div>
                )}
              </div>

              {/* Margin Calculator */}
              {assignMargin.status !== 'none' && (
                <div className={cn(
                  "p-3 rounded-lg border",
                  assignMargin.status === 'CRITICAL' ? "bg-red-500/10 border-red-500/30" :
                  assignMargin.status === 'WARNING' ? "bg-yellow-500/10 border-yellow-500/30" :
                  "bg-green-500/10 border-green-500/30"
                )}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-300">Artist Share:</span>
                    <span className={cn("font-bold", assignMargin.color)}>
                      {assignMargin.percent}%
                      {assignMargin.status === 'CRITICAL' && <span className="ml-2 text-xs">(CRITICAL)</span>}
                      {assignMargin.status === 'WARNING' && <span className="ml-2 text-xs">(WARNING)</span>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-zinc-300">Your Margin:</span>
                    <span className={cn("font-bold", assignMargin.color)}>
                      {(100 - parseFloat(assignMargin.percent)).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Assignment Option */}
              <div className="space-y-3">
                <Label className="text-zinc-300">Assignment</Label>
                
                {/* Send to Pool option */}
                <div 
                  className={cn(
                    "p-3 rounded-lg border cursor-pointer transition-colors",
                    assignForm.send_to_pool 
                      ? "bg-blue-500/20 border-blue-500/50" 
                      : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
                  )}
                  onClick={() => setAssignForm({ ...assignForm, send_to_pool: true, assigned_artist_id: "" })}
                >
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                      assignForm.send_to_pool ? "border-blue-500 bg-blue-500" : "border-zinc-500"
                    )}>
                      {assignForm.send_to_pool && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <span className="text-zinc-50 font-medium">Send to Pool</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1 ml-6">Artists can pick up this task from the pool</p>
                </div>

                {/* Assign to specific artist */}
                <div 
                  className={cn(
                    "p-3 rounded-lg border cursor-pointer transition-colors",
                    !assignForm.send_to_pool 
                      ? "bg-purple-500/20 border-purple-500/50" 
                      : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
                  )}
                  onClick={() => setAssignForm({ ...assignForm, send_to_pool: false })}
                >
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                      !assignForm.send_to_pool ? "border-purple-500 bg-purple-500" : "border-zinc-500"
                    )}>
                      {!assignForm.send_to_pool && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <span className="text-zinc-50 font-medium">Assign to Artist</span>
                  </div>
                  
                  {!assignForm.send_to_pool && (
                    <div className="mt-3 ml-6">
                      <Select
                        value={assignForm.assigned_artist_id || ""}
                        onValueChange={(v) => setAssignForm({ ...assignForm, assigned_artist_id: v })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-50">
                          <SelectValue placeholder="Select artist..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                          {artists.map(a => (
                            <SelectItem key={a.id} value={a.id} className="text-zinc-300">
                              <div className="flex items-center justify-between w-full gap-4">
                                <span>{a.name}</span>
                                <span className="text-xs text-zinc-500">
                                  {a.rating ? `${a.rating}/10` : ""}
                                  {a.hourly_rate ? ` • $${a.hourly_rate}/hr` : ""}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1 border-zinc-700"
                  onClick={() => { setAssignDialogOpen(false); setAssigningTask(null); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  onClick={handleAssignTask}
                  disabled={
                    !assignForm.agreed_price || 
                    parseFloat(assignForm.agreed_price) <= 0 ||
                    assignMargin.status === 'CRITICAL' ||
                    (!assignForm.send_to_pool && !assignForm.assigned_artist_id)
                  }
                >
                  {assignForm.send_to_pool ? "Send to Pool" : "Assign"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Reject Price Request Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Reject Price Request</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-zinc-400 text-sm">Rejection reason (optional)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="mt-2 bg-zinc-950 border-zinc-700 text-zinc-50"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmRejectPriceRequest}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Tasks from Price Request Dialog */}
      <Dialog open={createTaskDialogOpen} onOpenChange={setCreateTaskDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Create Tasks from Request</DialogTitle>
          </DialogHeader>
          {selectedPriceRequest && (
            <div className="py-4 space-y-4">
              <div className="p-3 rounded bg-zinc-800/50 border border-zinc-700">
                <p className="text-sm text-zinc-50 font-medium">{selectedPriceRequest.task_name}</p>
                <p className="text-xs text-zinc-400 mt-1">
                  {selectedPriceRequest.task_type} • Total: {selectedPriceRequest.quantity || 1} units • ${parseFloat(selectedPriceRequest.proposed_price || 0).toFixed(2)}/unit
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-400 text-sm">
                    Split into tasks by quantity
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs border-zinc-700"
                    onClick={addTaskRow}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Task
                  </Button>
                </div>
                <p className="text-xs text-zinc-500">
                  Enter the quantity for each task. Total must equal {selectedPriceRequest.quantity || 1}.
                </p>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {taskQuantities.map((task, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500 w-16">Task {index + 1}:</span>
                      <Input
                        type="number"
                        min="0"
                        max={selectedPriceRequest.quantity || 10000}
                        value={task.qty || ''}
                        onChange={(e) => updateTaskQuantity(index, e.target.value)}
                        placeholder="Qty"
                        className="w-24 h-8 bg-zinc-950 border-zinc-700 text-zinc-50 text-sm"
                      />
                      <span className="text-xs text-zinc-500">units</span>
                      {taskQuantities.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          onClick={() => removeTaskRow(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="p-2 rounded bg-zinc-800/50 border border-zinc-700">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-400">Total allocated:</span>
                    <span className={getTotalAllocated() === (selectedPriceRequest.quantity || 0) ? "text-emerald-400" : "text-amber-400"}>
                      {getTotalAllocated()} / {selectedPriceRequest.quantity || 0}
                    </span>
                  </div>
                  {getRemainingQuantity() !== 0 && (
                    <div className="flex items-center justify-between text-xs mt-1">
                      <span className="text-zinc-400">Remaining:</span>
                      <span className={getRemainingQuantity() < 0 ? "text-red-400" : "text-amber-400"}>
                        {getRemainingQuantity()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateTaskDialogOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTasksFromRequest}
              disabled={creatingTasks || getRemainingQuantity() !== 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {creatingTasks ? "Creating..." : `Create ${taskQuantities.filter(t => t.qty > 0).length} Task${taskQuantities.filter(t => t.qty > 0).length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={!!editingPayment} onOpenChange={(open) => !open && setEditingPayment(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Pencil className="h-5 w-5 text-orange-400" />
              Edit Payment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Amount-lock banner — visible only when this payment is linked to an approved request */}
            {editingPayment?.source_request_id && (
              <div className="rounded-md border border-emerald-700/40 bg-emerald-900/20 px-3 py-2 text-xs text-emerald-300 flex items-start gap-2">
                <Link2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  Amount and currency are locked by the source {editingPayment.source_request_type === "studio_request" ? "studio request" : "price request"}.
                  To change the price, revise or replace the request — payment will update automatically.
                </div>
              </div>
            )}

            {/* Payment Name */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Payment Name</Label>
              <Input
                value={editPaymentForm.name}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                placeholder="e.g., Milestone 1 Payment"
              />
            </div>

            {/* Amount and Currency */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 flex items-center gap-1">
                  Amount
                  {editingPayment?.source_request_id && <Link2 className="h-3 w-3 text-emerald-400" />}
                </Label>
                <Input
                  type="number"
                  value={editPaymentForm.amount}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={!!editingPayment?.source_request_id}
                  title={editingPayment?.source_request_id ? "Locked by source request" : undefined}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 flex items-center gap-1">
                  Currency
                  {editingPayment?.source_request_id && <Link2 className="h-3 w-3 text-emerald-400" />}
                </Label>
                <Select
                  value={editPaymentForm.currency}
                  onValueChange={(v) => setEditPaymentForm({ ...editPaymentForm, currency: v })}
                  disabled={!!editingPayment?.source_request_id}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 disabled:opacity-60 disabled:cursor-not-allowed">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {["USD", "EUR", "UAH"].map(c => <SelectItem key={c} value={c} className="text-zinc-50">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Month */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Month</Label>
              <Select value={editPaymentForm.month} onValueChange={(v) => setEditPaymentForm({ ...editPaymentForm, month: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue placeholder="Select month..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {monthOptions.map(m => <SelectItem key={m.value} value={m.value} className="text-zinc-50">{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Legal Entity */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Legal Entity (for Invoice)</Label>
              <Select value={editPaymentForm.legal_entity_id || "none"} onValueChange={(v) => setEditPaymentForm({ ...editPaymentForm, legal_entity_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue placeholder="Select legal entity..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="none" className="text-zinc-500">- None -</SelectItem>
                  {legalEntities.filter(e => e.is_active).map(entity => (
                    <SelectItem key={entity.id} value={entity.id} className="text-zinc-50">
                      {entity.name} {entity.company_type === 'llc' ? '(LLC)' : '(FOP)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">Bank details from this entity will appear on invoice</p>
            </div>

            {/* Client billing profile (which counterparty entity to invoice) — only when client has 2+ profiles */}
            {(() => {
              const clientObj = getClient();
              const profiles = clientObj?.billing_profiles || [];
              if (profiles.length < 2) return null;
              const defaultProfile = profiles.find(p => p.is_default) || profiles[0];
              return (
                <div className="space-y-2">
                  <Label className="text-zinc-400">Bill to (Client Legal Entity)</Label>
                  <Select
                    value={editPaymentForm.client_billing_profile_id || "default"}
                    onValueChange={(v) => setEditPaymentForm({ ...editPaymentForm, client_billing_profile_id: v === "default" ? "" : v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue placeholder="Use client default..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="default" className="text-zinc-300">
                        Default ({defaultProfile?.label || defaultProfile?.legal_name || "client default"})
                      </SelectItem>
                      {profiles.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-zinc-50">
                          {p.label || p.legal_name || "(unnamed profile)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">Used as the recipient (Otrymuvach) on the invoice and act</p>
                </div>
              );
            })()}

            {/* Contract Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400">Contract №</Label>
                <Input
                  value={editPaymentForm.contract_number}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, contract_number: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="e.g., 2026/01-15"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Contract Date</Label>
                <Input
                  type="date"
                  value={editPaymentForm.contract_date}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, contract_date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPayment(null)} className="border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSavePayment} className="bg-orange-600 hover:bg-orange-700 text-white">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Payment Dialog */}
      <Dialog open={splitPaymentDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setSplitPaymentDialogOpen(false);
          setPaymentToSplit(null);
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Split className="h-5 w-5 text-purple-400" />
              Split Payment
            </DialogTitle>
          </DialogHeader>
          
          {paymentToSplit && (
            <div className="space-y-4">
              {/* Original Payment Info */}
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-sm text-zinc-400">Splitting payment:</p>
                <p className="text-lg font-semibold text-zinc-50">{paymentToSplit.name}</p>
                <p className="text-sm text-green-400 font-mono">
                  {getCurrencySymbol(paymentToSplit.currency)}{parseFloat(paymentToSplit.amount).toLocaleString()}
                </p>
              </div>

              {/* Split Parts */}
              <div className="space-y-3">
                <Label className="text-zinc-400">Split into parts:</Label>
                {splitParts.map((part, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-800/30 rounded border border-zinc-700">
                    <span className="text-zinc-500 text-sm w-6">{idx + 1}.</span>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={part.percent}
                        onChange={(e) => updateSplitPart(idx, "percent", e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 w-20 h-8 text-sm text-center"
                        placeholder="%"
                      />
                      <span className="text-zinc-500 text-sm">%</span>
                    </div>
                    <span className="text-zinc-600">=</span>
                    <span className="text-green-400 font-mono text-sm w-24">
                      {getCurrencySymbol(paymentToSplit.currency)}{((paymentToSplit.amount * (parseFloat(part.percent) || 0)) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <Select value={part.month} onValueChange={(v) => updateSplitPart(idx, "month", v)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 w-32 h-8 text-sm">
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {monthOptions.map(m => (
                          <SelectItem key={m.value} value={m.value} className="text-zinc-50 text-sm">{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {splitParts.length > 2 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeSplitPart(idx)}
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Row & Total */}
              <div className="flex items-center justify-between">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addSplitPart}
                  className="text-purple-400 border-purple-700 hover:bg-purple-900/30"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Part
                </Button>
                <span className={cn(
                  "text-sm font-mono",
                  Math.abs(splitParts.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0) - 100) < 0.5
                    ? "text-green-400"
                    : "text-yellow-400"
                )}>
                  Total: {splitParts.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0).toFixed(1)}%
                </span>
              </div>

              <p className="text-xs text-zinc-500">
                Original payment will be deleted and replaced with {splitParts.length} new payments.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSplitPaymentDialogOpen(false)}
              className="border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSplitPayment}
              disabled={splittingPayment}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {splittingPayment ? "Splitting..." : `Split into ${splitParts.length} Payments`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Bank Transaction Dialog */}
      <Dialog open={linkTransactionDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setLinkTransactionDialogOpen(false);
          setLinkingServiceId(null);
          setUnmatchedTransactions([]);
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Landmark className="h-5 w-5 text-cyan-400" />
              Связать с банковской транзакцией
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingTransactions ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-zinc-400">Загрузка транзакций...</div>
              </div>
            ) : unmatchedTransactions.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Landmark className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Нет подходящих несвязанных транзакций</p>
                <p className="text-xs mt-1">Синхронизируйте данные на странице "Bank Transactions"</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-zinc-500 mb-3">
                  Найдено {unmatchedTransactions.length} транзакций с похожей суммой. Выберите нужную:
                </p>
                {unmatchedTransactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-cyan-600 cursor-pointer transition-colors"
                    onClick={() => handleLinkTransaction(tx.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">{tx.date}</Badge>
                          <Badge className="bg-blue-900/30 text-blue-400 text-[10px]">{tx.fop_name || "FOP"}</Badge>
                        </div>
                        <p className="text-zinc-50 text-sm font-medium">{tx.counterparty_name || "—"}</p>
                        <p className="text-zinc-400 text-xs truncate max-w-md" title={tx.purpose}>
                          {tx.purpose?.substring(0, 80) || "—"}{tx.purpose?.length > 80 ? "..." : ""}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-green-400 font-mono font-medium">
                          +{tx.amount?.toLocaleString()} {tx.currency}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-zinc-800 pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setLinkTransactionDialogOpen(false);
                setLinkingServiceId(null);
              }}
              className="border-zinc-700"
            >
              Отмена
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/bank-transactions")}
              className="border-zinc-700 text-cyan-400 hover:text-cyan-300"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Открыть все транзакции
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Studio Request Dialog - Send request to Partner */}
      <Dialog open={studioRequestDialogOpen} onOpenChange={setStudioRequestDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Send className="h-5 w-5 text-amber-400" />
              Send Request to Partner
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-xs text-zinc-500">Project</p>
              <p className="text-sm text-zinc-50 font-medium">{formData.name}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 mb-1.5 block text-xs">Request Type</Label>
                <Select 
                  value={studioRequestForm.request_type} 
                  onValueChange={(v) => setStudioRequestForm(f => ({ ...f, request_type: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price_adjustment">Price Adjustment</SelectItem>
                    <SelectItem value="rework">Rework Request</SelectItem>
                    <SelectItem value="additional_work">Additional Work</SelectItem>
                    <SelectItem value="deadline_change">Deadline Change</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-400 mb-1.5 block text-xs">Priority</Label>
                <Select 
                  value={studioRequestForm.priority} 
                  onValueChange={(v) => setStudioRequestForm(f => ({ ...f, priority: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-400 mb-1.5 block text-xs">Title *</Label>
              <Input
                value={studioRequestForm.title}
                onChange={(e) => setStudioRequestForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Brief title for the request"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div>
              <Label className="text-zinc-400 mb-1.5 block text-xs">Description</Label>
              <Textarea
                value={studioRequestForm.description}
                onChange={(e) => setStudioRequestForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Detailed description of what you need..."
                className="bg-zinc-800 border-zinc-700 min-h-[80px]"
              />
            </div>
            
            {(studioRequestForm.request_type === "price_adjustment" || 
              studioRequestForm.request_type === "additional_work") && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-zinc-400 mb-1.5 block text-xs">Price per unit ($)</Label>
                  <Input
                    type="number"
                    value={studioRequestForm.price_per_unit}
                    onChange={(e) => setStudioRequestForm(f => ({ ...f, price_per_unit: e.target.value }))}
                    placeholder="0.00"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 mb-1.5 block text-xs">Quantity</Label>
                  <Input
                    type="number"
                    value={studioRequestForm.quantity}
                    onChange={(e) => setStudioRequestForm(f => ({ ...f, quantity: e.target.value }))}
                    placeholder="1"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 mb-1.5 block text-xs">Discount (%)</Label>
                  <Input
                    type="number"
                    value={studioRequestForm.discount}
                    onChange={(e) => setStudioRequestForm(f => ({ ...f, discount: e.target.value }))}
                    placeholder="0"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
            )}
            
            {/* Additional 360-tour points (auto-calc at $20/pt) */}
            <div className="rounded border border-purple-500/20 bg-purple-500/5 p-3">
              <Label className="text-purple-300 mb-1.5 block text-xs">
                Additional 360° tour points ($20/point)
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  value={studioRequestForm.extra_360_points}
                  onChange={(e) => setStudioRequestForm(f => ({ ...f, extra_360_points: e.target.value }))}
                  placeholder="0"
                  className="bg-zinc-800 border-zinc-700 w-32"
                  data-testid="studio-extra-360-points"
                />
                <span className="text-xs text-zinc-400">
                  × $20 = <span className="font-mono text-purple-300">${(parseInt(studioRequestForm.extra_360_points) || 0) * 20}</span>
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">
                Added on top of the request total below.
              </p>
            </div>

            {/* Calculated total or manual amount */}
            <div>
              {(() => {
                const baseAmount = studioRequestForm.price_per_unit && studioRequestForm.quantity
                  ? parseFloat(studioRequestForm.price_per_unit) * parseInt(studioRequestForm.quantity) * (1 - (parseFloat(studioRequestForm.discount) || 0) / 100)
                  : parseFloat(studioRequestForm.amount) || 0;
                const extraTotal = (parseInt(studioRequestForm.extra_360_points) || 0) * 20;
                const grand = (baseAmount + extraTotal).toFixed(2);
                const auto = !!(studioRequestForm.price_per_unit && studioRequestForm.quantity) || extraTotal > 0;
                return (
                  <>
                    <Label className="text-zinc-400 mb-1.5 block text-xs">
                      Total Amount (USD) {auto ? "(auto-calculated)" : ""}
                      {extraTotal > 0 ? (
                        <span className="ml-2 text-purple-300">incl. +${extraTotal} for 360 points</span>
                      ) : null}
                    </Label>
                    <Input
                      type="number"
                      value={auto ? grand : studioRequestForm.amount}
                      onChange={(e) => setStudioRequestForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder="0.00"
                      className="bg-zinc-800 border-zinc-700"
                      readOnly={auto}
                    />
                  </>
                );
              })()}
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setStudioRequestDialogOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateStudioRequest}
              disabled={submittingStudioRequest || !studioRequestForm.title}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submittingStudioRequest ? (
                "Sending..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Price Request Modal for creating requests to partner */}
      <BulkPriceRequestModal
        open={showBulkRequestModal}
        onOpenChange={setShowBulkRequestModal}
        serviceTypes={serviceTypes}
        projectId={actualProjectId}
        vexosFees={vexosFees || undefined}
        onSubmit={async (requests) => {
          // Create studio requests from the bulk form
          try {
            for (const req of requests) {
              const pricePerUnit = req.base_price || req.proposed_price || 0;
              const qty = req.quantity || 1;
              const discountPct = req.discount_percent || 0;
              const totalAmount = pricePerUnit * qty * (1 - discountPct / 100);
              
              await api.post("/studio-requests", {
                project_id: actualProjectId,
                request_type: "additional_work",
                title: req.task_name || req.service_name,
                description: req.notes || "",
                amount: totalAmount || null,
                price_per_unit: pricePerUnit || null,
                quantity: qty,
                discount: discountPct || null,
                priority: "normal",
              });
            }
            toast.success(`${requests.length} request(s) sent to partner`);
            setShowBulkRequestModal(false);
            fetchData();
          } catch (error) {
            toast.error("Failed to create requests");
          }
        }}
      />

      {/* Leaving with unsaved Project Details. Three honest choices — save and
          go, go anyway, or stay — rather than a yes/no that hides one of them. */}
      <Dialog open={!!pendingNav} onOpenChange={(open) => { if (!open) setPendingNav(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md" data-testid="unsaved-changes-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Є незбережені зміни</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400">
            Ви змінили дані проєкту, але не натиснули «Зберегти». Якщо піти зараз, зміни зникнуть.
          </p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              className="text-zinc-400 hover:text-zinc-100"
              onClick={() => setPendingNav(null)}
              data-testid="unsaved-stay"
            >
              Залишитись
            </Button>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300"
              onClick={() => { const to = pendingNav; savedSnapshotRef.current = JSON.stringify(formData); setPendingNav(null); navigate(to); }}
              data-testid="unsaved-discard"
            >
              Вийти без збереження
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700"
              disabled={saving}
              onClick={async () => {
                const to = pendingNav;
                const ok = await handleSave();
                // A failed save (missing required fields) must not throw the
                // edit away by navigating anyway — the dialog stays put.
                if (ok) { setPendingNav(null); navigate(to); }
              }}
              data-testid="unsaved-save"
            >
              {saving ? "Збереження…" : "Зберегти зміни"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesProjectEditPage;
